// Single source of truth for all UI copy translations.
// Shape: { en: { key: string }, es: { key: string } }.
// Keys are dot-namespaced by surface (nav.*, dash.*, matches.*, stats.*, etc.).
// English is authoritative; Spanish uses football/soccer domain terms.
//
// Lookup is by key against the active language; if a key is missing in Spanish
// the translator (src/lib/i18n/LangProvider.tsx) falls back to English, and if
// the key is missing entirely it returns the key itself — so a forgotten string
// degrades to readable English rather than throwing.

export type Dict = Record<string, string>;

const en: Dict = {
  "coachScout.title": "Scouting",
  "coachScout.subtitle": "Player pipeline & transfer targets",
  "coachScout.addProspect": "Add Prospect",
  "coachScout.kpiTotalProspects": "Total Prospects",
  "coachScout.kpiTotalProspectsSub": "active pipeline",
  "coachScout.kpiPriorityTargets": "Priority Targets",
  "coachScout.kpiPriorityTargetsSub": "ready to approach",
  "coachScout.kpiAvgPotential": "Avg Potential",
  "coachScout.kpiAvgPotentialSub": "across all pipeline",
  "coachScout.kpiExpiringSoon": "Expiring Soon",
  "coachScout.kpiExpiringSoonSub": "contracts ≤ 2026",
  "coachScout.trendingNow": "Trending Now",
  "coachScout.playersInForm": "Players in form",
  "coachScout.searchPlaceholder": "Name, club, position…",
  "coachScout.filterAll": "All",
  "coachScout.allPos": "All Pos",
  "coachScout.shortlist": "Shortlist ({n})",
  "coachScout.sortPotential": "Sort: Potential",
  "coachScout.sortOvr": "Sort: OVR",
  "coachScout.sortTrend": "Sort: Trend",
  "coachScout.sortAge": "Sort: Age",
  "coachScout.sortValue": "Sort: Value",
  "coachScout.sortName": "Sort: Name",
  "coachScout.prospectsShown": "{n} prospect shown",
  "coachScout.prospectsShownPlural": "{n} prospects shown",
  "coachScout.selectedForComparison": "{n}/2 selected for comparison",
  "coachScout.compare": "Compare",
  "coachScout.clear": "Clear",
  "coachScout.playerComparison": "Player Comparison",
  "coachScout.closeX": "✕ Close",
  "coachScout.noProspectsMatch": "No prospects match your filters.",
  "coachScout.colPlayer": "Player",
  "coachScout.colContract": "Contract",
  "coachScout.colStatus": "Status",
  "coachScout.colTrend": "Trend",
  "coachScout.colForm": "Form",
  "coachScout.aiRecTitle": "AI Scouting Recommendation",
  "coachScout.aiRecPart1": "Based on your squad profile,",
  "coachScout.aiRecPart2": "(🇳🇬 RW, 18y) has the highest potential in your database at",
  "coachScout.aiRecPot": "92 POT",
  "coachScout.aiRecPart3": ". His pace (34.1 km/h) would complement your existing wide options.",
  "coachScout.aiRecPart4": "Also flag:",
  "coachScout.aiRecAnd": "and",
  "coachScout.aiRecPart5": "both expire in 2025 — window opens January.",
  "coachScout.statusIdentified": "Identified",
  "coachScout.statusWatched": "Watched",
  "coachScout.statusShortlisted": "Shortlisted",
  "coachScout.statusTarget": "Target",
  "coachScout.statusSigned": "Signed",
  "coachScout.statusRejected": "Rejected",
  "coachScout.priorityHigh": "High",
  "coachScout.priorityMed": "Med",
  "coachScout.priorityLow": "Low",
  "coachScout.priorityLabel": "Priority",
  "coachScout.contract": "Contract",
  "coachScout.out": "Out",
  "coachScout.noPlayers": "No players",
  "coachScout.unitYr": "y",
  "coachScout.ovr": "OVR",
  "coachScout.pot": "POT",
  "coachScout.age": "Age",
  "coachScout.value": "Value",
  "coachScout.potential": "POTENTIAL",
  "coachScout.trend30d": "30-day trend",
  "coachScout.infoNationality": "Nationality",
  "coachScout.infoPreferredFoot": "Preferred Foot",
  "coachScout.infoHeight": "Height",
  "coachScout.infoTopSpeed": "Top Speed",
  "coachScout.infoGoalsSeason": "Goals (season)",
  "coachScout.infoAssistsSeason": "Assists (season)",
  "coachScout.attributes": "Attributes",
  "coachScout.attrSpeed": "Speed",
  "coachScout.attrFinishing": "Finishing",
  "coachScout.attrPlaymaking": "Playmaking",
  "coachScout.attrWorkRate": "Work Rate",
  "coachScout.attrDefending": "Defending",
  "coachScout.attrStrength": "Strength",
  "coachScout.matchStats": "Match Stats",
  "coachScout.passAccuracy": "Pass Accuracy",
  "coachScout.scoutNotesHeading": "Scout Notes",
  "coachScout.scoutingReports": "Scouting Reports ({n})",
  "coachScout.addReport": "Add report",
  "coachScout.noReportsYet": "No reports yet",
  "coachScout.updateStatus": "Update Status",
  "coachScout.reportsCount": "{n} report",
  "coachScout.reportsCountPlural": "{n} reports",
  "coachScout.highPriority": "HIGH PRIORITY",
  "coachScout.addReportTitle": "Add Scouting Report",
  "coachScout.matchContext": "Match context",
  "coachScout.matchContextPlaceholder": "e.g. Club A vs Club B (2-1 W)",
  "coachScout.rating": "Rating",
  "coachScout.ratingPoor": "Poor",
  "coachScout.ratingAverage": "Average",
  "coachScout.ratingExcellent": "Excellent",
  "coachScout.scoutNotes": "Scout notes",
  "coachScout.scoutNotesPlaceholder": "Observations, strengths, concerns…",
  "coachScout.cancel": "Cancel",
  "coachScout.saveReport": "Save Report",
  "coachScout.browseTitle": "Browse Draffte Passport Players",
  "coachScout.browseSubtitle": "{n} players in the Draffte network · verified profiles",
  "coachScout.inPipeline": "✓ In Pipeline",
  "coachScout.added": "✓ Added",
  "coachScout.addToPipeline": "+ Add to Pipeline",
  "coachScout.noPlayersMatch": "No players match your filters.",
  "coachScout.playersAdded": "{n} player added to pipeline",
  "coachScout.playersAddedPlural": "{n} players added to pipeline",
  "coachScout.selectPlayersToAdd": "Select players to add to your scouting pipeline",
  "coachScout.done": "Done",
  "coachScout.addedFromNetwork": "Added from Draffte Passport network.",
  "coachScout.coachFallback": "Coach",
  "coachAnalytics.title": "Team Analytics",
  "coachAnalytics.subtitle": "Performance intelligence · Season {y}",
  "coachAnalytics.notTracked": "not tracked",
  "coachAnalytics.speedNotTracked": "Top speed needs player tracking from the match video — we record ball touches, not positions. Nothing here is estimated.",
  "coachAnalytics.rowShotsPg": "Shots p/G",
  "coachAnalytics.rowGoalsPg": "Goals p/G",
  "coachAnalytics.rowDuelsWon": "Duels won p/G",
  "coachAnalytics.export": "Export",
  "coachAnalytics.refresh": "Refresh",
  "coachAnalytics.emptyTitle": "No analytics yet",
  "coachAnalytics.emptyDesc": "Add players and record matches to see analytics.",
  "coachAnalytics.kpiSquadAvgOvr": "Squad Avg OVR",
  "coachAnalytics.kpiImproving": "Improving",
  "coachAnalytics.kpiDeclining": "Declining",
  "coachAnalytics.kpiAvgTopSpeed": "Avg Top Speed",
  "coachAnalytics.kpiThisMonth": "{n} this month",
  "coachAnalytics.kpiTrendingUp": "players trending up",
  "coachAnalytics.kpiNeedAttention": "need attention",
  "coachAnalytics.kpiKmhAcrossSquad": "km/h across squad (est.)",
  "coachAnalytics.squadOvrProgression": "Squad OVR Progression",
  "coachAnalytics.avgOverallLast8": "Average overall rating — last 8 months",
  "coachAnalytics.ovr": "OVR",
  "coachAnalytics.teamAttributeProfile": "Team Attribute Profile",
  "coachAnalytics.vsLeagueBenchmark": "vs. league benchmark",
  "coachAnalytics.legendSquad": "Squad",
  "coachAnalytics.legendBenchmark": "Benchmark",
  "coachAnalytics.attrSpd": "SPD",
  "coachAnalytics.attrFin": "FIN",
  "coachAnalytics.attrPly": "PLY",
  "coachAnalytics.attrWrk": "WRK",
  "coachAnalytics.attrDef": "DEF",
  "coachAnalytics.attrStr": "STR",
  "coachAnalytics.posForwards": "Forwards",
  "coachAnalytics.posMidfielders": "Midfielders",
  "coachAnalytics.posDefenders": "Defenders",
  "coachAnalytics.posGoalkeeper": "Goalkeeper",
  "coachAnalytics.nPlayers": "{n} players",
  "coachAnalytics.avgOvr": "Avg OVR",
  "coachAnalytics.avgSpeed": "Avg Speed",
  "coachAnalytics.avgFinishing": "Avg Finishing",
  "coachAnalytics.playerBreakdown": "Player Breakdown",
  "coachAnalytics.searchPlaceholder": "Search…",
  "coachAnalytics.filterAll": "All",
  "coachAnalytics.groupPerformance": "Performance",
  "coachAnalytics.groupPhysical": "Physical",
  "coachAnalytics.groupTactical": "Tactical",
  "coachAnalytics.sortOvr": "Sort: OVR",
  "coachAnalytics.sortTrend": "Sort: Trend",
  "coachAnalytics.sortSpeed": "Sort: Speed",
  "coachAnalytics.sortWorkRate": "Sort: Work Rate",
  "coachAnalytics.sortName": "Sort: Name",
  "coachAnalytics.thPlayer": "Player",
  "coachAnalytics.thOvr": "OVR",
  "coachAnalytics.thTrend": "Trend",
  "coachAnalytics.thTopSpeed": "Top Speed",
  "coachAnalytics.thKmSession": "Km/Session",
  "coachAnalytics.thSessionsMo": "Sessions/Mo",
  "coachAnalytics.thMinsPlayed": "Mins Played",
  "coachAnalytics.thPassAcc": "Pass Acc.",
  "coachAnalytics.thShotsOT": "Shots OT",
  "coachAnalytics.thTackles": "Tackles",
  "coachAnalytics.thAerials": "Aerials",
  "coachAnalytics.thForm": "Form",
  "coachAnalytics.noPlayersMatch": "No players match your filters.",
  "coachAnalytics.aiInsightTitle": "AI Performance Insight",
  "coachAnalytics.aiInsightLead": "Your squad's average OVR has improved by",
  "coachAnalytics.aiInsightDelta": "{n} points",
  "coachAnalytics.aiInsightMid": "over the last 8 months. Midfield playmaking is your strongest area vs. benchmark, while defending could benefit from additional focused sessions.",
  "coachAnalytics.aiInsightAnd": "and",
  "coachAnalytics.aiInsightTail": "are showing declining trends — consider reviewing their training load.",
  "coachAnalytics.age": "Age",
  "coachAnalytics.thirtyDayTrend": "30-day trend",
  "coachAnalytics.attributeRadar": "Attribute Radar",
  "coachAnalytics.coreAttributes": "Core Attributes",
  "coachAnalytics.physicalMetrics": "Physical Metrics",
  "coachAnalytics.physTopSpeed": "Top Speed",
  "coachAnalytics.physKmSession": "Km/Session",
  "coachAnalytics.physSessions": "Sessions",
  "coachAnalytics.unitKmh": "km/h est.",
  "coachAnalytics.unitKm": "km est.",
  "coachAnalytics.unitPerMo": "/mo",
  "coachAnalytics.gkStats": "Goalkeeper Stats",
  "coachAnalytics.attackingStats": "Attacking Stats (per game)",
  "coachAnalytics.midfieldStats": "Midfield Stats (per game)",
  "coachAnalytics.defensiveStats": "Defensive Stats (per game)",
  "coachAnalytics.rowReflexes": "Reflexes (DEF)",
  "coachAnalytics.rowDistribution": "Distribution %",
  "coachAnalytics.rowAerialClaims": "Aerial Claims",
  "coachAnalytics.rowSweeping": "Sweeping",
  "coachAnalytics.rowHandling": "Handling (STR)",
  "coachAnalytics.rowShotsOnTarget": "Shots on Target",
  "coachAnalytics.rowEstGoals": "Est. Goals/Game",
  "coachAnalytics.rowKeyPasses": "Key Passes",
  "coachAnalytics.rowAerialDuels": "Aerial Duels",
  "coachAnalytics.rowPressing": "Pressing (Tkl)",
  "coachAnalytics.rowPassAccuracy": "Pass Accuracy %",
  "coachAnalytics.rowTacklesPg": "Tackles p/G",
  "coachAnalytics.rowBallRecoveries": "Ball Recoveries",
  "coachAnalytics.rowAerialWins": "Aerial Wins",
  "coachAnalytics.rowInterceptions": "Interceptions",
  "coachAnalytics.rowShotsBlocked": "Shots Blocked",
  "coachAnalytics.statSpeed": "Speed",
  "coachAnalytics.statFinishing": "Finishing",
  "coachAnalytics.statPlaymaking": "Playmaking",
  "coachAnalytics.statWorkRate": "Work Rate",
  "coachAnalytics.statDefending": "Defending",
  "coachAnalytics.statStrength": "Strength",
  "coachAnalytics.statTopSpeed": "Top Speed",
  "coachAnalytics.statKmSession": "Km/Session",
  "coachAnalytics.statPassAcc": "Pass Acc.",
  "coachAnalytics.statShotsOT": "Shots OT",
  "coachAnalytics.statTackles": "Tackles",
  "coachAnalytics.statAerialW": "Aerial W.",
  "coachAI.title": "AI Analysis",
  "coachAI.subtitle": "Draffte Intelligence Engine · Updated 2 hours ago",
  "coachAI.liveAnalysis": "Live Analysis",
  "coachAI.pillCritical": "{n} Critical",
  "coachAI.pillWarnings": "{n} Warnings",
  "coachAI.pillPositives": "{n} Positives",
  "coachAI.pillPlayerReports": "{n} Player Reports",
  "coachAI.tabInsights": "Insights",
  "coachAI.tabAskAI": "Ask AI",
  "coachAI.tabPlayers": "Player Reports",
  "coachAI.tabTactical": "Tactical Reports",
  "coachAI.catTactical": "Tactical",
  "coachAI.catPhysical": "Physical",
  "coachAI.catPsychology": "Psychology",
  "coachAI.catPerformance": "Performance",
  "coachAI.catInjury": "Injury Risk",
  "coachAI.riskLow": "Low",
  "coachAI.riskMedium": "Medium",
  "coachAI.riskHigh": "High",
  "coachAI.pinned": "Pinned",
  "coachAI.pin": "Pin",
  "coachAI.recommendedAction": "Recommended Action",
  "coachAI.aiConfidence": "AI Confidence",
  "coachAI.ovr": "OVR",
  "coachAI.aiRating": "AI Rating",
  "coachAI.thirtyDayProj": "30-day proj.",
  "coachAI.injRisk": "Inj. Risk",
  "coachAI.strength": "Strength",
  "coachAI.weakness": "Weakness",
  "coachAI.aiRecommendation": "AI Recommendation",
  "coachAI.formTrend_rising": "Rising form",
  "coachAI.formTrend_stable": "Stable form",
  "coachAI.formTrend_declining": "Declining form",
  "coachAI.tacticalReport": "Tactical Report",
  "coachAI.suggestedFormation": "Suggested formation:",
  "coachAI.strengthsToExploit": "Strengths to exploit",
  "coachAI.threatsToManage": "Threats to manage",
  "coachAI.aiSuggestions": "AI Suggestions",
  "coachAI.chatPlaceholder": "Ask about your squad, tactics, player fitness…",
  "coachAI.chatHint": "Press Enter to send · Shift+Enter for new line",
  "coachAI.playersDesc": "AI-generated assessments based on camera tracking, session metrics, and match footage.",
  "coachAI.tacticalDesc": "AI-generated tactical blueprints based on opponent analysis, squad availability, and recent form.",
  "coachAI.explainerTitle": "How Draffte AI generates tactical reports",
  "coachAI.explainerBody": "Reports are generated using a combination of your team's camera tracking, match event logs, formation history, and opponent statistics from public databases. The AI identifies patterns across all data sources to produce contextual recommendations specific to your squad.",
  "coachAI.sourceCameraTracking": "Camera Tracking",
  "coachAI.sourceMatchEvents": "Match Events",
  "coachAI.sourceVideoAnalysis": "Video Analysis",
  "coachAI.sourceOpponentData": "Opponent Data",
  "coachAI.suggestion1": "Who are my highest injury-risk players right now?",
  "coachAI.suggestion2": "What formation should I use against a high press?",
  "coachAI.suggestion3": "Which players are underperforming their potential?",
  "coachAI.suggestion4": "How has our pressing improved this season?",
  "coachAI.suggestion5": "Who should I rest before the next match?",
  "coachAI.suggestion6": "What's our biggest tactical vulnerability?",
  "coachSetup.title": "Match Setup",
  "coachSetup.subtitle": "Configure jerseys for accurate player tracking",
  "coachSetup.backToMatch": "Back to match",
  "coachSetup.step1Colors": "1. Colors",
  "coachSetup.step2Lineup": "2. Lineup",
  "coachSetup.whyColorsTitle": "Why jersey colors?",
  "coachSetup.whyColorsBody": "Our camera uses jersey colors to instantly identify which team each player belongs to. Without this, we need ~4 seconds of video to auto-detect — and accuracy drops with similar colors.",
  "coachSetup.homeJersey": "Home Jersey",
  "coachSetup.awayJersey": "Away Jersey",
  "coachSetup.goalkeeper": "Goalkeeper",
  "coachSetup.sameColorWarning": "Home and away colors are the same — tracking will struggle to distinguish teams.",
  "coachSetup.cameraPreview": "Camera Preview",
  "coachSetup.yourTeam": "Your Team",
  "coachSetup.vs": "VS",
  "coachSetup.opponent": "Opponent",
  "coachSetup.continueToLineup": "Continue to Lineup",
  "coachSetup.duplicateWarning": "Duplicate jersey numbers detected: {nums}. Each player must have a unique number.",
  "coachSetup.startingXI": "Starting XI ({n}/11)",
  "coachSetup.selectMore": "Select {n} more",
  "coachSetup.gkBadge": "GK",
  "coachSetup.substitutes": "Substitutes ({n})",
  "coachSetup.savedRedirecting": "Saved — Redirecting to upload...",
  "coachSetup.saving": "Saving...",
  "coachSetup.saveContinue": "Save & Continue to Upload",
  "coachSetup.customColor": "Custom color",
  "coachSetup.colorRed": "Red",
  "coachSetup.colorOrange": "Orange",
  "coachSetup.colorAmber": "Amber",
  "coachSetup.colorYellow": "Yellow",
  "coachSetup.colorGreen": "Green",
  "coachSetup.colorTeal": "Teal",
  "coachSetup.colorSkyBlue": "Sky Blue",
  "coachSetup.colorBlue": "Blue",
  "coachSetup.colorIndigo": "Indigo",
  "coachSetup.colorPurple": "Purple",
  "coachSetup.colorPink": "Pink",
  "coachSetup.colorRose": "Rose",
  "coachSetup.colorWhite": "White",
  "coachSetup.colorGray": "Gray",
  "coachSetup.colorBlack": "Black",
  "coachSetup.colorBrown": "Brown",
  "coachUpload.title": "Upload Match Video",
  "coachUpload.subtitle": "Upload the full match recording for analysis",
  "coachUpload.back": "Back",
  "coachUpload.pipelineUpload": "Upload Video",
  "coachUpload.pipelineProcessing": "Processing",
  "coachUpload.pipelineFullDetails": "Full Details",
  "coachUpload.dropTitle": "Drag & drop your match video here",
  "coachUpload.dropSubtitle": "or click to browse — MP4, MOV, MKV supported",
  "coachUpload.max4gb": "Max 4GB",
  "coachUpload.min720p": "720p minimum",
  "coachUpload.fullMatchPreferred": "Full match preferred",
  "coachUpload.uploading": "Uploading {name}",
  "coachUpload.uploadComplete": "Upload complete — starting analysis...",
  "coachUpload.analyzing": "Analyzing match video...",
  "coachUpload.analyzingSubtitle": "Tracking players, detecting events, building stats",
  "coachUpload.stepDetecting": "Detecting players & ball",
  "coachUpload.stepClassifying": "Classifying teams",
  "coachUpload.stepTracking": "Tracking positions",
  "coachUpload.stepComputing": "Computing stats",
  "coachUpload.stepGenerating": "Generating insights",
  "coachUpload.analysisComplete": "Analysis complete!",
  "coachUpload.redirectingFullDetails": "Redirecting to Full Details...",
  "coachUpload.processingFailedShort": "Processing failed",
  "coachUpload.tryAgain": "Try Again",
  "coachUpload.tipsTitle": "Tips for best results",
  "coachUpload.tip1": "Use a fixed, wide-angle camera covering the full pitch",
  "coachUpload.tip2": "Ensure jerseys have distinct colors (home vs away)",
  "coachUpload.tip3": "Record the entire match without moving the camera",
  "coachUpload.tip4": "Good lighting improves player detection accuracy",
  "coachUpload.tip5": "Assign jersey numbers before uploading for best player identification",
  "coachUpload.skipViewStats": "Skip — View existing stats",
  "coachUpload.errorProcessingFailed": "Processing failed. Please try again.",
  "coachUpload.errorSelectVideo": "Please select a video file (MP4, MOV, etc.)",
  "coachInvolve.back": "Back",
  "coachInvolve.title": "Player Involvement",
  "coachInvolve.aiTag": "AI",
  "coachInvolve.subtitle": "On-ball touches, heatmaps and auto-cut highlights from the match video",
  "coachInvolve.clockCalibrated": "clock-calibrated",
  "coachInvolve.loading": "Loading…",
  "coachInvolve.noAnalysisTitle": "No analysis yet",
  "coachInvolve.noAnalysisBody": "Run the AI pipeline on this match's video to get per-player on-ball involvement, position heatmaps and highlight reels.",
  "coachInvolve.preparing": "Preparing…",
  "coachInvolve.startAnalysis": "Start AI analysis",
  "coachInvolve.processingMatch": "Processing match…",
  "coachInvolve.processingSubtitle": "This usually takes 1–2 hours for a full match. The page refreshes automatically.",
  "coachInvolve.onBallInvolvement": "On-ball involvement",
  "coachInvolve.framesAnalyzed": "· {n} frames analyzed",
  "coachInvolve.unknown": "Unknown",
  "coachInvolve.touches": "touches",
  "coachInvolve.detailSummary": "{touches} touches · {moments} on-ball moments",
  "coachInvolve.heatmapAlt": "#{n} heatmap",
  "coachInvolve.noHighlight": "No highlight reel for this player (top performers only).",
  "coachInvolve.podCommandLabel": "Paste this into a GPU pod terminal to run the job:",
  "coachInvolve.copyCommand": "Copy command",
  "coachInvolve.failedToLoad": "Failed to load",
  "coachInvolve.failedToStart": "Failed to start",
  // ── Nav / header ──
  "nav.overview": "Overview",
  "nav.squad": "Squad",
  "nav.sessions": "Sessions",
  "nav.matches": "Matches",
  "nav.analytics": "Analytics",
  "nav.myTeams": "My teams",
  "coachTeams.title": "My teams",
  "coachTeams.subtitle": "Your team plus any team another coach shared with you.",
  "coachTeams.roleOwnerYou": "Owner — you",
  "coachTeams.makeOwner": "Make owner",
  "coachTeams.makeOwnerTip": "Transfer ownership of this team to this coach",
  "coachTeams.transferTitle": "Transfer team ownership",
  "coachTeams.transferBody": "{coach} will become the owner of {team}. You will become a read-only viewer: you'll no longer be able to edit the squad, matches or sharing codes.",
  "coachTeams.transferWarning": "You cannot undo this. Only the new owner could transfer the team back to you.",
  "coachTeams.transferTypeToConfirm": "Type {team} to confirm",
  "coachTeams.transferConfirm": "Transfer ownership",
  "coachTeams.cancel": "Cancel",
  "coachTeams.transferFailed": "Couldn't transfer ownership. Try again in a moment.",
  "coachTeams.viewerBannerTitle": "Read-only view.",
  "coachTeams.viewerBannerBody": "You're viewing {team}, a team another coach shared with you. Everything on this page belongs to that team — none of your own team's data appears here, and nothing can be edited.",
  "coachTeams.viewStats": "View match stats",
  "coachTeams.loading": "Loading teams…",
  "coachTeams.loadFailed": "Couldn't load your teams. Try again in a moment.",
  "coachTeams.demoNote": "The demo account can't join or share teams.",
  "coachTeams.active": "Active",
  "coachTeams.leaveFailed": "Could not leave this team.",
  "coachTeams.activeDirected": "Your dashboard is currently pointed at {team}, a squad you direct. Squad, fixtures, matchday and the camera all follow it. Team settings below apply only to teams you own — switch to one of your own teams to reach them.",
  "coachTeams.switchTo": "Switch to this team",
  "coachTeams.switching": "Switching…",
  "coachTeams.switchFailed": "Couldn't switch teams. Try again in a moment.",
  "coachTeams.createTeam": "Create a new team",
  "coachTeams.createTitle": "Create a new team",
  "coachTeams.teamName": "Team name",
  "coachTeams.country": "Country",
  "coachTeams.squadLevel": "Squad level",
  "coachTeams.countryPlaceholder": "Select country",
  "coachTeams.levelPlaceholder": "Select level",
  "coachTeams.searchCountry": "Search country…",
  "coachTeams.levelUnset": "— No level",
  "coachTeams.noCountry": "No country found.",
  "coachTeams.create": "Create team",
  "coachTeams.createFailed": "Couldn't create the team. Try again in a moment.",
  "coachTeams.roleOwner": "Owner",
  "coachTeams.roleViewer": "Viewer",
  "coachTeams.roleDirector": "Director",
  "coachTeams.readOnly": "Read only",
  "coachTeams.players": "players",
  "coachTeams.coachBy": "Coach:",
  "coachTeams.rename": "Rename team",
  "coachTeams.renameFailed": "Couldn't rename the team.",
  "coachTeams.inviteTitle": "Coach invite code",
  "coachTeams.inviteBody": "Share this code with another coach to let them SEE this team (squad and results, read-only). It never lets them edit anything, and it's separate from your players' join code.",
  "coachTeams.showCode": "Show coach code",
  "coachTeams.codeFailed": "Couldn't get the coach code.",
  "coachTeams.copy": "Copy",
  "coachTeams.copied": "Copied",
  "coachTeams.rotate": "New code",
  "coachTeams.rotateTip": "Issue a fresh code — the old one stops working",
  "coachTeams.joinAnother": "Join another coach's team",
  "coachTeams.joinTitle": "Join a team",
  "coachTeams.joinBody": "Enter the 6-character coach code another coach shared with you. You'll get read-only access to their squad and results.",
  "coachTeams.join": "Join",
  "coachTeams.checking": "Checking code…",
  "coachTeams.notFound": "No team found for that code.",
  "coachTeams.joinFailed": "Couldn't join that team.",
  "coachTeams.view": "View",
  "coachTeams.leave": "Leave this team",
  "coachTeams.viewFailed": "Couldn't load this team.",
  "coachTeams.backToTeams": "Back to My teams",
  "coachTeams.squad": "Squad",
  "coachTeams.matches": "Matches",
  "coachTeams.noPlayers": "No players yet.",
  "coachTeams.noMatches": "No matches yet.",
  "coachTeams.vs": "vs",
  "coachTeams.at": "at",
  "coachTeams.upcoming": "upcoming",
  "squad.edit": "Edit",
  "squad.editPlayer": "Edit player",
  "squad.editName": "Name",
  "squad.editJersey": "Jersey #",
  "squad.editPosition": "Position",
  "squad.editSave": "Save",
  "squad.editCancel": "Cancel",
  "squad.editFailed": "Couldn't save the changes.",
  "squad.editNameRequired": "The player needs a name.",
  "squad.editJerseyRange": "Jersey number must be between 1 and 99.",
  "squad.editConnectedHint": "This player is linked to a Draffte account — their own profile updates may overwrite these fields.",
  "squad.editJerseyKeep": "Leaving this blank keeps #{n}.",
  "coachTeams.accessTitle": "Coaches with access",
  "coachTeams.revoke": "Revoke",
  "nav.scouting": "Scouting",
  "nav.aiAnalysis": "AI Analysis",
  "nav.coach": "Coach",
  "nav.signOut": "Sign out",
  "theme.light": "Light mode",
  "theme.dark": "Dark mode",
  "theme.toLight": "Switch to light mode",
  "theme.toDark": "Switch to dark mode",
  "lang.toggle": "Switch language",
  "lang.toSpanish": "Cambiar a Español",
  "lang.toEnglish": "Switch to English",

  // ── Dashboard (coach-home) ──
  "dash.welcomeBack": "Welcome back,",
  "dash.seasonOverview": "Season overview",
  "dash.yourTeam": "Your team",
  "dash.squad": "Squad",
  "dash.activePlayers": "Active players",
  "dash.average": "Average",
  "dash.teamAverage": "Team average",
  "dash.sessionsStat": "Sessions",
  "dash.thisWeek": "This week",
  "dash.topPerformers": "Top Performers",
  "dash.thisSeason": "This season",
  "dash.addPlayersTop": "Add players to see top performers",
  "dash.formAlert": "Form alert:",
  "dash.formAlertBody": "— below expected output last session. Consider adjusted load this week.",
  "dash.welcomeCoach": "Welcome, Coach!",
  "dash.setupBody": "Set up your team to get started with squad management, match tracking, and analytics.",
  "dash.setupTeam": "Set Up Team",
  "dash.squadRoster": "Squad Roster",
  "dash.playersSortedOvr": "players · sorted by OVR",
  "dash.noPlayersYet": "No players added yet.",
  "dash.noPlayersFilter": "No players match this filter.",
  "dash.addPlayers": "Add players",
  "dash.teamPerformance": "Team Performance",
  "dash.squadAttrAverages": "Squad attribute averages",
  "dash.yourTeamLegend": "Your team",
  "dash.radarEmpty": "Attribute averages appear once your players are rated from match video.",
  "dash.recentSessions": "Recent Sessions",
  "dash.trainingLoadWeek": "Training load this week",
  "dash.noSessionsYet": "No training sessions recorded yet.",
  "dash.upcomingMatches": "Upcoming Matches",
  "dash.nextFixtures": "Next 3 fixtures",
  "dash.noUpcoming": "No upcoming matches scheduled.",
  "dash.home": "HOME",
  "dash.away": "AWAY",
  "dash.kickoffTbc": "Kickoff TBC",
  "dash.aiInsight": "AI Insight",
  "dash.new": "New",
  "dash.fullAnalysis": "Full analysis",
  "dash.aiEmpty": "AI insights appear once your games are processed.",
  "dash.aiFormLine": "Your last {n} games: {w}W-{d}D-{l}L ({gf} GF / {ga} GA).",
  "dash.aiFormLineOne": "Your last game: {w}W-{d}D-{l}L ({gf} GF / {ga} GA).",
  "dash.aiAttrLine": "{strong} ({strongVal}) is your strongest area, while {weak} ({weakVal}) has the most room to improve.",
  "dash.aiTopLine": "{p1} ({o1}) and {p2} ({o2}) are your highest-rated players.",
  "dash.aiTopOneLine": "{p1} ({o1}) is your highest-rated player.",
  "dash.aiDemoAnd": "and",
  "dash.aiDemoMid": "are your two highest-performing midfielders this week. Their combined work-rate",
  "dash.aiDemoTail": "is well above your squad average — pairing them as a double pivot on Sunday could help absorb Rival FC's pressing and dominate the second-half tempo.",
  "dash.aiTagPressing": "Pressing resistance",
  "dash.aiTagSetPiece": "Set piece delivery",
  "dash.aiTagRuns": "High-intensity runs",
  "dash.aiTagTempo": "Tempo control",
  "dash.activeSeason": "Active Season",
  "dash.manOfMatch": "Man of the Match",
  "dash.noLicence": "No Licence",

  // ── Attribute labels (radar) ──
  "attr.speed": "Speed",
  "attr.finishing": "Finishing",
  "attr.playmaking": "Playmaking",
  "attr.workRate": "Work rate",
  "attr.defense": "Defense",
  "attr.strength": "Strength",

  // ── Match detail / stats ──
  "stats.backToMatches": "Back to matches",
  "stats.playerInvolvementAi": "Player Involvement AI",
  "stats.exportPdf": "Export PDF",

  // ── Official match report (Export PDF) ──
  "report.back": "Back",
  "report.saveAsPdf": "Save as PDF",
  "report.loading": "Building the match report…",
  "report.loadFailed": "Could not load this match.",
  "report.notReady": "Report not available yet",
  "report.notReadyBody": "A match report needs a confirmed result and a tagged team sheet. This game is missing:",
  "report.blocker.no_result": "No analysis has been produced for this match yet.",
  "report.blocker.no_player_stats": "No per-player statistics — the match has not been tagged.",
  "report.blocker.no_team_stats": "No team statistics.",
  "report.blocker.no_score": "The final score has not been confirmed.",
  "report.sheetSummary": "Match Summary",
  "report.sheetLineups": "Line-ups",
  "report.sheetTimeline": "Match Events",
  "report.sheetTeamStats": "Team Statistics",
  "report.sheetPassing": "Passing",
  "report.sheetAttacking": "Attacking",
  "report.sheetTerritory": "Territory",
  "report.sheetPlayers": "Player Statistics",
  "report.sheetMoments": "Key Moments",
  "report.sheetMethod": "Method & Coverage",
  "report.date": "Date",
  "report.competition": "Competition",
  "report.venue": "Venue",
  "report.home": "Home",
  "report.away": "Away",
  "report.formation": "Formation",
  "report.fullTime": "Full time",
  "report.matchFacts": "Match facts",
  "report.xgTotal": "Total expected goals (xG)",
  "report.shotsRecorded": "shots recorded",
  "report.teamSheet": "Team sheet",
  "report.startingXI": "Starting XI",
  "report.substitutes": "Substitutes",
  "report.squad": "Squad — starting XI not recorded",
  "report.player": "Player",
  "report.pos": "Pos",
  "report.min": "Min",
  "report.rating": "Rating",
  "report.matchEvents": "Match events",
  "report.event": "Event",
  "report.team": "Team",
  "report.evGoal": "Goal",
  "report.evShot": "Shot",
  "report.evShotOn": "Shot on target",
  "report.evYellow": "Yellow card",
  "report.evRed": "Red card",
  "report.evSub": "Substitution",
  "report.evOther": "Event",
  "report.teamComparison": "Team comparison",
  "report.possession": "Possession",
  "report.shots": "Shots",
  "report.onTarget": "On target",
  "report.offTarget": "Off target",
  "report.opponent": "Opponent",
  "report.passes": "Passes",
  "report.passesCompleted": "Passes completed",
  "report.passAccuracy": "Pass accuracy",
  "report.keyPasses": "Key passes",
  "report.recoveries": "Recoveries",
  "report.turnovers": "Turnovers",
  "report.corners": "Corners",
  "report.freeKicks": "Free kicks",
  "report.fouls": "Fouls",
  "report.offsides": "Offsides",
  "report.fieldTilt": "Field tilt",
  "report.passDistribution": "Pass distribution",
  "report.byDirection": "By direction",
  "report.byLength": "By length",
  "report.byThird": "By third",
  "report.forward": "Forward",
  "report.sideways": "Sideways",
  "report.backward": "Backward",
  "report.short": "Short",
  "report.medium": "Medium",
  "report.long": "Long",
  "report.defThird": "Defensive third",
  "report.midThird": "Middle third",
  "report.finalThird": "Final third",
  "report.completed": "Cmp",
  "report.attempted": "Att",
  "report.passNetwork": "Pass network",
  "report.topCombinations": "Top pass combinations",
  "report.combination": "Combination",
  "report.shotMap": "Shot map",
  "report.distance": "Dist",
  "report.goal": "Goal",
  "report.moreShots": "more shots not listed",
  "report.momentum": "Momentum",
  "report.us": "US",
  "report.them": "THEM",
  "report.possessionZones": "Possession by zone",
  "report.attackingDirection": "Attacking direction",
  "report.playerStatistics": "Player statistics",
  "report.touches": "Tch",
  "report.goals": "G",
  "report.assists": "A",
  "report.passesShort": "Passes",
  "report.keyPassesShort": "KP",
  "report.recoveriesShort": "Rec",
  "report.turnoversShort": "TO",
  "report.duelsShort": "Duels",
  "report.offDuelsShort": "OffD",
  "report.defDuelsShort": "DefW",
  "report.duelsFootnote": "OffD = attacking duels contested · DefW = defending duels won. Both include duels estimated from who had the ball, so there is no win rate.",
  "report.keyMoments": "Key moments",
  "report.moment": "Moment",
  "report.ofMoments": "most significant of",
  "report.howThisWasMeasured": "How this was measured",
  "report.methodIntro": "Every number in this report comes from this match. Events were tagged from the recorded video and positions placed from the tracked ball. Where a figure is an estimate or covers only part of the match, it is labelled as such on the sheet it appears on — and anything that was not measured is shown as a dash, never as zero.",
  "report.methodSource": "Source",
  "report.methodSourceVal": "Tagged match video",
  "report.methodEvents": "Tagged events",
  "report.methodPasses": "Passes placed on the pitch",
  "report.methodPlaced": "placed",
  "report.methodLengths": "Pass lengths",
  "report.methodEstimated": "Estimated against a nominal pitch (no field calibration)",
  "report.methodMetric": "Metric (field calibrated)",
  "report.methodMinutes": "Minutes played",
  "report.methodMinutesOk": "Computed from the match clock, XI and substitutions",
  "report.methodMinutesPartial": "Partial — the match clock was not fully marked",
  "report.methodClock": "Clock marks",
  "report.methodOpponent": "Opponent detail",
  "report.methodOppPlayers": "Per player",
  "report.methodOppTeam": "Team level only",
  "report.limitations": "Limitations",
  "report.limitXg": "Expected goals (xG) is a distance-based model. It does not account for angle, goalkeeper position or defensive pressure.",
  "report.limitEstimated": "Pass lengths and zones are estimates: this venue has no field calibration, so distances are measured against a nominal pitch size.",
  "report.limitOpponent": "The opponent was tagged as a team, not player by player, so their sheet carries fewer figures by design.",
  "report.limitAbsent": "A dash (—) means the figure was not measured for this match. It does not mean zero.",
  "report.generated": "Generated",
  "report.note.estimated_lengths": "Lengths estimated — no field calibration",
  "report.note.opponent_team_level": "Opponent tagged at team level",
  "report.note.opponent_missing": "No opponent statistics recorded",
  "report.note.placed_only": "Shows only passes the ball track could place",
  "report.note.unrated": "This squad has not been rated for this match",
  "report.note.xg_is_distance_model": "xG is a distance-based estimate",
  "report.note.formation_unplotted": "Formation positions not plotted",

  "stats.shareLink": "Share Link",
  "stats.exportCsv": "Export CSV",
  "stats.linkCopied": "Link copied to clipboard!",
  "stats.comingSoon": "— coming soon!",
  "stats.home": "Home",
  "stats.away": "Away",
  "stats.loadingMatch": "Loading match data...",
  "tag.minutesWarnTitle": "Minutes won't compute until this is fixed",
  "tag.minutesWarnSubs": "{n} substitution(s) are missing the player who came off — re-tag them so minutes can be calculated.",
  "tag.minutesWarnClock": "Tag kickoff, half-time and full-time so minutes can be calculated.",
  "tag.minutesWarnXI": "Set the starting players so minutes can be calculated.",
  "tag.minutesWarnGeneric": "Minutes can't be calculated from the current tags — check the clock marks, starting XI and substitutions.",
  "tag.minutesWarnDismiss": "Dismiss",
  "stats.gameVideo": "Game video",
  "stats.gameVideoDownload": "Download full game video ↓",
  "stats.videoSourceTip": "Viewing source — the 1080p follow render or the wide panorama; event jumps stay in sync on both",
  "stats.fullscreen": "Fullscreen",
  "stats.analysisRunning": "Video analysis is still running — these stats are final; player heatmaps and the position map may still fill in.",
  "stats.processingTitle": "Processing Match Video",
  "stats.processingBody": "This may take a few minutes. The page will update automatically.",
  "stats.failedTitle": "Analysis didn't complete",
  "stats.failedBody": "Something went wrong processing this match. Our team has been notified — please try again shortly.",
  "stats.loadFailedTitle": "Couldn't load this match",
  "stats.loadFailedBody": "There was a problem loading this game's stats. Please refresh the page or try again shortly.",
  "stats.autoScore": "Auto-detected score:",
  "stats.confirmOrCorrect": "Please confirm or correct",
  "stats.confirmScore": "Confirm Score",
  "stats.editLater": "Edit Later",
  "stats.scoreConfirmed": "Score confirmed!",
  "stats.couldNotConfirm": "Could not confirm — try again",
  "stats.noteSaved": "Note saved",
  "stats.fullMatch": "Full match",

  // Penalty shootout
  "stats.shootout": "Penalty Shootout",
  "stats.shootoutWon": "won on penalties",
  "stats.shootoutScored": "Scored",
  "stats.shootoutMissed": "Missed",
  "stats.shootoutPens": "pens",
  "stats.noLineup": "No starting lineup was saved for this game. Set it in Lineup on matchday and it will show here.",

  // Main tabs
  "stats.tab.team": "Team Stats",
  "stats.tab.player": "Player Stats",
  "stats.tab.video": "Game Video",
  "stats.tab.insights": "Data Insights",
  "stats.tab.heatmaps": "Heatmaps",
  "stats.tab.formation": "Formation",
  "stats.tab.momentum": "Momentum",
  "stats.tab.highlights": "Highlights",
  "hl.moments": "moments",
  "hl.good": "good",
  "hl.bad": "to improve",
  "hl.rosterOnly": "lineups not tagged — names from roster",
  "hl.reel": "Match reel",
  "hl.reelSub": "the top moments, in match order",
  "hl.playAll": "Play reel",
  "hl.stop": "Stop",
  "hl.srcFollow": "AI camera",
  "hl.srcWide": "Wide view",
  "hl.teamMoment": "Team moment",
  "hl.timeline": "Timeline",
  "hl.timelineSub": "every moment of the match",
  "hl.clickToPlay": "click a dot to watch it",
  "hl.byPlayer": "By player",
  "hl.allPlayers": "All",
  "hl.pickPlayer": "Pick a player to split their moments into good ones and ones to improve.",
  "hl.pane.good": "Good moments",
  "hl.pane.improve": "To improve",
  "hl.pane.none": "Nothing here in this match.",
  "hl.playingMoment": "Moment",
  "hl.detail.counter": "counter-attack",
  "hl.detail.ledToShot": "cost a shot",
  "hl.empty.title": "No highlights yet",
  "hl.empty.body": "This match hasn't been tagged by the studio yet. Highlights appear automatically once it is.",
  "hl.type.goal": "Goal",
  "hl.type.assist": "Assist",
  "hl.type.shot_on_target": "Shot on target",
  "hl.type.shot_off": "Shot off target",
  "hl.type.shot_blocked": "Blocked shot",
  "hl.type.key_pass": "Key pass",
  "hl.type.recovery": "Ball recovered",
  "hl.type.turnover": "Possession lost",
  "hl.type.turnover_punished": "Loss punished",
  "hl.type.save_moment": "Save",
  "hl.type.chance_against": "Chance against",
  "hl.type.foul": "Foul",
  "hl.type.yellow_card": "Yellow card",
  "hl.type.red_card": "Red card",
  "hl.type.moment": "Moment",

  // Stat group tabs
  "stats.group.summary": "Summary",
  "stats.group.distribution": "Distribution",
  "stats.group.offensive": "Offensive",
  "stats.group.defensive": "Defensive",
  "stats.group.goalkeeper": "Goalkeeper",
  "stats.group.sprint": "Sprint/Distance",
  "stats.group.ranking": "Ranking",
  "ranking.metric.rating": "Rating",
  "ranking.metric.passes": "Passes",
  "ranking.metric.recoveries": "Recoveries",
  "ranking.metric.duels": "Duels",
  "ranking.mvp": "MVP",
  "ranking.jointMvp": "Joint MVP",
  "ranking.joint": "Joint #{rank}",
  "ranking.notMeasured": "Not measured",
  "ranking.fullList": "Team performances",
  "ranking.passHint": "Ranked by completed passes.",
  "ranking.duelHint": "Ranked by duels won — challenges for the ball estimated from who came away with it, plus tagged tackles and dribbles.",
  "ranking.empty": "The ranking is on its way",
  "ranking.emptyBody": "Players appear here when this game has ratings or tagged stats.",
  "ranking.metricEmpty": "No measured {metric} yet",
  "ranking.metricEmptyBody": "Choose another stat to see the figures available for this game.",
  "ranking.source": "The same ranking your players see in the app — built from this game's ratings and recorded stats. Equal values share a place.",

  // Summary stats
  "stat.goals": "Goals",
  "stat.possessions": "Possessions",
  "stat.shotsTotal": "Shots in total",
  "stat.fouls": "Fouls",
  "stat.offsides": "Offsides",
  "stat.freeKicks": "Free kicks",
  "stat.corners": "Corners",
  "stat.avgRating": "Average player game rating",
  // Offensive
  "stat.shotsOn": "Shots on target",
  "stat.shotsOff": "Shots off target",
  "stat.shotsBlocked": "Shots blocked",
  "stat.shotsInsidePA": "Shots inside PA",
  "stat.shotsOutsidePA": "Shots outside PA",
  "stat.successfulTakeOns": "Successful take ons",
  "stat.allTakeOns": "All take ons",
  "stat.shotConversionRate": "Shot Conversion Rate",
  // Defensive
  "stat.tacklesWon": "Tackles won",
  "stat.allTackles": "All tackles",
  "stat.interceptions": "Interceptions",
  "stat.recoveries": "Ball recoveries",
  "stat.turnovers": "Possession lost",
  "stat.clearances": "Clearances",
  "stat.blocks": "Blocks",
  "stat.aerialsWon": "Aerials won",
  "stat.allAerials": "All aerials",
  "stat.duelsWon": "Duels won",
  "stat.allDuels": "All duels",
  // Distribution
  "stat.successfulPasses": "Successful passes",
  "stat.passes": "Passes",
  "stat.passAccuracy": "Pass accuracy",
  "stat.keyPasses": "Key passes",
  "stat.successfulPassesFinal": "Successful passes in final third",
  "stat.allPassesFinal": "All passes in final third",
  "stat.successfulPassesMid": "Successful passes in middle third",
  "stat.allPassesMid": "All passes in middle third",
  "stat.successfulPassesDef": "Successful passes in defensive third",
  "stat.allPassesDef": "All passes in defensive third",
  "stat.successfulLong": "Successful long passes",
  "stat.allLong": "All long passes",
  "stat.successfulMedium": "Successful medium range passes",
  "stat.allMedium": "All medium range passes",
  "stat.successfulShort": "Successful short passes",
  "stat.allShort": "All short passes",
  "stat.successfulForward": "Successful forward passes",
  "stat.allForward": "All forward passes",
  "stat.successfulSideways": "Successful sideways passes",
  "stat.allSideways": "All sideways passes",
  "stat.successfulBackward": "Successful backward passes",
  "stat.allBackward": "All backward passes",
  "stat.successfulCrosses": "Successful crosses",
  "stat.allCrosses": "All crosses",
  "stat.fieldTilt": "Field Tilt",
  "stats.keyPassesLabel": "key passes",

  // Pass network (PassNetworkPro)
  "pn.network": "Network",
  "pn.pass": "Pass",
  "pn.watchPass": "Watch this pass",
  "pn.close": "Close",
  "pn.play": "Play",
  "pn.pause": "Pause",
  "pn.replay": "Replay",
  "pn.srcFollow": "1080p follow video",
  "pn.srcWide": "Wide panorama",
  "pn.estimatedNote": "Estimated positions — placed from the ball track, not measured tracking",
  "pn.approxNote": "Approximate view — this match predates per-pass tracking; positions are schematic",
  "pn.passesMadeSuffix": "passes made",
  "pn.passesReceivedSuffix": "passes received",
  "pn.tapPlayerHint": "Tap any player to see every pass they made — or received",
  "pn.tapPassWent": "Tap a pass below to trace where the ball went",
  "pn.tapPassCame": "Tap a pass below to trace where the ball came from",
  "pn.attacking": "Attacking",
  "pn.middle": "Middle",
  "pn.defensive": "Defensive",
  "pn.thirdWord": "third",
  "pn.playerInvolvement": "Player (size = involvement)",
  "pn.connectionVolume": "Connection (width = volume)",
  "pn.forward": "Forward",
  "pn.sideways": "Sideways",
  "pn.backward": "Backward",
  "pn.passDetail": "Pass detail",
  "pn.completed": "Completed",
  "pn.lost": "Lost",
  "pn.direction": "Direction",
  "pn.length": "Length",
  "pn.into": "Into",
  "pn.lane": "Lane",
  "pn.minute": "Minute",
  "pn.outcome": "Outcome",
  "pn.retained": "Retained",
  "pn.turnover": "Turnover",
  "pn.left": "Left",
  "pn.central": "Central",
  "pn.right": "Right",
  "pn.prev": "‹ Prev",
  "pn.backToList": "Back to list",
  "pn.next": "Next ›",
  "pn.backToOverview": "Back to team overview",
  "pn.passesMade": "Passes made",
  "pn.passesReceived": "Passes received",
  "pn.passesMadeBy": "passes made",
  "pn.passesReceivedBy": "passes received",
  "pn.received": "Received",
  "pn.passesWord": "Passes",
  "pn.avgLength": "Avg length",
  "pn.dirIntoHim": "Direction of balls into him",
  "pn.dirOfPlay": "Direction of play",
  "pn.everyPassMade": "Every pass made",
  "pn.everyPassReceived": "Every pass received",
  "pn.all": "All",
  "pn.noPassesMade": "No passes made in this filter",
  "pn.noPassesReceived": "No passes received in this filter",
  "pn.aiRead": "AI read",
  "pn.totalPasses": "Total passes",
  "pn.completion": "Completion",
  "pn.progressive": "Progressive",
  "pn.intoEachThird": "Into each third",
  "pn.passLength": "Pass length",
  "pn.short": "Short",
  "pn.medium": "Medium",
  "pn.long": "Long",
  "pn.topCombos": "Top combinations",
  "pn.mostMade": "Most passes made",
  "pn.mostReceived": "Most passes received",
  "pn.networkRead": "Network read",

  // Shot map (ShotMapPro)
  "sm.both": "Both",
  "sm.allShots": "All shots",
  "sm.full": "Full",
  "sm.goal": "Goal",
  "sm.onTarget": "On target",
  "sm.offTarget": "Off target",
  "sm.blocked": "Blocked",
  "sm.unplaced": "{n} shots not on the map — tagged, but the ball track has no position for them, so they carry no xG:",
  "sm.unplacedOne": "1 shot not on the map — tagged, but the ball track has no position for it, so it carries no xG:",
  "sm.attack": "attack",
  "sm.sizeXgTap": "size = xG · tap a shot",
  "sm.shotDetail": "Shot detail",
  "sm.distance": "Distance",
  "sm.half": "Half",
  "sm.first": "First",
  "sm.second": "Second",
  "sm.quality": "Quality",
  "sm.bigChance": "Big chance",
  "sm.decent": "Decent",
  "sm.halfChance": "Half-chance",
  "sm.backToShotMap": "Back to shot map",
  "sm.shots": "Shots",
  "sm.on": "On",
  "sm.goals": "Goals",
  "sm.conversion": "conversion",
  "sm.xgRace": "xG race (cumulative)",
  "sm.goalMarker": "● = goal",
  "sm.shotOutcomes": "Shot outcomes (both teams)",
  "sm.topShooters": "Top shooters (by xG)",
  "sm.shotRead": "Shot read",
  "sm.showingShots": "Showing",
  "sm.shotsClear": "shots — clear",

  // Viz pending
  "viz.generatingFromVideo": "— generating from match video",
  "viz.generatingBody": "Draffte's AI camera is processing this match to place every shot, pass and touch at its true pitch position. This visualization unlocks automatically once the computer-vision pass completes.",
  "viz.aiProcessing": "AI processing",

  // Analyst chat
  "analyst.askAnalyst": "Ask the Analyst",
  "analyst.grounded": "answers grounded in this match's measured data",
  "analyst.placeholder": "Ask anything about this match…",
  "analyst.unavailable": "The analyst is unavailable right now — please try again in a moment.",
  "analyst.q1": "Where did we lose the ball most often?",
  "analyst.q2": "Who was our most dangerous player?",
  "analyst.q3": "What should we focus on in training this week?",
  "reel.aiHighlight": "AI Highlight Reel",
  "reel.cvHeatmap": "CV Position Heatmap",

  // H2H summary
  "h2h.played": "Played",
  "h2h.wins": "Wins",
  "h2h.draws": "Draws",
  "h2h.losses": "Losses",
  "h2h.win": "WIN",
  "h2h.draw": "DRAW",
  "h2h.loss": "LOSS",
  "h2h.headToHead": "Head-to-Head vs",
  "h2h.pending": "No previous meetings recorded with this opponent yet — head-to-head history builds as you play them.",
  "h2h.goalsFor": "GF",
  "h2h.goalsAgainst": "GA",
  "h2h.homeShort": "H",
  "h2h.awayShort": "A",

  // Player-table column glossary (header tooltips)
  "col.shirtNumber.name": "Shirt Number",
  "col.shirtNumber.desc": "The player's jersey number",
  "col.player.name": "Player",
  "col.player.desc": "Player name",
  "col.position.name": "Position",
  "col.position.desc": "Position played on the pitch",
  "col.rating.name": "Match Rating",
  "col.rating.desc": "Overall performance score out of 10",
  "col.minutes.name": "Minutes Played",
  "col.minutes.desc": "Total minutes on the pitch",
  "col.goals.name": "Goals",
  "col.goals.desc": "Goals scored",
  "col.assists.name": "Assists",
  "col.assists.desc": "Passes that directly led to a goal",
  "col.shots.name": "Shots",
  "col.shots.desc": "Total shot attempts",
  "col.keyPasses.name": "Key Passes",
  "col.keyPasses.desc": "Passes that created a shooting chance",
  "col.fouls.name": "Fouls",
  "col.fouls.desc": "Fouls committed",
  "col.yellowCards.name": "Yellow Cards",
  "col.yellowCards.desc": "Cautions received",
  "col.redCards.name": "Red Cards",
  "col.redCards.desc": "Sending-offs received",
  "col.shotsOnTarget.name": "Shots on Target",
  "col.shotsOnTarget.desc": "Shots that hit the target",
  "col.offDuels.tag": "Disp.",
  "col.offDuels.name": "Dispossessed (offensive duels lost)",
  "col.offDuels.desc": "He had the ball and an opponent took it off him (estimated from the match, plus tagged dribbles lost). Duels he won by keeping the ball are only counted when tagged, so no win rate is shown",
  "col.defDuels.tag": "Def. duels",
  "col.defDuels.name": "Defensive duels won",
  "col.defDuels.desc": "The opponent had the ball and he challenged: times he won it (estimated, plus tagged tackles). Lost ones are not shown — a defender who is beaten leaves no tag",
  "col.dribbles.name": "Dribbles",
  "col.dribbles.desc": "Take-ons: won / attempted (tagged)",
  "col.tackles.name": "Tackles",
  "col.tackles.desc": "Tackles: won / attempted (tagged)",
  "col.interceptions.name": "Interceptions",
  "col.interceptions.desc": "Opponent passes cut out",
  "col.clearances.name": "Clearances",
  "col.clearances.desc": "Defensive clearances of the ball",
  "col.blocks.name": "Blocks",
  "col.blocks.desc": "Shots or crosses blocked",
  "col.passesCompleted.name": "Passes Completed",
  "col.passesCompleted.desc": "Passes that reached a teammate",
  "col.passesAttempted.name": "Passes Attempted",
  "col.passesAttempted.desc": "Total passes attempted",
  "col.passAccuracy.name": "Pass Accuracy",
  "col.passAccuracy.desc": "Share of passes completed",
  "col.crossesCompleted.name": "Crosses Completed",
  "col.crossesCompleted.desc": "Crosses that found a teammate",
  "col.crossesAttempted.name": "Crosses Attempted",
  "col.crossesAttempted.desc": "Total crosses attempted",
  "col.goalsConceded.name": "Goals Conceded",
  "col.goalsConceded.desc": "Goals let in while on the pitch",
  "col.catches.name": "Catches",
  "col.catches.desc": "Crosses/shots caught cleanly",
  "col.parries.name": "Parries / Blocks",
  "col.parries.desc": "Saves turned behind for a corner or pushed back to an opponent",
  "col.gkFaced.name": "Shots on Target Faced",
  "col.gkFaced.desc": "Opponent shots on target while this keeper was in goal",
  "col.saves.name": "Saves",
  "col.saves.desc": "Shots on target that were not goals. In brackets: the ones where the keeper's own touch was tagged",
  "col.savePct.name": "Save %",
  "col.savePct.desc": "Open-play saves over open-play shots on target (penalties excluded). Shown once three shots have been faced",
  "col.cleanSheet.name": "Clean Sheet",
  "col.cleanSheet.desc": "No goal conceded over most of his time in goal. Needs the match clock",
  "col.gkKicks.name": "Goal Kicks Kept",
  "col.gkKicks.desc": "Goal kicks that left us with the ball, over the ones that resolved (kept or lost)",
  "col.gkBallWins.name": "Ball Wins",
  "col.gkBallWins.desc": "Times he won the ball straight off an opponent",
  "col.penalties.name": "Penalties Saved / Faced",
  "col.penalties.desc": "Penalties saved over penalties faced. A dash means none was faced",
  "col.gkMinutes.name": "Minutes in Goal",
  "col.gkMinutes.desc": "Minutes spent in goal. A dash means the match clock was not tagged",
  "col.saves.tag": "Sv",
  "col.gkFaced.tag": "Faced",
  "col.savePct.tag": "Sv%",
  "col.cleanSheet.tag": "CS",
  "col.gkKicks.tag": "GK",
  "col.gkBallWins.tag": "BW",
  "col.penalties.tag": "Pen",
  "stats.gkNotIdentified": "The goalkeeper could not be identified for this match, so no keeper stats are shown.",
  "stats.gkAmbiguous": "Two goalkeepers were listed in the starting XI and the tags do not say which kept goal, so nobody is credited.",
  "stats.gkNoClock": "The match clock was not tagged: minutes in goal and clean sheets are not available.",
  "stats.gkFootnote": "Saves are shots on target that were not goals; the number in brackets is how many carried the keeper's own touch. Save % is open play only. A dash means not measured. Crosses claimed, punches, sweeping and one-on-ones are not measured yet.",
  "games.shotsFaced": "Shots faced",
  "games.savePct": "Save %",
  "games.cleanSheet": "Clean sheet",
  "games.yes": "Yes",
  "games.no": "No",
  "games.gkPensNote": "Penalties faced: {saved} saved of {faced}",
  "col.goalKicksSuccessful.name": "Goal Kicks Successful",
  "col.goalKicksSuccessful.desc": "Goal kicks that reached a teammate",
  "col.goalKicksAttempted.name": "Goal Kicks Attempted",
  "col.goalKicksAttempted.desc": "Total goal kicks taken",
  "col.gkIntSuccessful.name": "Interceptions Successful",
  "col.gkIntSuccessful.desc": "Successful keeper interceptions/sweeps",
  "col.gkIntAttempted.name": "Interceptions Attempted",
  "col.gkIntAttempted.desc": "Total keeper interception attempts",

  // Video event types
  "ev.goal": "Goal",
  "ev.shot": "Shot",
  "ev.shotOnTarget": "Shot on Target",
  "ev.yellowCard": "Yellow Card",
  "ev.redCard": "Red Card",
  "ev.substitution": "Substitution",
  "ev.corner": "Corner",
  "ev.freeKick": "Free Kick",
  "ev.keyPass": "Key Pass",
  "ev.tackle": "Tackle",
  "ev.save": "Save",
  "ev.offside": "Offside",
  "ev.foul": "Foul",
  "ev.event": "Event",

  // Stats — misc hardcoded strings
  "stats.halfTime": "HT",
  "stats.reelAutoCut": "Cut automatically by Draffte Vision from the full match recording — moments where #{n} was on the ball.",
  "stats.thirdAttacking": "Attacking",
  "stats.thirdMiddle": "Middle",
  "stats.thirdDefensive": "Defensive",
  "stats.thirdSuffix": "third",
  // Pass legend / pass explorer
  "stats.outgoing": "Outgoing",
  "stats.incoming": "Incoming",
  "stats.failed": "Failed",
  "stats.arrowToReceiver": "Arrow points to receiver",
  "stats.eachColourConnection": "Each colour = one connection (see list)",
  "stats.bandsThirds": "Bands = % of passes into each third",
  "stats.clickPlayerIsolate": "· click a player to isolate",
  "stats.passesIntoThirdBody": "Share of completed passes ending in each third of the pitch — where this team built and finished its play.",
  "stats.completed": "Completed",
  "stats.received": "Received",
  "stats.accuracy": "Accuracy",
  "stats.connectionsSuffix": "Connections",
  "stats.allConnections": "All Connections",
  "stats.passesArrow": "passes →",
  "stats.colourPairVolume": "colour = pair · width = volume",
  "stats.passProfile": "Pass Profile",
  "stats.passProfileMain": "{player} completed {out} passes with {failed} failed ({pct}% accuracy). ",
  "stats.passProfileBest": "Most reliable connection: {to} ({pct}%). ",
  "stats.passProfileWorst": "Struggled reaching {to} ({pct}% — {failed} failed).",
  "stats.momentumHowTo": "How to read this:",
  "stats.momentumHowToBody": "each bar is one minute of play. Bars rising above the line are minutes {home} were on top — holding the ball high up the pitch and creating chances; bars dropping below are minutes {away} had control. The taller the bar, the clearer the edge. A ball on a white post marks a goal, on the side of the team that scored.",
  "stats.momentumGoal": "Goal",
  // Possession key-insight (generated prose)
  "stats.chanLeft": "left",
  "stats.chanCentral": "central",
  "stats.chanRight": "right",
  "stats.keyInsightBody": "{topPct}% of the measured ball activity was in the {topChan} channel",
  "stats.keyInsightImbalanced": " while the {lowChan} side saw just {lowPct}% — consider switching play more often",
  "stats.keyInsightBalanced": " — a balanced spread across the pitch",
  // Momentum AI story (generated prose)
  "stats.momStoryControlled": "{team} controlled the overall flow",
  "stats.momStoryBalanced": ", though it stayed finely balanced",
  "stats.momStoryHomeSpell": "{home}'s strongest spell came around the {min}' mark, ",
  "stats.momStoryHomeSustained": "with roughly {mins} minutes of sustained pressure",
  "stats.momStoryHomeBursts": "in short bursts",
  "stats.momStoryAwayPush": "{away} pushed back hardest around the {min}' mark",
  "stats.momStoryAwayClaim": ", claiming about {mins} minutes of their own",
  "stats.momStoryXg": "By full time the expected-goals split was {hxg}–{axg}, ",
  "stats.momStoryXgHome": "reflecting {home}'s greater threat",
  "stats.momStoryXgAway": "flattering {away}'s chances",
  "stats.momStoryXgEven": "an even contest",

  // Player table / video / insights / heatmaps
  "stats.noPlayerData": "No player data",
  "stats.aiReel": "AI REEL",
  "stats.matchVideoHere": "Match video will appear here",
  "stats.possession": "Possession",
  "stats.shots": "Shots",
  "stats.eventsAll": "All",
  "stats.keyMoments": "Key Moments",
  "stats.goalsFilter": "Goals",
  "stats.shotsFilter": "Shots",
  "stats.cardsFilter": "Cards",
  "stats.fullMatchFilter": "Full Match",
  "stats.firstHalf": "1st Half",
  "stats.secondHalf": "2nd Half",
  "stats.events": "Events",
  "stats.clickToJump": "Click to jump",
  "stats.play": "PLAY",
  "stats.opp": "OPP",
  "stats.aiMatchAnalysis": "AI Match Analysis",
  "stats.generatedFromData": "Generated from match data",
  "stats.strengths": "Strengths",
  "stats.areasToImprove": "Areas to Improve",
  "stats.tacticalInsights": "Tactical Insights",
  "stats.insightsPending": "AI match insights appear here once the video has been analysed. You can still ask the analyst above about this game.",
  "stats.momentumPending": "The momentum & xG timeline is built from match-video analysis — it appears here once this game has been processed.",
  "stats.recommendations": "Recommendations for Next Match",
  "stats.possessionZones": "Possession Zones",
  "shape.title": "Team shape",
  "shape.caption": "Where {team}'s outfield players stood while the ball was in play, measured from the match video. Both halves are turned so the team attacks upward.",
  "shape.attack": "ATTACK ↑",
  "shape.measured": "Measured",
  "shape.thirds": "Time by third",
  "shape.attThird": "Attacking third",
  "shape.midThird": "Middle third",
  "shape.defThird": "Defensive third",
  "shape.lanes": "Time by lane",
  "shape.left": "Left",
  "shape.centre": "Centre",
  "shape.right": "Right",
  "shape.note": "On average {n} of our outfield players were tracked at once over {m} minutes in play. The goalkeeper wears another kit and is not included. This is the team, not individual players: far from the camera we can see players but not read their numbers.",
  "stats.shotMap": "Shot Map",
  "stats.teamStats": "Team stats",
  "stats.passNetwork": "Pass Network",
  "stats.passZones": "Zones",
  "stats.passPlayers": "Players",
  "stats.estimated": "Estimated",
  "running.title": "Running (est.)",
  "running.none": "No running data for this game yet. It is measured from our side's video after the game is finished.",
  "running.distance": "Distance (est.)",
  "running.perMin": "m/min",
  "running.topSpeed": "Top speed",
  "running.measured": "measured {n} of {m} min",
  "running.measuredOnly": "measured {n} min",
  "running.note": "Estimated from the panorama camera on part of the game only.",
  "running.careerTitle": "Running · {n} games measured",
  "running.careerTitleOne": "Running · {n} game measured",
  "running.avgDistance": "Avg distance / game (est.)",
  "running.avgPerMin": "Avg m/min (est.)",
  "running.bestTop": "Best top speed (est.)",
  "stats.passSplitsNote": "Pass-type splits estimated from video (tagger logged totals only)",
  "stats.needsVideo": "These stats come from match-video analysis — coming soon.",
  "stats.possessionHeatmap": "Possession heatmap",
  "stats.lowActivity": "Low activity",
  "stats.mediumActivity": "Medium",
  "stats.highActivity": "High activity",
  "stats.heatZonesCaption": "Ball activity by pitch zone — each circle is an area of the pitch, not a player.",
  "stats.heatZonesLegend": "share of the team's ball activity",
  "stats.heatZonesLegendBoth": "share of all ball activity (both teams)",
  "stats.heatZonesLegendTeam": "share of {team}'s ball activity",
  "stats.bothTeams": "Both teams",
  "viz.notAvailable": "{feature} isn't available for this game",
  "viz.notAvailableBody": "This view is built from the analysed match video, and this game wasn't analysed.",
  "stats.hotspotBreakdown": "Hotspot Breakdown",
  "stats.leftSide": "Left Side",
  "stats.centralSide": "Central",
  "stats.rightSide": "Right Side",
  "stats.keyInsight": "Key Insight",
  "stats.shotDetails": "Shot Details",
  "stats.noShotsFilter": "No shots for this filter.",
  "stats.totalXg": "Total xG",
  "stats.conversion": "Conversion",
  "stats.avgDistance": "Avg Distance",
  "stats.insideBox": "Inside Box",
  "stats.selectPlayer": "Select Player",
  "stats.allPlayers": "All Players",
  "stats.passesIntoThird": "Passes Into Each Third",
  "stats.attackingThird": "Attacking third",
  "stats.middleThird": "Middle third",
  "stats.defensiveThird": "Defensive third",
  "stats.coachNotes": "Coach Notes",
  "stats.addNote": "Add a note…",
  "stats.saveNote": "Save Note",
  "stats.savedNotes": "Saved Notes",
  "stats.noNotes": "No notes yet.",
  "stats.formationTitle": "Formation",
  "stats.momentumTitle": "Match Momentum",
  "stats.homeMomentum": "Home Momentum",
  "stats.momentumLegend": "Momentum",
  "stats.homeXg": "Home xG",
  "stats.awayXg": "Away xG",
  "stats.actualGoals": "Actual Goals",
  "stats.aiReadMomentum": "AI Read: Momentum Story",
  "stats.notesSaved": "saved · stored on this device",
  "stats.notesPlaceholder": "Add your tactical notes, observations, and action items...",
  "stats.deleteNote": "Delete note",

  // ── Matches list ──
  "matches.title": "Matches",
  "matches.seasonSub": "Regional League season",
  "matches.addGameCalendar": "Add Game Calendar",
  "matches.addMatch": "Add Match",
  "matches.noMatchesYet": "No matches recorded yet",
  "matches.noMatchesBody": "Start tracking your team's performance by adding your first match result or upcoming fixture.",
  "matches.played": "Played",
  "matches.points": "Points",
  "matches.won": "Won",
  "matches.drawn": "Drawn",
  "matches.lost": "Lost",
  "matches.goalDifference": "Goal Difference",
  "matches.seasonRecord": "Season Record",
  "matches.formLast5": "Form — Last 5",
  "matches.mostRecentLeft": "Most recent on the left",
  "matches.upcomingFixtures": "Upcoming Fixtures",
  "matches.results": "Results",
  "matches.noMatchesFilter": "No matches match this filter",
  "matches.pointsProgression": "Points Progression",
  "matches.matchEvents": "Match Events",
  "matches.playerRatings": "Player Ratings",
  "matches.coachNotes": "Coach Notes",
  "matches.fullGameRecording": "Full Game Recording",
  "matches.viewFullDetails": "View Full Details",
  "matches.fullDetails": "Full Details",
  "matches.overview": "Overview",
  "matches.editTeamName": "Edit team name",
  "matches.saveTeamName": "Save team name",
  "matches.watchGame": "Watch Game ▶",
  "matches.watchGameAfter": "Watch Game is available after the match is played.",
  "matches.all": "All",
  "matches.upcoming": "Upcoming",
  "matches.win": "W",
  "matches.draw": "D",
  "matches.loss": "L",

  // ── Sessions (coach-home/sessions) ──
  "sessions.title": "Sessions",
  "sessions.trainingLog": "Training log",
  "sessions.attendance": "Attendance",
  "sessions.newSession": "New session",
  "sessions.noSessionsTitle": "No training sessions yet",
  "sessions.noSessionsBody": "Create your first session to plan training and take attendance for your squad.",
  "sessions.totalSessions": "Total Sessions",
  "sessions.allTime": "All time",
  "sessions.thisWeek": "This Week",
  "sessions.sessionsSub": "Sessions",
  "sessions.avgAttendance": "Avg Attendance",
  "sessions.perSession": "Per session",
  "sessions.squadSize": "Squad Size",
  "sessions.players": "Players",
  "sessions.upcoming": "Upcoming",
  "sessions.trainingLogHeading": "Training Log",
  "sessions.sessionsCount": "sessions",
  "sessions.noFilterMatch": "No sessions match this filter",
  "sessions.all": "All",
  // AI training focus
  "sessions.aiTrainingFocus": "AI Training Focus",
  "sessions.aiTrainingSub": "What to work on this week — from your last match",
  "sessions.group.defense": "Defense",
  "sessions.group.midfield": "Midfield",
  "sessions.group.forwards": "Forwards",
  "sessions.group.goalkeepers": "Goalkeepers",
  "sessions.fallbackSummary": "Based on the last match, the priority is sharpening defensive transitions and converting the chances we created. Build this week's sessions around regaining shape after losing the ball and finishing with composure in the final third.",
  "sessions.fallbackDefense": "Drill the recovery run and re-organisation after turnovers — we conceded space in behind on the counter. Add 1v1 defending and communication on the back line.",
  "sessions.fallbackMidfield": "Work counter-pressing triggers and quicker ball circulation. The midfield was bypassed too easily; rehearse compactness and the press-and-cover rotations.",
  "sessions.fallbackForwards": "Finishing under pressure and movement off the last defender. We created enough but were wasteful — repetition on first-time finishes and timing of runs.",
  "sessions.fallbackGoalkeepers": "Distribution to start attacks and command of the box on crosses. Add reaction-save work and sweeping behind a high line.",
  // Session types (display labels; ids stay English)
  "sessions.type.Tactical": "Tactical",
  "sessions.type.Physical": "Physical",
  "sessions.type.Technical": "Technical",
  "sessions.type.Recovery": "Recovery",
  "sessions.type.MatchPrep": "Match Prep",
  "sessions.defaultFocus": "Training session",
  // Session card
  "sessions.tomorrow": "Tomorrow",
  "sessions.today": "Today",
  "sessions.inDays": "In {n} days",
  "sessions.planned": "Planned",
  "sessions.present": "present",
  "sessions.viewDetails": "View details",
  // Detail panel
  "sessions.percentPresent": "% present",
  "sessions.absent": "absent",
  "sessions.coachNotes": "Coach Notes",
  "sessions.tapToToggle": "tap to toggle",
  "sessions.presentBadge": "Present",
  "sessions.absentBadge": "Absent",
  "sessions.attendanceNote": "Attendance Note",
  "sessions.saved": "Saved",
  "sessions.attendanceNotePlaceholder": "Who was late, injured, or excused — and why…",
  // Attendance modal
  "sessions.takeAttendance": "Take Attendance",
  "sessions.uncheckAbsent": "uncheck anyone absent",
  "sessions.presentAbsentSummary": "{present} present · {absent} absent",
  "sessions.uncheckAll": "Uncheck all",
  "sessions.checkAll": "Check all",
  "sessions.attendanceSaved": "Attendance saved!",
  "sessions.saveAttendance": "Save attendance",
  // New session modal
  "sessions.newSessionTitle": "New Session",
  "sessions.planNextTraining": "Plan your next training",
  "sessions.date": "Date",
  "sessions.durationMin": "Duration (min)",
  "sessions.sessionType": "Session type",
  "sessions.sessionFocus": "Session focus",
  "sessions.focusPlaceholder": "e.g. High press triggers & defensive shape",
  "sessions.notesOptional": "Notes (optional)",
  "sessions.notesPlaceholder": "Pre-session notes or instructions...",
  "sessions.expectedPlayers": "Expected players",
  "sessions.deselectAll": "Deselect all",
  "sessions.selectAll": "Select all",
  "sessions.ofSelected": "{n} of {total} selected",
  "sessions.sessionScheduled": "Session scheduled!",
  "sessions.scheduleSession": "Schedule session",

  // ── Squad / roster ──
  "squad.management": "Squad Management",
  "squad.playersRegistered": "players registered · Season 2025/26",
  "squad.lineup": "Lineup",
  "squad.resetLineup": "Reset lineup",
  "squad.searchPlayers": "Search players…",
  "squad.noPlayersYet": "No players yet",
  "squad.noPlayersBody": "Add players to your squad to start managing formations, tracking performance, and analyzing stats.",
  "squad.bench": "Bench",
  "squad.noBench": "No bench players",
  "squad.formation": "Formation",
  "squad.physicalProfile": "Physical & Profile",
  "squad.thisWeek": "This week",
  "squad.attributes": "Attributes",
  "squad.passportConnectors": "Passport Connectors",
  "squad.kitTitle": "Team kits",
  "squad.kitBody": "Design your home, away and goalkeeper kits — shirt, pattern, shorts and socks. Tap a kit to customise.",
  "squad.kitHome": "Home",
  "squad.kitAway": "Away",
  "squad.kitGk": "Goalkeeper",
  "squad.kitGkHome": "GK Home",
  "squad.kitGkAway": "GK Away",
  "squad.kitEdit": "Edit",
  "squad.kitEditing": "Editing",
  "squad.kitPreviewHint": "Live preview",
  "squad.kitPattern": "Pattern",
  "squad.kitShirt": "Shirt colour",
  "squad.kitAccent": "Accent / trim",
  "squad.kitShorts": "Shorts",
  "squad.kitSocks": "Socks",
  "squad.kitSave": "Save kit",
  "squad.kitCancel": "Cancel",
  "squad.kitSaved": "Saved",
  "squad.kitExtraAdd": "Add kit",
  "squad.kitExtraName": "Kit name",
  "squad.kitExtraDefaultName": "New kit",
  "squad.kitExtraRemove": "Remove this kit",
  "squad.kitExtraDeleteTitle": "Delete this kit?",
  "squad.kitExtraDeleteBody": "the design is removed for good, here and in the app. Your four standard kits are not affected.",
  "squad.kitExtraDeleteConfirm": "Delete kit",
  "squad.logoTitle": "Team logo",
  "squad.logoBody": "Upload your club crest — it appears on your squad and across the app.",
  "squad.logoUpload": "Upload logo",
  "squad.logoReplace": "Replace logo",
  "squad.logoUploading": "Uploading…",
  "squad.logoAlt": "Team logo",
  "squad.patSolid": "Solid",
  "squad.patStripes": "Stripes",
  "squad.patHoops": "Hoops",
  "squad.patSash": "Sash",
  "squad.patHalves": "Halves",
  "squad.patChecks": "Checks",
  // ── Signup (web) ──
  "signup.signIn": "Sign in",
  "signup.eyebrow": "Get started",
  "signup.whoTitle": "Who are you?",
  "signup.whoSub": "Choose your account type to get the right experience",
  "signup.langLabel": "Language",
  "signup.player": "Player",
  "signup.playerDesc": "Track your performance, build your Passport card and get scouted",
  "signup.coach": "Coach",
  "signup.coachDesc": "Manage your squad, analyse match data and track every player's development",
  "signup.getStarted": "Get started",
  "signup.haveAccount": "Already have an account?",
  "signup.stepWord": "Step",
  "signup.genderTitle": "Gender",
  "signup.genderSub": "Select your gender",
  "signup.genderMale": "Male",
  "signup.genderFemale": "Female",
  "signup.nameTitle": "Your name",
  "signup.nameSub": "As it appears on your jersey",
  "signup.firstName": "First name",
  "signup.firstNamePh": "First",
  "signup.lastName": "Last name",
  "signup.lastNamePh": "Last",
  "signup.heightTitle": "Height",
  "signup.heightSub": "Select your height",
  "signup.unitCm": "CM",
  "signup.unitFeetInches": "Feet & inches",
  "signup.weightTitle": "Weight",
  "signup.weightSub": "Balance your stats",
  "signup.unitKg": "KG",
  "signup.unitLbs": "Lbs",
  "signup.footTitle": "Preferred foot",
  "signup.footSub": "Select your stronger foot",
  "signup.footLeft": "Left",
  "signup.footRight": "Right",
  "signup.footBoth": "Both",
  "signup.dobTitle": "Date of birth",
  "signup.dobSub": "When were you born?",
  "signup.dobDay": "Day",
  "signup.dobMonth": "Month",
  "signup.dobYear": "Year",
  "signup.positionTitle": "Positions",
  "signup.positionSub": "Tap every position you can play",
  "signup.positionHint": "Pick one or more on the pitch — your first pick is your main role.",
  "signup.jerseyTitle": "Team & jersey",
  "signup.jerseySub": "Join your team and set your number — helps cameras track you accurately",
  "signup.teamCode": "Team code",
  "signup.teamCodePh": "e.g. AB12CD",
  "signup.teamChecking": "Checking…",
  "signup.teamJoining": "Joining",
  "signup.teamNotFound": "No team found for that code — double-check with your coach.",
  "signup.teamCodeHelper": "Ask your coach for the 6-character code to land on their squad. You can skip this and add it later.",
  "signup.teamCodeHelperRequired": "Ask your coach for the 6-character code to land on their squad. At least one valid team is required — add more codes to join several teams at once.",
  "signup.teamCodeAddAnother": "Add another team code",
  "signup.teamCodeRemove": "Remove team code",
  "signup.jerseyNumber": "Your jersey number",
  "signup.jerseyNumberHelp": "Enter the number you wear on your jersey. This helps our AI identify you during matches — even when you run behind other players.",
  "signup.jerseyColor": "Your team's jersey color",
  "signup.jerseyColorSkip": "You can skip this — your coach can also set it.",
  "signup.jerseyColorByCoach": "Your coach sets the team's kit colours.",
  "signup.nationalityTitle": "Nationality",
  "signup.nationalitySub": "Search and select your country",
  "signup.searchCountry": "Search country...",
  "signup.teamsTitle": "Team history",
  "signup.teamsSub": "Current and past clubs",
  "signup.noCurrentClub": "I don't have a current club",
  "signup.clubSingular": "club",
  "signup.clubsPlural": "clubs",
  "signup.scrollToSeeAll": "scroll to see all",
  "signup.current": "Current",
  "signup.past": "Past",
  "signup.selectCountry": "Select country",
  "signup.clubName": "Club name",
  "signup.fromYear": "From (year)",
  "signup.yearPh2019": "e.g. 2019",
  "signup.yearErrFull": "Must be XXXX (e.g. 2019)",
  "signup.toYear": "To (year)",
  "signup.present": "Present",
  "signup.yearPh2023": "e.g. 2023",
  "signup.yearErrShort": "Must be XXXX",
  "signup.yearErrOrder": "To cannot be before From",
  "signup.currentClub": "Current club",
  "signup.scrollMoreClubs": "↓ Scroll down for more clubs",
  "signup.addAnotherClub": "Add another club",
  "signup.photoTitle": "Profile picture",
  "signup.photoSub": "Professional photo for your Passport",
  "signup.photoHint": "Use the upload area on the right.",
  "signup.accountTitle": "Create account",
  "signup.accountSub": "Email and password to sign in",
  "signup.email": "Email",
  "signup.emailPh": "you@email.com",
  "signup.password": "Password (min 8)",
  "signup.confirmPassword": "Confirm password",
  "signup.passwordsMismatch": "Passwords don't match",
  "signup.vGenderHint": "Select your gender on the left",
  "signup.vYourName": "Your name",
  "signup.vFootHint": "Choose your foot on the left",
  "signup.vNationalityHint": "Select a country on the left",
  "signup.vYourClubs": "Your clubs",
  "signup.vAddClubsHint": "Add clubs on the left — they'll appear here",
  "signup.vTapToUpload": "Tap to upload",
  "signup.vSecureAccount": "Secure account",
  "signup.back": "Back",
  "signup.next": "Next",
  "signup.createPassport": "Create my Passport",
  "signup.submitPortrait": "Creating your portrait…",
  "signup.submitJoining": "Joining your team…",
  "signup.submitCreating": "Creating…",
  "signup.successTitle": "You're on the list",
  "signup.successSub": "Your Passport is ready. View it below.",
  "signup.goToPassport": "Go to my Passport",
  "signup.backToHome": "Back to home",
  "signup.errEmailInUse": "An account with this email already exists. Please sign in.",
  "signup.errWeakPassword": "Password must be at least 6 characters.",
  "signup.errCreateFailed": "Account creation failed. Please try again.",
  // ── Player settings (web) ──
  "settings.title": "Settings",
  "settings.subtitle": "Manage your profile, language and account",
  "settings.photoChange": "Change photo",
  "settings.photoCreating": "Creating your portrait…",
  "settings.photoHint": "Tap the camera to update your portrait",
  "settings.profileTitle": "Profile",
  "settings.profileSub": "Edit the details you entered at sign-up",
  "settings.firstName": "First name",
  "settings.lastName": "Last name",
  "settings.gender": "Gender",
  "settings.genderMale": "Male",
  "settings.genderFemale": "Female",
  "settings.dob": "Date of birth",
  "settings.nationality": "Nationality",
  "settings.position": "Position",
  "settings.jersey": "Jersey number",
  "settings.height": "Height (cm)",
  "settings.weight": "Weight (kg)",
  "settings.foot": "Preferred foot",
  "settings.footLeft": "Left",
  "settings.footRight": "Right",
  "settings.footBoth": "Both",
  "settings.save": "Save changes",
  "settings.saving": "Saving…",
  "settings.saved": "Saved",
  "settings.langTitle": "Language",
  "settings.legalTitle": "Legal",
  "settings.terms": "Terms & Conditions",
  "settings.privacy": "Privacy Policy",
  "settings.cookies": "Cookie Policy",
  "settings.accountTitle": "Account",
  "settings.signOut": "Sign out",
  "settings.deleteAccount": "Delete account",
  "settings.deleteConfirmTitle": "Delete your account?",
  "settings.deleteConfirmBody": "This permanently deletes your Passport and all your data. This can't be undone.",
  "settings.deleteConfirm": "Delete forever",
  "settings.deleting": "Deleting…",
  "settings.cancel": "Cancel",
  // ── My Teams (player) ──
  "settings.teamsTitle": "My Teams",
  "settings.teamsSub": "Every team you play for",
  "settings.teamsLoading": "Loading your teams…",
  "settings.teamsEmpty": "You haven't joined a team yet. Enter a code below to join one.",
  "settings.teamsMember": "Squad member",
  "settings.teamsJoinTitle": "Join another team",
  "settings.teamsJoinPh": "Team code",
  "settings.teamsJoinBtn": "Join",
  "settings.teamsChecking": "Checking…",
  "settings.teamsJoining": "Joining",
  "settings.teamsNotFound": "No team found for that code — double-check with your coach.",
  "settings.teamsKeepHistory": "Joining a new team keeps all your existing teams and match history.",
  // ── Career (player) ──
  "career.title": "Career",
  "career.subtitle": "Your performance, game by game",
  "career.noRatedMatches": "No rated matches yet.",
  "career.chartAria": "OVR rating progression by match",
  "career.firstMatch": "First match",
  "career.matchSingular": "match",
  "career.matchPlural": "matches",
  "career.latest": "Latest",
  "career.noGamesTitle": "No games yet",
  "career.noGamesBody": "Play a rated match with your coach to build your career profile.",
  "career.uploadMatch": "See my games",
  "career.overall": "Overall",
  "career.avgRating": "Avg rating",
  "career.matches": "Matches",
  "career.potential": "Potential",
  "career.topPct": "Top {n}%",
  "career.percentile": "Percentile",
  "career.byTheNumbers": "By the numbers",
  "career.matchesPlayed": "Matches played",
  "career.goals": "Goals",
  "career.assists": "Assists",
  "career.playerOfMatch": "Player of the Match",
  "career.ovrProgression": "OVR progression",
  "career.whatsDriving": "What's driving your stats",
  "career.careerHighs": "Career highs",
  "career.bestRating": "Best rating",
  "career.vs": "vs",
  "career.mostGoalsGame": "Most goals (game)",
  "career.inSingleMatch": "In a single match",
  "career.biggestJump": "Biggest jump",
  "career.matchOnMatchGain": "Match-on-match OVR gain",
  "career.seasonRecap": "Season recap",
  // ── Achievements (player) ──
  "ach.title": "Achievements",
  "ach.subtitle": "Badges, streaks and personal records",
  "ach.close": "Close",
  "ach.unlocked": "Unlocked",
  "ach.locked": "Locked",
  "ach.progress": "Progress",
  "ach.inARow": "in a row",
  "ach.noAchievementsTitle": "No achievements yet",
  "ach.noAchievementsBody": "Play rated matches to start unlocking badges.",
  "ach.uploadMatch": "See my games",
  "ach.scoringStreak": "Scoring streak",
  "ach.manOfMatch": "Man of the Match",
  "ach.unbeatenRun": "Unbeaten run",
  "ach.trophyCabinet": "Trophy cabinet",
  "ach.of": "of",
  "ach.unlockedLower": "unlocked",
  "ach.badges": "Badges",
  "ach.activeStreaks": "Active streaks",
  "ach.personalRecords": "Personal records",
  "ach.bestRating": "Best rating",
  "ach.vs": "vs",
  "ach.mostGoalsGame": "Most goals (game)",
  "ach.biggestJump": "Biggest jump",
  "ach.nextUp": "Next up",
  // ── Scout report (player) ──
  "scout.title": "Scout Report",
  "scout.subtitle": "A professional scout's read on your game",
  "scout.locked": "Scout report locked",
  "scout.lockedBody": "Play a few rated matches and your AI scout report will appear here.",
  "scout.uploadMatch": "See my games",
  "scout.scoutGrade": "Scout grade",
  "scout.overall": "Overall",
  "scout.potential": "Potential",
  "scout.position": "Position",
  "scout.percentile": "Percentile",
  "scout.topPct": "Top {n}%",
  "scout.rankedPrefix": "Ranked",
  "scout.of": "of",
  "scout.playersSuffix": "players.",
  "scout.scoutCall": "Scout call",
  "scout.recommendation": "Recommendation",
  "scout.scoutVerdict": "Scout verdict",
  "scout.strengths": "Strengths",
  "scout.areasToDevelop": "Areas to develop",
  "scout.playingStyle": "Playing style",
  "scout.outlook": "Outlook",
  "scout.projection": "Projection",
  "scout.comparison": "Comparison",
  "scout.backToHome": "Back to Home",
  // ── Training (player) ──
  "train.typePhysical": "Physical",
  "train.typeTechnical": "Technical",
  "train.typeTactical": "Tactical",
  "train.typeRecovery": "Recovery",
  "train.typeMatchPrep": "Match prep",
  "train.typeTraining": "Training",
  "train.dateTbc": "Date TBC",
  "train.min": "min",
  "train.attended": "Attended",
  "train.missed": "Missed",
  "train.loading": "Loading your training…",
  "train.loadFailed": "Failed to load training",
  "train.backToPassport": "Back to Passport",
  "train.title": "Training",
  "train.subtitle": "Your attendance and upcoming sessions",
  "train.notOnTeam": "You're not on a team yet",
  "train.notOnTeamBody": "Ask your coach for a team code to join their squad and see training sessions.",
  "train.attendance": "Attendance",
  "train.attendanceScore": "Attendance score",
  "train.sessions": "sessions",
  "train.inARow": "in a row",
  "train.upcomingSessions": "Upcoming sessions",
  "train.noUpcoming": "No upcoming sessions scheduled",
  "train.recentSessions": "Recent sessions",
  "train.noPast": "No past sessions yet",
  // ── Passport home (player) ──
  "home.heroTitle": "Your Passport",
  "home.heroSubtitle": "Your real stats, Draffte Card. Share one link with coaches and scouts.",
  "home.tileMyGames": "My Games",
  "home.tileCareer": "Career",
  "home.tileAchievements": "Achievements",
  "home.tileScoutReport": "Scout Report",
  "home.tileTraining": "Training",
  "home.tileRanking": "Ranking",
  "home.highlightVideos": "Highlight Videos",
  "home.highlightVideosSub": "Your best moments from recent games",
  "home.uploadMatchVideo": "Upload match video",
  "home.highlight": "Highlight",
  "home.highlightVideosNote": "Connect your Draffte camera to auto-generate highlights after each game",
  "home.ovrProgression": "General OVR progression",
  "home.ovrProgressionSub": "Your OVR (card rating) over recent games",
  "home.ovrExampleNote": "Example progression — upload games to see real data",
  "home.gameAxis": "Game →",
  "home.game": "Game",
  "home.teamHistory": "Team history",
  "home.teamHistorySub": "Clubs and national teams you've represented",
  "home.teamEmpty": "Add your clubs in profile to see your journey",
  "home.club": "Club",
  "home.current": "Current",
  "home.performance": "Performance",
  "home.performanceSub": "Pace, distance, intensity from your games",
  "home.performanceEmpty": "Connect camera & wearables to see live stats",
  "home.sharePassport": "Share your Passport",
  "home.sharePassportSub": "One link. Coaches and scouts see your card — no app required.",
  "home.copyLink": "Copy link",
  "home.copied": "Copied",
  "home.player": "Player",
  "home.notYetRated": "Not yet rated",
  "home.yearsOld": "years old",
  "home.statsSynced": "Stats synced",
  "home.form": "form",
  "home.synced": "Synced",
  "home.tier.elite": "Elite",
  "home.tier.gold": "Gold",
  "home.tier.silver": "Silver",
  "home.tier.bronze": "Bronze",
  "home.foot.left": "Left",
  "home.foot.both": "Both",
  "home.foot.right": "Right",
  // ── Ranking (player) ──
  "rank.title": "Ranking",
  "rank.subtitle": "Your position by country and globally.",
  "rank.fairnessNote": "This ranking is based on your age and position for fair comparison with players in your category.",
  "rank.rankingLabel": "Ranking",
  "rank.country": "Country",
  "rank.standings": "Standings",
  "rank.standingsSub": "Switch between country and global view",
  "rank.tabCountry": "Country",
  "rank.tabGlobal": "Global",
  "rank.globalRanking": "Global ranking",
  "rank.emptyTitle": "No ranking yet",
  "rank.emptySub": "Upload and process a match to get your first ranking.",
  "rank.gold": "Gold",
  "rank.silver": "Silver",
  "rank.bronze": "Bronze",
  "rank.demoTopPlayer": "Top Player",
  "rank.demoSecond": "Second",
  "rank.demoThird": "Third",
  "rank.demoYou": "You",
  // ── Analytics (player) ──
  "analytics.eyebrow": "Performance intelligence",
  "analytics.title": "Analytics",
  "analytics.heroLead": "See how",
  "analytics.heroRealData": "real game data",
  "analytics.heroMiddle": "becomes your six stats and your",
  "analytics.heroTrailing": "Simple, transparent, scout-ready.",
  "analytics.yourActualStats": "Your actual stats",
  "analytics.currentStatsNote": "These are your current card stats. The formula below shows how they average to your OVR.",
  "analytics.flowGames": "Your games",
  "analytics.flowStats": "6 stats",
  "analytics.howOvrTitle": "How your OVR is calculated",
  "analytics.howOvrSub": "Average of your six card stats — fair and easy to understand",
  "analytics.whatDrivesTitle": "What drives your stats",
  "analytics.whatDrivesLead": "Your stats are calibrated using your",
  "analytics.whatDrivesGender": "gender",
  "analytics.whatDrivesAnd": "and",
  "analytics.whatDrivesAge": "age",
  "analytics.whatDrivesRest": "so comparisons are fair across players. Raw metrics from the pitch — top speed, stamina, distance covered, high-intensity runs and more — are captured by the Draffte camera and wearables, then translated into your six card stats (SPD, FIN, PLY, WRK, DEF, STR) and your overall form.",
  "analytics.liveDataFrom": "Live data from",
  "analytics.processedMatchSingular": "processed match",
  "analytics.processedMatchesPlural": "processed matches",
  "analytics.exampleValuesNote": "Right now these are example values. When you go live, the same metrics (speed, stamina, distance, etc.) will come from your real game data — camera & wearables — and feed into the same six stats and your OVR. Same logic, real numbers.",
  "analytics.statsExplainedTitle": "Stats explained",
  "analytics.statsExplainedSub": "What each stat abbreviation means and how it is calculated from your game data.",
  "analytics.howCalculated": "How it's calculated",
  "analytics.backToHome": "Back to Home",
  "analytics.feeds": "Feeds",
  "analytics.feedsAll": "All",
  "analytics.metric_speed_label": "Top speed",
  "analytics.metric_speed_desc": "Max speed reached in games",
  "analytics.metric_stamina_label": "Stamina",
  "analytics.metric_stamina_desc": "Sustained effort over 90 min",
  "analytics.metric_acceleration_label": "Acceleration",
  "analytics.metric_acceleration_desc": "Explosiveness off the mark",
  "analytics.metric_distance_label": "Distance per game",
  "analytics.metric_distance_desc": "Total distance covered",
  "analytics.metric_intensity_label": "High intensity",
  "analytics.metric_intensity_desc": "Sprints & high-intensity runs",
  "analytics.metric_consistency_label": "Form consistency",
  "analytics.metric_consistency_desc": "Stable performance across games",
  "analytics.stat_SPD_name": "Speed",
  "analytics.stat_SPD_desc": "Your top speed and acceleration on the pitch. Reflects how fast you can run at your peak.",
  "analytics.stat_SPD_calc": "Derived from the maximum speed (km/h) you reach during the game, measured by camera tracking and wearables. Calibrated by age group so younger and older players are compared fairly.",
  "analytics.stat_FIN_name": "Finishing",
  "analytics.stat_FIN_desc": "Quality and effectiveness in front of goal — shooting accuracy and composure in scoring situations.",
  "analytics.stat_FIN_calc": "Built from shot accuracy, conversion in key moments, and goal-threat actions. As we add more in-game events (shots, xG), this stat will reflect your real finishing data.",
  "analytics.stat_PLY_name": "Playmaking",
  "analytics.stat_PLY_desc": "Vision and execution in build-up: passing accuracy, key passes, and ball progression.",
  "analytics.stat_PLY_calc": "Based on pass completion, chance creation, and involvement in dangerous moves. Will be refined with pass type, direction, and pressure from camera + wearables.",
  "analytics.stat_WRK_name": "Work rate",
  "analytics.stat_WRK_desc": "Off-ball effort: pressing, tracking back, and intensity when not in possession.",
  "analytics.stat_WRK_calc": "Driven by high-intensity runs, sprints, and movement when the team is defending. Combines distance at high speed with recovery and repeat efforts.",
  "analytics.stat_DEF_name": "Defending",
  "analytics.stat_DEF_desc": "Defensive impact: duels, tackles, interceptions, and positioning when the opposition has the ball.",
  "analytics.stat_DEF_calc": "From defensive actions, duels won, and positioning during opposition possession. Will be enriched with event detection (tackles, clearances) as our tracking improves.",
  "analytics.stat_STR_name": "Strength",
  "analytics.stat_STR_desc": "How often you come out of a one-on-one with the ball — taking it off an opponent, or keeping it when challenged.",
  "analytics.stat_STR_calc": "Duels won as a share of all your duels: tackles and dribbles tagged on the match video, plus the times the ball changed team right off a player's feet. Ranked against players in your position; it appears once you have been in at least five duels.",
  // ── My Games (player) ──
  "games.title": "My Games",
  "games.subtitle": "Every game you've played",
  "games.subtitleCount": "{n} games played",
  "games.rating": "Rating",
  "games.home": "Home",
  "games.away": "Away",
  "games.motm": "MOTM",
  "games.vs": "vs",
  "games.attacking": "Attacking",
  "games.minutes": "Minutes",
  "games.shots": "Shots",
  "games.onTarget": "On target",
  "games.keyPasses": "Key passes",
  "games.dribbles": "Dribbles",
  "games.goalsLabel": "Goals",
  "games.passing": "Passing",
  "games.passesCompleted": "Passes",
  "games.passesAttempted": "Attempted",
  "games.passAccuracy": "Accuracy",
  "games.defending": "Defending",
  "games.tackles": "Tackles",
  "games.interceptions": "Interceptions",
  "games.clearances": "Clearances",
  "games.goalkeeping": "Goalkeeping",
  "games.saves": "Saves",
  "games.catches": "Catches",
  "games.conceded": "Conceded",
  "games.noDetail": "Detailed stats come from match video — this game was logged manually by your coach.",
  "games.distanceHeatmapNote": "Distance & heatmap come from match-video analysis — coming soon for individual games.",
  "games.emptyTitle": "No games yet",
  "games.emptyBody": "Your games appear here once your coach rates a match you played in.",
  "games.pageOf": "Page {p} of {total}",
  "games.backToPassport": "Back to Passport",
  // ── Player nav (web shell menu) ──
  "pnav.home": "Home",
  "pnav.myGames": "My Games",
  "pnav.career": "Career",
  "pnav.achievements": "Achievements",
  "pnav.scout": "Scout Report",
  "pnav.training": "Training",
  "pnav.ranking": "Ranking",
  "pnav.analytics": "Analytics",
  "pnav.settings": "Settings",
  "pnav.menu": "Menu",
  "pnav.close": "Close",

  // Generic
  "common.loading": "Loading…",
  // ── i18n batch: squad/matches/stats/passnet/shotmap ──
  "col.assists.tag": "A",
  "col.blocks.tag": "Blk",
  "col.catches.tag": "Cat",
  "col.clearances.tag": "Clr",
  "col.crossesAttempted.tag": "CA",
  "col.crossesCompleted.tag": "CC",
  "col.dribbles.tag": "Drb",
  "col.fouls.tag": "F",
  "col.gkIntAttempted.tag": "Ia",
  "col.gkIntSuccessful.tag": "Is",
  "col.goalKicksAttempted.tag": "GKa",
  "col.goalKicksSuccessful.tag": "GKs",
  "col.goals.tag": "G",
  "col.goalsConceded.tag": "GC",
  "col.interceptions.tag": "Int",
  "col.keyPasses.tag": "KP",
  "col.minutes.tag": "Min",
  "col.runDistance.tag": "Dist",
  "col.runDistance.name": "Distance covered",
  "col.runDistance.desc": "Estimated kilometres run, scaled from the minutes we could follow the player to the minutes they played",
  "col.runPerMin.tag": "m/min",
  "col.runPerMin.name": "Metres per minute",
  "col.runPerMin.desc": "Average work rate while on the pitch",
  "col.runTop.tag": "Top",
  "col.runTop.name": "Top speed",
  "col.runTop.desc": "Fastest sprint measured, only where the shirt number was read",
  "col.parries.tag": "Par",
  "col.passAccuracy.tag": "Acc%",
  "col.passesAttempted.tag": "PA",
  "col.passesCompleted.tag": "PC",
  "col.player.tag": "Player",
  "col.position.tag": "Pos",
  "col.rating.tag": "Rtg",
  "col.redCards.tag": "RC",
  "col.shirtNumber.tag": "No.",
  "col.shots.tag": "Sh",
  "col.shotsOnTarget.tag": "SOT",
  "col.tackles.tag": "Tkl",
  "col.recoveries.tag": "Rec",
  "col.recoveries.name": "Ball recoveries",
  "col.recoveries.desc": "Times this player won the ball back from the opponent (tagged ground truth — includes tackles and interceptions).",
  "col.turnovers.tag": "Lost",
  "col.turnovers.name": "Possession lost",
  "col.turnovers.desc": "Times this player lost the ball to the opponent (tagged ground truth).",
  "col.yellowCards.tag": "YC",
  "heat.zone.centralAttThird": "Central Att. Third",
  "heat.zone.centralDefensive": "Central Defensive",
  "heat.zone.centralMidfield": "Central Midfield",
  "heat.zone.leftChannelMid": "Left Channel (Mid)",
  "heat.zone.leftDefensive": "Left Defensive",
  "heat.zone.leftWingAtt": "Left Wing (Att.)",
  "heat.zone.rightDefensive": "Right Defensive",
  "heat.zone.rightMidfield": "Right Midfield",
  "heat.zone.rightWingAtt": "Right Wing (Att.)",
  "matches.inDays": "In {days} days",
  "matches.pens": "PENS",
  "matches.scorerNotSet": "Scorer not set",
  "matches.resultDraw": "DRAW",
  "matches.resultLoss": "LOSS",
  "matches.resultWin": "WIN",
  "matches.statCorners": "Corners",
  "matches.statPassAcc": "Pass acc.",
  "matches.statPossession": "Possession",
  "matches.statShotsOn": "Shots on",
  "matches.statShotsTotal": "Shots total",
  "matches.teamStats": "Team Stats",
  "matches.today": "Today",
  "matches.tomorrow": "Tomorrow",
  "matches.vs": "vs",
  // ── Formation builder (upcoming-match lineup tab) ──
  "matches.buildFormation": "Build your formation",
  "formation.autoPick": "Auto-pick",
  "formation.bench": "Bench",
  "formation.benchEmpty": "Everyone is on the pitch.",
  "formation.captain": "Captain",
  "formation.demoNote": "Demo mode — lineup changes are not saved.",
  "formation.emptySlotHint": "Tap a position to pick a player",
  "formation.loadError": "Couldn't load your squad.",
  "formation.loading": "Loading squad…",
  "formation.makeCaptain": "Make captain",
  "formation.noPlayers": "No players in your squad yet. Add players on the Squad page first.",
  "formation.pickerTitle": "Pick a player",
  "formation.matchFormat": "Match format",
  "formation.formatHelp": "Choose the format, then tap each position to select your starting team.",
  "formation.backendUnavailable": "Youth match setup is not available on this server yet. Your existing lineup is safe.",
  "formation.benchHelp": "Tap to choose substitutes. Players left unselected are not called up.",
  "formation.reviewCallUp": "Review & notify players",
  "formation.reviewChanged": "The team changed. Close this review and check the latest lineup before notifying players.",
  "formation.publishHelp": "Save keeps a draft. Notifications are sent only after you confirm the complete team.",
  "formation.publishUnavailable": "Player notifications are not available on this server yet.",
  "formation.confirmHelp": "Confirm your {format} team: {selected} players selected and {others} not selected. Linked players receive their own selection update in the app. Players without a linked player account cannot receive it.",
  "formation.confirmPublish": "Confirm & notify",
  "formation.cancel": "Keep editing",
  "formation.published": "Call-up published",
  "formation.alreadyPublished": "This call-up is already published. No duplicate notifications were sent.",
  "formation.delivery": "{selected} selected and {others} not-selected updates delivered in the app. {unlinked} squad members have no linked player account.",
  "formation.demoPublished": "Demo call-up reviewed. No notifications were sent.",
  "formation.publishError": "Could not publish the call-up. Your lineup is saved as a draft; try again.",
  "formation.wrongTeam": "Switch to this match’s team before editing its lineup.",
  "formation.frozen": "This match has started. Its original team sheet and format are locked.",
  "formation.placed": "{n} of {total} positions filled",
  "formation.setupLoadError": "Could not load match setup. Please try again.",
  "formation.setupSaveError": "Could not save jersey numbers. Your changes are still here.",
  "formation.jerseyHelp": "Jersey numbers belong to the selected starters and substitutes. Save your lineup first, then enter a unique number for each player.",
  "formation.jerseys": "Match jersey numbers",
  "formation.numbersMissing": "Every selected player needs a unique jersey number from 1 to 99.",
  "formation.continueUpload": "Continue to game upload",
  "formation.remove": "Remove",
  "formation.removeCaptain": "Remove captain",
  "formation.retry": "Retry",
  "formation.save": "Save lineup",
  "formation.saveError": "Couldn't save the lineup — your changes are still here. Try again.",
  "formation.saved": "Lineup saved",
  "formation.saving": "Saving…",
  "pn.networkRead.body1": "ran the build-up ({out} passes played), with {topTarget} the most-found target ({inn} received).",
  "pn.networkRead.body2": "{acc}% of passes found a teammate, and {fwd}% went forward{longest}.",
  "pn.networkRead.identityPossession": "A patient, possession-based identity — recycling to keep control.",
  "pn.networkRead.identityVertical": "A progressive, vertical passing identity.",
  "pn.networkRead.longest": " — the longest a {lenM}m ball from {from} to {to}",
  "pn.note.intoDeep": "deeper to feet to start moves",
  "pn.note.intoHigh": "high up the pitch in attacking areas",
  "pn.note.lengthLong": "favouring longer, line-breaking balls",
  "pn.note.lengthShort": "keeping it mostly short and simple",
  "pn.note.made": "{player} played {n} passes at {acc}% completion from {zone} role — {style}, {length}. {fwd}% went forward and {back}% backward, averaging {avgLen}m.{topSentence} {rel}",
  "pn.note.received": "{player} received {n} passes ({acc}% completed to him) in {zone} role — picked out {into}. {fwd}% of the balls into him travelled forward, averaging {avgLen}m.{topSentence} {rel}",
  "pn.note.relMadeHigh": "Very secure in possession.",
  "pn.note.relMadeLow": "Turnover-prone — needs safer options under pressure.",
  "pn.note.relMadeMid": "Fairly reliable, with room to tighten his selection.",
  "pn.note.relRecvHigh": "Almost always finds him cleanly.",
  "pn.note.relRecvLow": "Frequently overhit or cut out before reaching him.",
  "pn.note.relRecvMid": "Mostly reaches him, with some balls lost in transit.",
  "pn.note.strongestConn": " Strongest connection: {topR} ({count} passes).",
  "pn.note.styleBalanced": "a balanced link in the build-up",
  "pn.note.styleProgressive": "a progressive, forward-minded distributor",
  "pn.note.styleRecycler": "a recycler who keeps possession ticking over",
  "pn.note.suppliedBy": " Most-fed by {topS} ({count} passes).",
  "pn.note.zoneAdvanced": "an advanced",
  "pn.note.zoneDeep": "a deep",
  "pn.note.zoneMidfield": "a midfield",
  "sm.estimatedTooltip": "Outcome is real (tagged); pitch position is estimated",
  "sm.shotRead.aheadOfNumbers": "— a result that ran ahead of the underlying numbers.",
  "sm.shotRead.bigChances": "{n} big chances (≥0.3 xG).",
  "sm.shotRead.body1": "{goals} goals from {shots} shots ({xg} combined xG).",
  "sm.shotRead.body2": "{team} created the better chances ({xg} xG)",
  "sm.shotRead.inLine": "— broadly in line with the chances created.",
  "sm.xgPerShot": "xG/shot",
  "squad.addFirstPlayer": "Add First Player",
  "squad.attackDirection": "Attack ↑",
  "squad.availAvailable": "Available",
  "squad.availDoubtful": "Doubtful",
  "squad.availInjured": "Injured",
  "squad.benchDragHelp": "Drag a bench player onto a pitch position to substitute",
  "squad.benchDragging": "Dragging — drop on pitch to substitute",
  "squad.colDistance": "Distance (est.)",
  "squad.colForm": "Form",
  "squad.colOvr": "OVR",
  "squad.colPlayer": "Player",
  "squad.colSpeed": "Speed (est.)",
  "squad.colStatus": "Status",
  "squad.connectedCount": "connected",
  "squad.copied": "Copied!",
  "squad.copyInviteLink": "Copy invite link",
  "squad.teamCode": "Team code",
  "squad.teamCodeHint": "Players enter this code in the Draffte Passport app to join your squad.",
  "squad.copyCode": "Copy code",
  "squad.copyMessage": "Copy invite message",
  "squad.newCode": "New code",
  "squad.regenWarn": "Generate a new code? The current one will stop working.",
  "squad.inviteMessage": "Join my team on Draffte! Download the Draffte Passport app and enter team code: {code}",
  "squad.noCodeYet": "Create your team to get an invite code.",
  "squad.dragHintSwap": "Drop on a pitch position to swap",
  "squad.filterAll": "All",
  "squad.footBoth": "Both",
  "squad.footLeft": "Left",
  "squad.footRight": "Right",
  "squad.formStable": "Stable",
  "squad.formTrend": "Form trend",
  "squad.genderFemale": "Female",
  "squad.genderMale": "Male",
  "squad.inviteFootnote": "Players who use your code or link join your team automatically — no club to pick or enter.",
  "squad.passportConnected": "Draffte Passport Connected",
  "squad.passportConnectedDesc": "Stats auto-sync after each session & match",
  "squad.passportConnectorsBody": "have a Draffte Passport. Share your link — it auto-assigns them to your team.",
  "squad.passportNone": "No Passport Account",
  "squad.passportNoneDesc": "Invite this player to create a Passport",
  "squad.pendingCount": "pending",
  "squad.physAge": "Age",
  "squad.physFoot": "Foot",
  "squad.physGender": "Gender",
  "squad.physHeight": "Height",
  "squad.physNation": "Nation",
  "squad.physWeight": "Weight",
  "squad.playersWord": "players",
  "squad.posCAM": "Attacking Mid",
  "squad.posCB": "Centre Back",
  "squad.posCDM": "Defensive Mid",
  "squad.posCM": "Central Mid",
  "squad.posGK": "Goalkeeper",
  "squad.posLB": "Left Back",
  "squad.posLW": "Left Wing",
  "squad.posRB": "Right Back",
  "squad.posRW": "Right Wing",
  "squad.posST": "Striker",
  "squad.roleDefender": "Defender",
  "squad.roleForward": "Forward",
  "squad.roleMidfielder": "Midfielder",
  "squad.rolePortero": "Goalkeeper",
  "squad.sendPassportInvite": "Send Passport Invite",
  // Removing a player is reversible — the backend soft-deletes, so his rated
  // games and passport history stay intact. The copy says so, because "remove"
  // reads as permanent and a coach hesitating over a real squad is a support ticket.
  "squad.cancel": "Cancel",
  "squad.removePlayer": "Remove from squad",
  "squad.removeConfirmTitle": "Remove {name} from the squad?",
  "squad.removeConfirmBody": "He comes off the live squad. His rated games and everything on his Passport stay — add him back later and his history returns.",
  "squad.removeConfirmAction": "Yes, remove",
  "squad.removing": "Removing…",
  "squad.statRun": "RUN",
  "squad.statSpd": "SPD",
  "squad.statWrk": "WRK",
  "squad.step1Desc": "Send players your team code or invite link",
  "squad.step1Title": "Share code",
  "squad.step2Desc": "Players enter the code in the Draffte Passport app to join your squad",
  "squad.step2Title": "They join",
  "squad.step3Desc": "Stats appear here after every game & session",
  "squad.step3Title": "Auto-sync",
  "squad.unitYrs": "yrs",
  "squad.weekDistance": "Distance",
  "squad.weekSessions": "Sessions",
  "squad.weekFromVideo": "Speed, distance & load come from match video",
  "squad.weekFromVideoSub": "Unlocks once a recorded match is analysed by the camera AI.",
  "squad.weekTopSpeed": "Top speed",
  "squad.runningLast": "last: vs {opp}",
  "card.provisional": "prov.",
  "card.provisionalHint": "Provisional: top speed measured on match video, on a fixed scale. It becomes a ranking against players in the same position once six of them are measured.",
  "stats.insight.clinicalFinishing.detail": "21% shot conversion rate — well above league average of 12%.",
  "stats.insight.clinicalFinishing.title": "Clinical Finishing",
  "stats.insight.defensiveTransitions.detail": "Conceded from a quick free kick — awareness was lacking at 45'.",
  "stats.insight.defensiveTransitions.title": "Defensive Transitions",
  "stats.insight.leftFlankDominance.detail": "68% of successful attacks originated from the left side through Amy L. and Luca F.",
  "stats.insight.leftFlankDominance.title": "Left Flank Dominance",
  "stats.insight.leonDiscipline.detail": "Yellow card for a reckless challenge — 3 fouls in 67 minutes before substitution.",
  "stats.insight.leonDiscipline.title": "Leon D. Discipline",
  "stats.insight.midfieldControl.detail": "Tom K. completed 87% of passes and created 3 key chances.",
  "stats.insight.midfieldControl.title": "Midfield Control",
  "stats.insight.rightSideVulnerable.detail": "Only 32% of defensive actions on the right — Kai W. was caught high.",
  "stats.insight.rightSideVulnerable.title": "Right Side Vulnerable",
  "stats.insight.setPieceThreat.detail": "2 goals came directly from set piece situations. Sarah T. delivery was exceptional.",
  "stats.insight.setPieceThreat.title": "Set Piece Threat",
  "stats.recommendation.exploitLeft": "Exploit the left channel more — Amy L. + Luca F. combination was lethal",
  "stats.recommendation.quickTransitions": "Practice quick transitions from free kicks to avoid conceding",
  "stats.recommendation.rightBackPositioning": "Address right-back positioning when team is in possession",
  "stats.recommendation.setPieceVariations": "Set piece routines working well — add near-post variations",
  "stats.recommendation.startNina": "Consider starting Nina A. alongside Tom K. for better ball retention",
  "stats.tacticalInsight.leftVsRightChances": "Your team created 3× more chances from the left than the right",
  "stats.tacticalInsight.pressBroken": "Opponent's press was broken within 3 passes 78% of the time",
  "stats.tacticalInsight.recoveryTime": "Ball recovery time averaged 4.2 seconds — 1.8s faster than opponent",
  "stats.tacticalInsight.shotsInsideBox": "60% of shots came from inside the penalty area",
  "stats.tacticalInsight.vulnerableWindow": "Opponent was most vulnerable between 55-70 minutes",
  // ── sessions demo content ──
  "sessions.focus.matchPrepRival": "Final walkthrough vs Rival FC — set pieces & defensive triggers",
  "sessions.focus.preMatchShape": "Pre-match shape rehearsal — 4-2-3-1 high block",
  "sessions.focus.setPiecesShape": "Set pieces & defensive shape consolidation",
  "sessions.focus.sprintIntervals": "High-intensity sprint intervals — 8×40m + agility gates",
  "sessions.focus.passingRondos": "Passing accuracy under pressure — rondos & positional play",
  "sessions.focus.highPressTriggers": "High press 4-2-3-1 triggers — compactness & counter-press",
  "sessions.focus.postMatchRecovery": "Post-match recovery — stretching, hydrotherapy & light passing",
  "sessions.focus.oppositionAnalysis": "Opposition analysis — shape recognition & transition triggers",
  "sessions.focus.sprintEndurance": "Sprint endurance & agility — shuttle runs + 1v1 pressure drills",
  "sessions.focus.finishingCrossing": "Finishing & crossing drills — box play and wide channel combinations",
  "sessions.notes.leonHighPress": "Leon struggled to maintain high press in the second half. Consider modified role.",
  "sessions.notes.strongWorkRate": "Strong work rate across the squad. Sarah led the running drills.",
  "sessions.notes.bestPassing": "Full squad. Best passing session this month. Nina showed great composure.",
  "sessions.notes.activeRecovery": "Active recovery only. Carlos and Alex attended physio separately.",
  "sessions.notes.wellPrepared": "Strong tactical work. Team well-prepared for Sunday.",
  "sessions.notes.combination": "Marcus and Amy excellent in combination. Nina showed improved link-up play.",

  // ── Coach signup ──
  "coachSignup.eyebrow": "Coach Account",
  "coachSignup.roleTitle": "What's your role?",
  "coachSignup.roleSub": "Select your primary coaching position",
  "coachSignup.nameTitle": "Your name",
  "coachSignup.nameSub": "How your squad will know you",
  "coachSignup.dobSub": "Used for age-category filters in the Draffte ranking system",
  "coachSignup.natSub": "Your country of origin",
  "coachSignup.licenseTitle": "Coaching licence",
  "coachSignup.licenseSub": "Your highest coaching qualification",
  "coachSignup.experienceTitle": "Experience",
  "coachSignup.experienceSub": "Years of coaching + age groups you've worked with",
  "coachSignup.yearsExp": "Years of experience",
  "coachSignup.yearsUnit": "yrs",
  "coachSignup.ageGroupsLabel": "Age groups coached",
  "coachSignup.clubTitle": "Current club",
  "coachSignup.clubSub": "Are you currently coaching a team?",
  "coachSignup.clubYes": "Yes, I am",
  "coachSignup.clubNo": "Not right now",
  "coachSignup.clubNameLabel": "Club / Team name",
  "coachSignup.clubNamePh": "e.g. FC Barcelona Academy",
  "coachSignup.clubCountry": "Country",
  "coachSignup.searchShort": "Search…",
  "coachSignup.squadLevelLabel": "Squad level",
  "coachSignup.historyTitle": "Club history",
  "coachSignup.historySub": "Teams you have coached — most recent first",
  "coachSignup.clubN": "Club",
  "coachSignup.rolePh": "Your role (e.g. Head Coach)",
  "coachSignup.currentlyHere": "Currently coaching here",
  "coachSignup.photoSub": "Optional — your photo on the coach dashboard",
  "coachSignup.tapToChange": "Tap to change",
  "coachSignup.uploadPhoto": "Upload a photo",
  "coachSignup.accountTitle": "Create account",
  "coachSignup.accountSub": "Your login credentials for Draffte Coach",
  "coachSignup.emailLabel": "Email address",
  "coachSignup.emailPh": "coach@club.com",
  "coachSignup.passwordLabel": "Password",
  "coachSignup.passwordPh": "Min. 8 characters",
  "coachSignup.confirmPh": "Repeat password",
  "coachSignup.byCreating": "By creating an account you agree to Draffte's",
  "coachSignup.termsOfService": "Terms of Service",
  "coachSignup.and": "and",
  "coachSignup.privacyPolicy": "Privacy Policy",
  "coachSignup.hidePassword": "Hide password",
  "coachSignup.showPassword": "Show password",
  "coachSignup.change": "Change",
  "coachSignup.createCoachAccount": "Create Coach Account",
  "coachSignup.continue": "Continue",
  "coachSignup.alreadyHave": "Already have an account?",
  "coachSignup.signUpAsPlayer": "Sign up as Player",
  "coachSignup.welcome": "Welcome, Coach {name}",
  "coachSignup.successSub": "Your coach dashboard is ready. Start building your squad.",
  "coachSignup.goToDashboard": "Go to my Dashboard",
  "coachSignup.stepWord": "Step",
  // Visual panel
  "coachSignup.vDraffteCoach": "Draffte Coach",
  "coachSignup.vCoachFallback": "Coach",
  "coachSignup.vYearsOld": "{n} years old",
  "coachSignup.vYearsCoaching": "Years coaching",
  "coachSignup.vBeginner": "Beginner",
  "coachSignup.vExperienced": "Experienced",
  "coachSignup.vVeteran": "Veteran",
  "coachSignup.vAgeGroupsSelected": "Age groups selected",
  "coachSignup.vClubPlaceholder": "Your current club will appear here",
  "coachSignup.vHistoryPlaceholder": "Your history will appear here",
  "coachSignup.vSigningUpAs": "Signing up as",
  // Role option descriptions
  "coachSignup.role.head_coach": "Head Coach",
  "coachSignup.role.assistant_coach": "Assistant Coach",
  "coachSignup.role.gk_coach": "Goalkeeper Coach",
  "coachSignup.role.fitness_coach": "Fitness / S&C Coach",
  "coachSignup.role.technical_director": "Technical Director",
  "coachSignup.role.scout": "Scout",
  "coachSignup.role.academy_coach": "Academy Coach",
  "coachSignup.role.team_manager": "Team Manager",
  "coachSignup.roleDesc.head_coach": "Lead the team strategy & selection",
  "coachSignup.roleDesc.assistant_coach": "Support the head coach in all areas",
  "coachSignup.roleDesc.gk_coach": "Specialist goalkeeper development",
  "coachSignup.roleDesc.fitness_coach": "Physical conditioning & injury prevention",
  "coachSignup.roleDesc.technical_director": "Oversee club's footballing philosophy",
  "coachSignup.roleDesc.scout": "Player identification & recruitment",
  "coachSignup.roleDesc.academy_coach": "Youth player development",
  "coachSignup.roleDesc.team_manager": "Team logistics, schedules & paperwork",
  // Licence group headers
  "coachSignup.licenseGroup.entry": "No / Entry Level",
  "coachSignup.licenseGroup.uefa": "UEFA",
  "coachSignup.licenseGroup.ussf": "USSF (USA)",
  "coachSignup.licenseGroup.concacaf": "CONCACAF",
  "coachSignup.licenseGroup.afc": "AFC",
  "coachSignup.licenseGroup.caf": "CAF (Africa)",
  "coachSignup.licenseGroup.other": "Other",
  // Licence labels
  "coachSignup.license.none": "No Licence",
  "coachSignup.license.grassroots": "Grassroots / Entry Level",
  "coachSignup.license.uefa_c": "UEFA C Licence",
  "coachSignup.license.uefa_b": "UEFA B Licence",
  "coachSignup.license.uefa_a": "UEFA A Licence",
  "coachSignup.license.uefa_pro": "UEFA Pro Licence",
  "coachSignup.license.ussf_d": "USSF D Licence",
  "coachSignup.license.ussf_c": "USSF C Licence",
  "coachSignup.license.ussf_b": "USSF B Licence",
  "coachSignup.license.ussf_a": "USSF A Licence",
  "coachSignup.license.ussf_pro": "USSF Pro Licence",
  "coachSignup.license.concacaf_b": "CONCACAF B Licence",
  "coachSignup.license.concacaf_a": "CONCACAF A Licence",
  "coachSignup.license.afc_a": "AFC A Licence",
  "coachSignup.license.caf_a": "CAF A Licence",
  "coachSignup.license.nscaa_national": "NSCAA National Diploma",
  "coachSignup.license.other": "Other",
  // Age group labels
  "coachSignup.age.u6_u9": "U6 – U9",
  "coachSignup.age.u10_u12": "U10 – U12",
  "coachSignup.age.u13_u16": "U13 – U16",
  "coachSignup.age.u17_u21": "U17 – U21",
  "coachSignup.age.senior_amateur": "Senior Amateur",
  "coachSignup.age.senior_semi_pro": "Senior Semi-Pro",
  "coachSignup.age.senior_professional": "Senior Professional",
  "coachSignup.age.womens": "Women's / Girls'",
  "coachSignup.age.futsal": "Futsal",
  // Squad level labels
  "coachSignup.squad.amateur": "Amateur",
  "coachSignup.squad.semi_pro": "Semi-Professional",
  "coachSignup.squad.professional": "Professional",
  "coachSignup.squad.academy": "Academy",
  "coachSignup.squad.national_team": "National Team",
  // ── Coach card + profile modal ──
  "coachCard.tapToEdit": "Tap to edit",
  "coachCard.title": "Your coaching profile",
  "coachCard.sub": "Shown on your card and to players who join your team.",
  "coachCard.firstName": "First name",
  "coachCard.lastName": "Last name",
  "coachCard.role": "Coaching role",
  "coachCard.license": "Licence",
  "coachCard.experience": "Years of experience",
  "coachCard.club": "Club",
  "coachCard.nationality": "Nationality",
  "coachCard.bio": "About you",
  "coachCard.bioPlaceholder": "Playing style, philosophy, what you want the team to be known for…",
  "coachCard.photo": "Change photo",
  "coachCard.save": "Save changes",
  "coachCard.saving": "Saving…",
  "coachCard.cancel": "Cancel",
  "coachCard.saveFailed": "Could not save. Check your connection and try again.",
  // ── Squad leaders (Overview) ──
  "dash.leaderPassTitle": "Best passer",
  "dash.leaderPassSub": "Pass accuracy",
  // ── Overview: team video sections ──
  // ── Position codes. English keeps the code itself; see lib/positions.ts. ──
  "pos.GK": "GK",   "pos.CB": "CB",   "pos.LB": "LB",   "pos.RB": "RB",
  "pos.LWB": "LWB", "pos.RWB": "RWB", "pos.CDM": "CDM", "pos.CM": "CM",
  "pos.CAM": "CAM", "pos.LM": "LM",   "pos.RM": "RM",   "pos.LW": "LW",
  "pos.RW": "RW",   "pos.ST": "ST",   "pos.CF": "CF",
  // Spelled out, for the tooltip on every abbreviation.
  "posName.GK": "Goalkeeper",        "posName.CB": "Centre Back",
  "posName.LB": "Left Back",         "posName.RB": "Right Back",
  "posName.LWB": "Left Wing Back",   "posName.RWB": "Right Wing Back",
  "posName.CDM": "Defensive Mid",    "posName.CM": "Central Mid",
  "posName.CAM": "Attacking Mid",    "posName.LM": "Left Mid",
  "posName.RM": "Right Mid",         "posName.LW": "Left Wing",
  "posName.RW": "Right Wing",        "posName.ST": "Striker",
  "posName.CF": "Second Striker",
  "squad.colDuelOff": "Off. duels",
  "squad.colDuelDef": "Def. duels",
  "squad.duelTipOne": "1 contested — won\u2013lost",
  "squad.duelTip": "{a} contested — won\u2013lost",
  "squad.duelTipUnresolved": "{a} contested · {n} the video never settled, counted as neither",
  "squad.duelTipUnresolvedOne": "1 contested · the video never settled it, counted as neither",
  "squad.duelTipEstimated": "Includes {n} estimated from who had the ball (taggers rarely tap duels). Only one side of those shows on video, so there is no win rate",
  "squad.duelWon": "won",
  "squad.duelContested": "contested",
  // ── Analyst clips (Ask the analyst → video) ──
  "clip.kind.goal": "Goals",          "clip.kind.shot": "Shots",
  "clip.kind.save": "Saves",          "clip.kind.assist": "Assists",
  "clip.kind.key_pass": "Key passes", "clip.kind.recovery": "Recoveries",
  "clip.kind.turnover": "Turnovers",  "clip.kind.foul": "Fouls",
  "clip.kind.card": "Cards",          "clip.kind.chance_against": "Chances against",
  "clip.kind.pass": "Passes",         "clip.kind.duel": "Duels",
  "clip.third.defensive": "defensive third", "clip.third.middle": "middle third",
  "clip.third.attacking": "attacking third",
  "clip.dir.forward": "forward", "clip.dir.sideways": "sideways", "clip.dir.backward": "backward",
  "clip.out.completed": "completed", "clip.out.lost": "lost",
  "clip.out.won": "won", "clip.out.unknown": "unresolved",
  "clip.showingOf": "showing {n} of {total}",
  "clip.minuteRange": "minutes {a}\u2013{b}",
  "clip.ignored": "could not filter by {f} for this kind",
  "clip.none": "Nothing of that kind in this match",
  "clip.noVideo": "This match has no playable video",
  "clip.play": "Play",
  "clip.unnamed": "not identified",
  // ── Club (My teams → the layer above a team) ──
  "club.noneTitle": "Run a whole club?",
  "club.noneBody": "A club groups your age categories under one roof. As director you see every category — its coach, its staff and its squad — and can add new ones. Each category's coach keeps control of their own squad.",
  "club.create": "Create a club",
  "club.createAction": "Create",
  "club.namePlaceholder": "Club name, e.g. Envigado Fútbol Club",
  "club.join": "Join with a code",
  "club.joinAction": "Join",
  "club.codePlaceholder": "CLUB CODE",
  "club.joinHint": "Your director shares this code. Joining adds YOUR team to their club — they can see it, they cannot edit it.",
  "club.youDirect": "You direct this club",
  "club.directedBy": "Directed by {n}",
  "club.categories": "Categories",
  "club.players": "Players",
  "club.staff": "Staff",
  "club.playersLower": "players",
  "club.yours": "Yours",
  "club.youManage": "You manage",
  "club.readOnly": "View only",
  "club.empty": "No categories yet. Add one, or share the club code with your coaches.",
  "club.addCategory": "Add a category",
  "club.addAction": "Add",
  "club.categoryPlaceholder": "Category, e.g. Sub 17",
  "club.coachEmailPlaceholder": "Coach's email (optional)",
  "club.addHint": "With an email, that coach OWNS the new category and edits its squad; you keep oversight. Without one, it is yours until you hand it over.",
  "club.codeHint": "Share this code with your category coaches. Each enters it once and their team joins the club — you can see it, they keep editing it.",
  "club.rotate": "New code",
  "club.failed": "That did not work. Try again.",
  // ── Team invites: hand ONE category to ONE coach (migration 036) ──────────
  "teamActions.enter": "Enter the team",
  "teamActions.enterSub": "Open this category's dashboard — squad, matches, sessions.",
  "teamActions.enterReadOnlySub": "Open it read-only. You can see this category, not edit it.",
  "teamActions.add": "Add",
  "teamActions.addSub": "Invite a coach or a team manager. They get write access to this category.",
  "teamActions.whoTitle": "Who are you adding?",
  "teamActions.asCoach": "Coach",
  "teamActions.asCoachSub": "Runs the squad: lineups, matches and sessions.",
  "teamActions.asManager": "Team Manager",
  "teamActions.asManagerSub": "Organises the team: logistics, schedules and paperwork.",
  "teamActions.sameAccess": "Both get the same access to {team}, through the same invite link.",
  "teamActions.addingCoach": "Add a coach",
  "teamActions.addingManager": "Add a team manager",
  "teamActions.emailLabelManager": "Team manager's email",
  "teamActions.emailPlaceholderManager": "manager@club.com",
  "teamActions.emailLabel": "Coach's email",
  "teamActions.emailPlaceholder": "coach@club.com",
  "teamActions.emailHint": "Optional. With an email, only that account can use the link — a forwarded link won't work.",
  "teamActions.grantNote": "Whoever opens this link becomes a director of {team}: they can edit this squad. They never become its owner, it works once, and you can cancel it.",
  "teamActions.create": "Create invite link",
  "teamActions.createYes": "Yes — create it",
  "teamActions.cancel": "Cancel",
  "teamActions.linkTitle": "Invite link",
  "teamActions.noMailer": "We don't send email yet, so nothing was sent. Copy this link and send it yourself — WhatsApp, message, however you like.",
  "teamActions.mailSent": "Sent to {e}. Copy the link too, in case it doesn't arrive.",
  "teamActions.liveTitle": "Other live invites",
  "teamActions.revoke": "Cancel this invite",
  "teamActions.revokeConfirm": "Cancel this invite? The link stops working immediately.",
  "teamActions.revokeYes": "Cancel it",
  "teamActions.anyoneWithLink": "Anyone with the link",
  "teamActions.expires": "Expires {d}",
  "teamActions.failed": "That did not work. Try again.",
  "teamActions.back": "Back",
  "invitePage.loading": "Checking the invite…",
  "invitePage.invalid": "This invite link is not valid.",
  "invitePage.invalidBody": "Check you copied the whole link, or ask for a new one.",
  "invitePage.expired": "This invite has expired. Ask for a new link.",
  "invitePage.revoked": "This invite was cancelled. Ask for a new link.",
  "invitePage.accepted": "This invite has already been used.",
  "invitePage.eyebrow": "You've been invited to",
  "invitePage.invitedBy": "Invited by {n}",
  "invitePage.forEmail": "This link is for {e}",
  "invitePage.grantNote": "You'll be able to run this squad — roster, matches, sessions. You do not become its owner, and this link works once.",
  "invitePage.join": "Join this team",
  "invitePage.joining": "Joining…",
  "invitePage.joined": "You're in",
  "invitePage.signUp": "Create your coach account",
  "invitePage.signIn": "I already have an account",
  "invitePage.signedOutNote": "You won't be asked to create a team — you're joining one that already exists.",
  "invitePage.expiresOn": "This link expires {d}",
  "invitePage.failed": "Could not join that team. Try again.",
  "coachSignup.invitedTo": "You're joining {team}",
  "coachSignup.invitedNote": "We won't ask you to create a team — this account joins one that already exists.",
  "coachSignup.invitedNoteClub": "At {club}. We won't ask you to create a team — this account joins one that already exists.",
  "coachSignup.invitedSuccessSub": "You're in. {team} is ready on your dashboard.",
  "coachSignup.inviteFailed": "Your account is ready, but joining the team didn't go through. Open the invite link again once you're signed in.",
  "vid.title": "Team videos",
  "vid.filmed": "From {n} filmed games",
  "vid.loading": "Loading clips…",
  "vid.goalsTitle": "Goals",
  "vid.goalsSub": "Every goal your team scored in a filmed game. Each clip starts where the move began, not at the shot.",
  "vid.savesTitle": "Saves",
  "vid.savesSub": "Every opponent shot on target your keeper kept out. Not credited to a named keeper — we can see the save, not reliably who made it.",
  "vid.recovTitle": "Recoveries",
  "vid.recovSub": "Where your team won the ball back. Ranked by what came next, so the ones that turned into an attack are first.",
  "vid.playAll": "Play all",
  "vid.viewFollow": "Ball cam",
  "vid.viewPano": "Wide",
  "vid.noFollow": "This game has no ball cam — showing the wide view",
  "vid.noPano": "This game has no wide view — showing the ball cam",
  "vid.stop": "Stop",
  "vid.pickAClip": "Pick a clip to play it here",
  "vid.nothingHere": "Nothing recorded in this section yet",
  "vid.sectionEmpty": "No clips of this kind in the filmed games yet.",
  "vid.showingOf": "Showing {shown} of {total}",
  "vid.ledToShot": "led to a shot",
  "vid.teamSave": "Save",
  "vid.unnamed": "Not identified",
  "vid.emptyTitle": "No video yet",
  "vid.emptyBody": "Clips come from games that were both filmed and tagged. {played} games played, {tagged} tagged — none with video we can play yet.",
  "vid.backToWall": "All sections",
  "vid.expand": "Enlarge",
  "vid.thisSeason": "this season",
  "vid.paneClips": "{n} to watch",
  "vid.paneNone": "None on film yet",
  "vid.couldNotLoad": "Couldn't load — try again",
  "vid.paneUnplayable": "none playable yet",
  "vid.playAllOf": "Play all — {section}",
  "vid.moments": "Moments",
  "dash.leaderAssistTitle": "Most assists",
  "dash.leaderAssistSub": "Assists",
  "dash.leaderTiedOne": "level with 1 other player",
  "dash.leaderTied": "level with {n} other players",
  "dash.leaderGoalsTitle": "Top scorer",
  "dash.leaderGoalsSub": "Goals",
  "dash.leaderDefTitle": "Best defender",
  "dash.leaderDefSub": "Tackles won + balls won back (interceptions included)",
  "dash.leaderEmpty": "Not measured yet",
  "dash.leaderEmptyBody": "Ranks appear once a game has been tagged.",
  "dash.leaderPasses": "{n} passes",
  "dash.leaderGames": "{n} games",
  "dash.leaderActions": "{n} actions",
  // ── Squad season table ──
  "squad.colCalledUp": "Called up",
  "squad.colStarted": "Started",
  "squad.colBench": "Bench",
  "squad.colUnused": "Unused",
  "squad.colMinutes": "Minutes",
  "squad.colGoals": "Goals",
  "squad.colAssists": "Assists",
  "squad.colPassAcc": "Pass %",
  "squad.colYellow": "Yellow",
  "squad.colRed": "Red",
  "squad.colRating": "Rating",
  "squad.showPct": "Show %",
  "squad.seasonSource": "Selection from {l} of {m} saved team sheets · minutes and passes from {s} tagged games",
  "squad.seasonLoading": "Loading season record…",
  // ── Analytics ──
  "coachAnalytics.kpiFastest": "Fastest 3",
  "coachAnalytics.kpiFastestSub": "km/h, estimated from rating",

  // ── Coach › Camera ────────────────────────────────────────────────────────
  // The coach's own camera: mounting check, connection, and the games on it.
  "locale.bcp47": "en-GB",

  "nav.camera": "Camera",

  "camList.eyebrow": "Your equipment",
  "camList.title": "Camera",
  "camList.subtitle": "Check the framing, manage the connection, and see which games are still waiting on the camera.",
  "camList.refresh": "Refresh",
  "camList.loadFailed": "We could not load your cameras",
  "camList.emptyTitle": "No camera assigned yet",
  "camList.emptyBody": "When Draffte assigns a camera to your club it appears here, with everything you need to set it up on match day.",
  "camList.unnamed": "Camera",
  "camList.online": "Online",
  "camList.offline": "Offline",
  "camList.unknown": "Not checked",
  "camList.lastSeen": "Last seen",
  "camList.never": "never",
  "camList.justNow": "just now",

  "camDetail.back": "All cameras",
  "camDetail.loading": "Loading…",
  "camDetail.refresh": "Refresh",
  "camDetail.checking": "Checking…",
  "camDetail.online": "Online",
  "camDetail.offline": "Offline",
  "camDetail.offlineHint": "Draffte cannot reach the camera right now. Power it on and give it about two minutes; if it still does not come back, add the ground's Wi-Fi over Bluetooth or turn mobile data on below.",
  "camDetail.loadFailed": "We could not open this camera",

  "camPitch.title": "Check the framing",
  "camPitch.hint": "A picture of what the camera can see right now — so you can confirm it is aimed at the pitch before kick-off.",
  "camPitch.cta": "Take a picture",
  "camPitch.again": "Take another",
  "camPitch.empty": "Take a picture to see what the camera is pointed at.",
  "camPitch.working": "Asking the camera for a picture…",
  "camPitch.workingHint": "This takes around twenty seconds. The camera has to start sending video first.",
  "camPitch.alt": "What the camera is currently pointed at",
  "camPitch.caption": "This is the AI camera operator's view, not the full width that gets recorded. It confirms the camera is on and aimed at a pitch — it does not confirm the far touchline is in frame.",
  "camPitch.timeout": "The camera did not send a picture in time. It may still be starting up, or its connection may be too slow to push video. Try again in a moment.",

  "camNet.title": "Connection",
  "camNet.hint": "Wi-Fi and mobile data both stay on together — mobile data is the safety net, not a replacement.",
  "camNet.refresh": "Refresh",
  "camNet.wifi": "Wi-Fi",
  "camNet.mobile": "Mobile data",
  "camNet.connected": "Connected",
  "camNet.notConnected": "Not connected",
  "camNet.dialling": "Connecting",
  "camNet.off": "Off",
  "camNet.unreadable": "No reading",
  "camNet.wifiUnreadableHint": "The camera did not answer, so we cannot say which network it is on. That usually means it is off or out of range.",
  "camNet.network": "Network",
  "camNet.address": "Address",
  "camNet.signal": "Signal",
  "camNet.sim": "SIM",
  "camNet.simValid": "Card detected",
  "camNet.simInvalid": "Card not readable",
  "camNet.apn": "APN",
  "camNet.apnNote": "The APN belongs to your SIM's operator and is set once by Draffte. If mobile data will not connect with a card in the slot, tell us which operator it is.",
  "camNet.addWifi": "Add a Wi-Fi network over Bluetooth",
  "camNet.modemOn": "Mobile data on",
  "camNet.modemOff": "Mobile data off",
  "camNet.modemOnHint": "The camera stays reachable when the ground's Wi-Fi drops.",
  "camNet.modemOffHint": "Turn it on so the camera keeps working away from your usual Wi-Fi.",
  "camNet.toggleFailed": "The camera did not accept the change",

  "camBle.title": "Add a Wi-Fi network",
  "camBle.subtitle": "Over Bluetooth, standing next to the camera",
  "camBle.introBody": "At a new ground the camera knows no Wi-Fi yet, so there is no network to tell it about one. Bluetooth is how you hand it the first network.",
  "camBle.step1": "Stand within a few metres of the camera and switch it on.",
  "camBle.step2": "Choose the camera in the Bluetooth window your browser opens.",
  "camBle.step3": "Type the ground's Wi-Fi name and password, and the camera joins.",
  "camBle.connectCta": "Connect over Bluetooth",
  "camBle.connectingTitle": "Connecting to the camera",
  "camBle.stepCreds": "Getting this camera's details…",
  "camBle.stepPicker": "Choose the camera in the Bluetooth window…",
  "camBle.stepConnecting": "Opening the connection…",
  "camBle.stepAuth": "Identifying with the camera…",
  "camBle.linked": "Connected to the camera",
  "camBle.ssidLabel": "Wi-Fi network name",
  "camBle.ssidPlaceholder": "Exactly as it appears on your phone",
  "camBle.pwdLabel": "Wi-Fi password",
  "camBle.band24Note": "The camera only joins 2.4 GHz networks. If the ground's Wi-Fi has a separate 5 GHz name, use the 2.4 GHz one.",
  "camBle.sendCta": "Send to the camera",
  "camBle.sendingTitle": "Sending the network…",
  "camBle.sendingBody": "Keep this window open and stay near the camera.",
  "camBle.joiningTitle": "The camera is joining…",
  "camBle.joiningBody": "This can take up to a minute.",
  "camBle.joinedTitle": "Camera connected",
  "camBle.joinedBody": "It joined the network and will use it from now on.",
  "camBle.done": "Done",
  "camBle.close": "Close",
  "camBle.retry": "Try again",
  "camBle.unconfirmedTitle": "Sent, but not confirmed",
  "camBle.unconfirmedBody": "The camera took the network but had not joined it by the time we stopped waiting. It may still join on its own. Refresh the connection panel in a minute to see whether it did — and if it has not, check the network name and password and send them again.",
  "camBle.unsupportedTitle": "This browser cannot use Bluetooth",
  "camBle.unsupportedBody": "Bluetooth from a web page works in Chrome or Edge on a computer, and in Chrome on Android. Safari does not support it, so on an iPhone or iPad use the Draffte app instead — it does the same thing.",
  "camBle.errTitle": "That did not work",
  "camBle.errNoPassword": "This camera has no saved password, so we cannot identify with it over Bluetooth. Contact Draffte.",
  "camBle.errSessionLost": "The Bluetooth connection dropped. Close this and try again.",
  "camBle.errSsidRequired": "Enter the Wi-Fi network name.",
  "camBle.errJoinRejected": "The camera could not join that network. The password is usually wrong, or the signal is too weak where the camera is mounted.",
  "camBle.errSendFailed": "We could not send the network to the camera.",

  "camGames.refresh": "Refresh",
  "camGames.storageTitle": "Space on the camera",
  "camGames.storageHint": "How much of the camera's video storage is used. Once it is full the camera records over the oldest match.",
  "camGames.storageChecking": "Asking the camera…",
  "camGames.storageUnknown": "The camera did not answer, so we do not know how much space is left.",
  "camGames.storageOk": "There is room for the next match.",
  "camGames.storageFull": "Storage is full — the next match will record over the oldest one. Send what you still need first.",
  "camGames.recordingsOnCamera": "{n} on the camera",
  "camGames.pendingTitle": "Still on the camera",
  "camGames.pendingHint": "Games you have played that have no video in Draffte yet.",
  "camGames.sendFromApp": "Sending a match runs from the Draffte app on a phone connected to the camera's own Wi-Fi — the file is pulled off the camera directly, which a web browser cannot do. Open Camera in the app to send.",
  "camGames.showingOf": "Showing the {n} most relevant of {total} — games already on the camera come first.",
  "camGames.pendingEmpty": "Nothing waiting. Every game you have played is already in Draffte.",
  "camGames.vs": "vs",
  "camGames.at": "at",
  "camGames.unknownOpponent": "Unknown opponent",
  "camGames.onCamera": "On the camera",
  "camGames.maybeOnCamera": "Check the camera",
  "camGames.notFound": "No footage found",
  "camGames.inDraffteTitle": "Recordings in Draffte",
  "camGames.inDraffteHint": "Matches you recorded yourself with this camera that have already arrived. Watch or download them from anywhere.",
  "camGames.inDraffteEmpty": "Nothing here yet. This list only holds matches you recorded from your own account — games filmed for you by Draffte are on your Matches page.",
  "camGames.seeMatches": "Go to Matches",
  "camGames.watch": "Watch",
  // ── Player dossier (the scouting export) ──────────────────────────────────
  "squad.export": "Export",
  "squad.exportPlayer": "Export this player's full report",
  "pdossier.back": "Back",
  "pdossier.saveAsPdf": "Save as PDF",
  "pdossier.loading": "Building the report…",
  "pdossier.loadFailed": "We could not load this player's report",
  "pdossier.noData": "Nothing on record yet",
  "pdossier.noDataBody": "{player} has no analysed matches yet, so there is nothing to report. A report appears once a match he played has been tagged.",
  "pdossier.sheet.profile": "Player profile",
  "pdossier.sheet.output": "Career output",
  "pdossier.sheet.matches": "Match by match",
  "pdossier.sheet.peers": "Against his peers",
  "pdossier.sheet.provenance": "Where these numbers come from",
  "pdossier.h.profile": "Profile",
  "pdossier.h.record": "Record",
  "pdossier.h.totals": "Totals",
  "pdossier.h.duels": "Duels",
  "pdossier.h.goalkeeping": "Goalkeeping",
  "pdossier.k.shotsFaced": "Shots on target faced",
  "pdossier.k.saves": "Saves",
  "pdossier.k.conceded": "Conceded",
  "pdossier.k.savePct": "Save % (open play)",
  "pdossier.k.cleanSheets": "Clean sheets",
  "pdossier.k.minutesInGoal": "Minutes in goal",
  "pdossier.gkBasis": "{n} matches in goal",
  "pdossier.gkNone": "no goalkeeper figures recorded yet — see the note below",
  "pdossier.gkNote": "Each figure is summed only over the matches that recorded it. Save % is open play, penalties excluded, and needs three shots faced. Clean sheets are counted over the matches with a tagged match clock. A dash means not measured. Crosses claimed, punches, sweeping and one-on-ones are not measured yet.",
  "pdossier.gkPens": "Penalties faced: {saved} saved of {faced}.",
  "pdossier.h.discipline": "Discipline",
  "pdossier.h.everyMatch": "Every match on record",
  "pdossier.h.percentile": "Percentile rank",
  "pdossier.h.measured": "What is measured",
  "pdossier.h.notMeasured": "What is not, and why",
  "pdossier.h.dash": "Reading a dash",
  "pdossier.k.matches": "Matches",
  "pdossier.k.minutes": "Minutes",
  "pdossier.k.avgRating": "Avg rating",
  "pdossier.k.goals": "Goals",
  "pdossier.k.assists": "Assists",
  "pdossier.k.motm": "MOTM",
  "pdossier.k.yellow": "Yellow cards",
  "pdossier.k.red": "Red cards",
  "pdossier.k.analysed": "Matches analysed",
  "pdossier.k.age": "Age",
  "pdossier.k.height": "Height",
  "pdossier.k.weight": "Weight",
  "pdossier.k.foot": "Foot",
  "pdossier.k.nationality": "Nationality",
  "pdossier.k.squad": "Squad",
  "pdossier.k.status": "Status",
  "pdossier.k.passport": "Passport",
  "pdossier.k.duels": "Tackles and take-ons",
  "pdossier.k.physical": "Distance covered and top speed",
  "pdossier.k.overall": "Overall",
  "pdossier.notRated": "Not yet rated",
  "pdossier.inSquad": "In squad",
  "pdossier.leftSquad": "Left the squad",
  "pdossier.connected": "Connected",
  "pdossier.notConnected": "Not connected",
  "pdossier.matchOne": "match",
  "pdossier.matchMany": "matches",
  "pdossier.per90Basis": "† A per-90 marked here is built on fewer matches than the total beside it: a rate uses only the matches that recorded both the stat and a clock, which is {n} of {total}.",
  "pdossier.provMinutes": "Recorded on {n} of {total} matches. A per-90 is built only from the matches that have both the figure and a clock, so its basis can be narrower still — the totals sheet marks any rate where it is.",
  "pdossier.notComparable": "not comparable",
  "pdossier.col.metric": "Metric",
  "pdossier.col.total": "Total",
  "pdossier.col.from": "From",
  "pdossier.col.per90": "Per 90",
  "pdossier.col.vsSquad": "vs squad",
  "pitch.title": "Pitch calibration",
  "pitch.calibrate": "Calibrate pitch",
  "pitch.exit": "Back to tagging",
  "pitch.chipOk": "Pitch calibrated ±{m} m",
  "pitch.chipForced": "Pitch calibrated ±{m} m (rough, saved anyway)",
  "pitch.chipNone": "Pitch not calibrated: tap ~16 points (1 min)",
  "pitch.chipChecking": "Checking pitch calibration…",
  "pitch.chipUnknown": "Pitch calibration status unavailable",
  "pitch.chipTip": "This game's own pitch calibration. Everything that places an event on the pitch (keeper touches, passes, heatmaps) reads it.",
  "pitch.unsaved": "unsaved",
  "pitch.q.excellent": "excellent",
  "pitch.q.good": "good",
  "pitch.q.rough": "too rough to measure with",
  "pitch.q.running": "good enough for distance and speed",
  "pitch.q.unusable": "unusable",
  "pitch.banner": "PITCH CALIBRATION — tap each landmark on the painted lines",
  "pitch.panoNote": "Calibration is done on the wide panorama, so the view was switched to it.",
  "pitch.tapHint": "Tap this on the video:",
  "pitch.allDone": "Every landmark is handled — check the fit, then save.",
  "pitch.progress": "{n} placed · {s} skipped · {t} total",
  "pitch.skip": "Skip (not visible)",
  "pitch.undo": "Undo",
  "pitch.clear": "Clear",
  "pitch.dragHint": "Drag a marker to adjust it. Skip any landmark you can't see; you need at least 7 to try a fit, and more spread-out ones fit better.",
  "pitch.far": "far touchline (top of the picture)",
  "pitch.near": "near touchline (bottom)",
  "pitch.format": "Pitch format",
  "pitch.thisGame": "This game is {fmt} — the field model starts on it.",
  "pitch.nineNote": "9v9 uses US Soccer's 9v9 field: penalty box 14 × 36 yd, goal area 5 × 16 yd, penalty spot 10 yd, centre circle 8 yd. 9v9 fields run 70–80 × 45–55 yd, so enter the real length and width when the club knows them. If a box is painted to a different size, skip its corners and use the outline, halfway line and centre spot.",
  "pitch.length": "Length (m)",
  "pitch.width": "Width (m)",
  "pitch.measured": "Measured with a tape",
  "pitch.measuredHint": "Fill in both lengths to say they were measured.",
  "pitch.groupLeft": "Left end",
  "pitch.groupRight": "Right end",
  "pitch.groupExtras": "Corners, spots and halfway",
  "pitch.rowTip": "Make this the next one to tap",
  "pitch.removePoint": "Remove this point",
  "pitch.placed": "placed",
  "pitch.skippedTag": "skipped",
  "pitch.checkFit": "Check fit",
  "pitch.propose": "Pre-fill with Claude",
  "pitch.proposing": "Claude is looking at this frame… (about 30 seconds)",
  "pitch.proposeTip": "Pause on a moment where the lines are clear of players first. Claude places the landmarks it can see; you check them.",
  "pitch.proposed": "Claude placed {n} landmarks. Check the lines it draws on the video, drag any point that is off, tap the ones it missed, then Save.",
  "pitch.proposedNone": "Claude couldn't make out any landmarks at this moment. Try a moment where the lines are clear of players.",
  "pitch.proposedSuggested": "Claude placed {n} landmarks and suggests {s} more (dashed violet). For each suggestion: click it if it sits exactly on the painted corner, drag it there if not, or tap the right spot. Suggestions don't count until you do. Then Save.",
  "pitch.suggestedHint": "Suggested (dashed marker), not placed yet. If it sits exactly on the paint, confirm it; otherwise drag it or tap the right spot.",
  "pitch.confirmSuggested": "It's on the spot — confirm",
  "pitch.suggestedTag": "suggested",
  "pitch.suggestedMarkerTip": "suggested: click if it is exactly on the paint, drag it if not",
  "calib.checkFind": "Find the mistake",
  "calib.checking": "Looking for the mistake… (up to a minute)",
  "calib.checkTip": "Tries swapping landmarks that are easy to mix up, and leaving out one point at a time, using only your own points. Nothing moves until you click.",
  "calib.checkNone": "No single mistake explains it. Zoom in on each point and check it sits exactly on its corner.",
  "calib.checkFound": "Found it — fixing this takes the error from {before} m to {after} m:",
  "calib.swapLine": "{a} and {b} look swapped",
  "calib.checkFoundOff": "These points disagree with all the others — without them the error is {after} m instead of {before} m. Zoom in on each and put it exactly on its corner:",
  "calib.offLine": "{a} (about {m} m off)",
  "calib.applySwap": "Swap them",
  "calib.applySwaps": "Swap them all",
  "calib.showPoint": "Show it",
  "pitch.traceTitle": "Trace lines",
  "pitch.legendTodo": "trace these",
  "pitch.legendDone": "traced",
  "pitch.legendNow": "tracing now",
  "pitch.traceWhy": "Pick a line, then click 5–10 times anywhere along its paint, spread out. A point on a line can't be mislabelled, and the near touchline has only three named landmarks — tracing it makes the fit much steadier.",
  "pitch.traceTip": "Click to trace this line; click again to stop",
  "pitch.traceClear": "Remove this line's points",
  "pitch.traceDotTip": "click to remove this point",
  "pitch.tracingHint": "Tracing",
  "pitch.tracingHow": "Click anywhere on this line's paint, 5–10 times, spread out — not on players. Click a dot to remove it. Esc or Done when finished.",
  "pitch.traceDone": "Done",
  "pitch.trace.near_touchline": "Near touchline",
  "pitch.trace.halfway": "Halfway line",
  "pitch.trace.far_touchline": "Far touchline",
  "pitch.trace.center_circle": "Centre circle",
  "pitch.trace.left_goal_line": "Left goal line",
  "pitch.trace.right_goal_line": "Right goal line",
  "pitch.trace.left_box_front": "Left box · front line",
  "pitch.trace.right_box_front": "Right box · front line",
  "calib.zoomIn": "Zoom in",
  "calib.zoomOut": "Zoom out",
  "calib.fit": "Whole",
  "calib.loupe": "Magnifier",
  "calib.stillLoading": "Loading the sharp frame…",
  "calib.stillFailed": "Couldn't load the sharp frame.",
  "calib.retry": "Retry",
  "calib.stillOn": "Sharp frame · full resolution",
  "calib.stillTip": "You are placing points on the camera's original frame, not the lighter video copy.",
  "calib.stillOff": "Video copy (blurry) · pause to get the sharp frame",
  "calib.help": "Scroll to zoom · right-drag or Space+drag to move around · click a point to select it, arrows nudge it 1 px (Shift: 10) · F zooms to it · Delete removes the selected point · ⌘/Ctrl+Z undo · 0 whole picture",
  "pitch.checking": "Checking…",
  "pitch.save": "Save",
  "pitch.saving": "Saving…",
  "pitch.saveAnyway": "Save anyway…",
  "pitch.saveAnywayConfirm": "Yes, store this loose fit",
  "pitch.remove": "Remove calibration",
  "pitch.removeConfirm": "Confirm: remove calibration",
  "pitch.removing": "Removing…",
  "pitch.fitResult": "Typical error {rms} m · worst {max} m",
  "pitch.fitLines": "Do the drawn lines sit on the painted ones? If not, drag the points and check again.",
  "pitch.fitStale": "Points changed since the last check — the dashed lines are out of date.",
  "pitch.saved": "Saved. This game now uses its own pitch calibration.",
  "pitch.removed": "Calibration removed.",
  "pitch.needPoints": "Place at least 7 landmarks first.",
  "pitch.videoNotReady": "The video isn't ready yet — wait for the picture, then try again.",
  "pitch.failed": "Request failed",
  "pitch.refusedHint": "Not saved. You can fix the points, or store it anyway if you're sure.",
  "pitch.refusedRunning": "Not saved yet. This fit is good enough for distance and speed (2 m or better), not yet for the finest pitch maps (1 m). Save anyway to keep it.",
  "pitch.keysBlocked": "Tagging shortcuts are off while calibrating.",
  "pitch.lm.left_box_goal_top": "Left penalty box · goal-line corner (far)",
  "pitch.lm.left_box_goal_bottom": "Left penalty box · goal-line corner (near)",
  "pitch.lm.left_penalty_box_top": "Left penalty box · front corner (far)",
  "pitch.lm.left_penalty_box_bottom": "Left penalty box · front corner (near)",
  "pitch.lm.left_goal_area_goal_top": "Left goal area · goal-line corner (far)",
  "pitch.lm.left_goal_area_goal_bottom": "Left goal area · goal-line corner (near)",
  "pitch.lm.left_goal_area_top": "Left goal area · front corner (far)",
  "pitch.lm.left_goal_area_bottom": "Left goal area · front corner (near)",
  "pitch.lm.right_box_goal_top": "Right penalty box · goal-line corner (far)",
  "pitch.lm.right_box_goal_bottom": "Right penalty box · goal-line corner (near)",
  "pitch.lm.right_penalty_box_top": "Right penalty box · front corner (far)",
  "pitch.lm.right_penalty_box_bottom": "Right penalty box · front corner (near)",
  "pitch.lm.right_goal_area_goal_top": "Right goal area · goal-line corner (far)",
  "pitch.lm.right_goal_area_goal_bottom": "Right goal area · goal-line corner (near)",
  "pitch.lm.right_goal_area_top": "Right goal area · front corner (far)",
  "pitch.lm.right_goal_area_bottom": "Right goal area · front corner (near)",
  "pitch.lm.tl_corner": "Corner flag · far left",
  "pitch.lm.bl_corner": "Corner flag · near left",
  "pitch.lm.tr_corner": "Corner flag · far right",
  "pitch.lm.br_corner": "Corner flag · near right",
  "pitch.lm.left_penalty_spot": "Penalty spot · left",
  "pitch.lm.right_penalty_spot": "Penalty spot · right",
  "pitch.lm.halfway_top": "Halfway line · far touchline",
  "pitch.lm.halfway_bottom": "Halfway line · near touchline",
  "pitch.lm.center_spot": "Center spot",
};

const es: Dict = {
  "coachScout.title": "Ojeo",
  "coachScout.subtitle": "Cartera de jugadores y objetivos de fichaje",
  "coachScout.addProspect": "Añadir candidato",
  "coachScout.kpiTotalProspects": "Total de candidatos",
  "coachScout.kpiTotalProspectsSub": "cartera activa",
  "coachScout.kpiPriorityTargets": "Objetivos prioritarios",
  "coachScout.kpiPriorityTargetsSub": "listos para abordar",
  "coachScout.kpiAvgPotential": "Potencial medio",
  "coachScout.kpiAvgPotentialSub": "en toda la cartera",
  "coachScout.kpiExpiringSoon": "Vencen pronto",
  "coachScout.kpiExpiringSoonSub": "contratos ≤ 2026",
  "coachScout.trendingNow": "Tendencia ahora",
  "coachScout.playersInForm": "Jugadores en forma",
  "coachScout.searchPlaceholder": "Nombre, club, posición…",
  "coachScout.filterAll": "Todos",
  "coachScout.allPos": "Todas las pos.",
  "coachScout.shortlist": "Lista de seguimiento ({n})",
  "coachScout.sortPotential": "Ordenar: Potencial",
  "coachScout.sortOvr": "Ordenar: OVR",
  "coachScout.sortTrend": "Ordenar: Tendencia",
  "coachScout.sortAge": "Ordenar: Edad",
  "coachScout.sortValue": "Ordenar: Valor",
  "coachScout.sortName": "Ordenar: Nombre",
  "coachScout.prospectsShown": "{n} candidato mostrado",
  "coachScout.prospectsShownPlural": "{n} candidatos mostrados",
  "coachScout.selectedForComparison": "{n}/2 seleccionados para comparar",
  "coachScout.compare": "Comparar",
  "coachScout.clear": "Limpiar",
  "coachScout.playerComparison": "Comparación de jugadores",
  "coachScout.closeX": "✕ Cerrar",
  "coachScout.noProspectsMatch": "Ningún candidato coincide con tus filtros.",
  "coachScout.colPlayer": "Jugador",
  "coachScout.colContract": "Contrato",
  "coachScout.colStatus": "Estado",
  "coachScout.colTrend": "Tendencia",
  "coachScout.colForm": "Forma",
  "coachScout.aiRecTitle": "Recomendación de ojeo con IA",
  "coachScout.aiRecPart1": "Según el perfil de tu plantilla,",
  "coachScout.aiRecPart2": "(🇳🇬 RW, 18 años) tiene el mayor potencial de tu base de datos con",
  "coachScout.aiRecPot": "92 POT",
  "coachScout.aiRecPart3": ". Su velocidad (34.1 km/h) complementaría tus opciones por banda actuales.",
  "coachScout.aiRecPart4": "Señala también:",
  "coachScout.aiRecAnd": "y",
  "coachScout.aiRecPart5": "ambos terminan contrato en 2025 — la ventana se abre en enero.",
  "coachScout.statusIdentified": "Identificado",
  "coachScout.statusWatched": "En seguimiento",
  "coachScout.statusShortlisted": "En lista de seguimiento",
  "coachScout.statusTarget": "Objetivo",
  "coachScout.statusSigned": "Fichado",
  "coachScout.statusRejected": "Descartado",
  "coachScout.priorityHigh": "Alta",
  "coachScout.priorityMed": "Media",
  "coachScout.priorityLow": "Baja",
  "coachScout.priorityLabel": "Prioridad",
  "coachScout.contract": "Contrato",
  "coachScout.out": "Libre",
  "coachScout.noPlayers": "Sin jugadores",
  "coachScout.unitYr": " a",
  "coachScout.ovr": "OVR",
  "coachScout.pot": "POT",
  "coachScout.age": "Edad",
  "coachScout.value": "Valor",
  "coachScout.potential": "POTENCIAL",
  "coachScout.trend30d": "Tendencia 30 días",
  "coachScout.infoNationality": "Nacionalidad",
  "coachScout.infoPreferredFoot": "Pie preferido",
  "coachScout.infoHeight": "Altura",
  "coachScout.infoTopSpeed": "Velocidad máx.",
  "coachScout.infoGoalsSeason": "Goles (temporada)",
  "coachScout.infoAssistsSeason": "Asistencias (temporada)",
  "coachScout.attributes": "Atributos",
  "coachScout.attrSpeed": "Velocidad",
  "coachScout.attrFinishing": "Definición",
  "coachScout.attrPlaymaking": "Creación de juego",
  "coachScout.attrWorkRate": "Ritmo de trabajo",
  "coachScout.attrDefending": "Defensa",
  "coachScout.attrStrength": "Fuerza",
  "coachScout.matchStats": "Estadísticas del partido",
  "coachScout.passAccuracy": "Precisión de pase",
  "coachScout.scoutNotesHeading": "Notas de ojeo",
  "coachScout.scoutingReports": "Informes de ojeo ({n})",
  "coachScout.addReport": "Añadir informe",
  "coachScout.noReportsYet": "Aún no hay informes",
  "coachScout.updateStatus": "Actualizar estado",
  "coachScout.reportsCount": "{n} informe",
  "coachScout.reportsCountPlural": "{n} informes",
  "coachScout.highPriority": "PRIORIDAD ALTA",
  "coachScout.addReportTitle": "Añadir informe de ojeo",
  "coachScout.matchContext": "Contexto del partido",
  "coachScout.matchContextPlaceholder": "p. ej. Club A vs Club B (2-1 V)",
  "coachScout.rating": "Valoración",
  "coachScout.ratingPoor": "Pobre",
  "coachScout.ratingAverage": "Media",
  "coachScout.ratingExcellent": "Excelente",
  "coachScout.scoutNotes": "Notas del ojeador",
  "coachScout.scoutNotesPlaceholder": "Observaciones, fortalezas, dudas…",
  "coachScout.cancel": "Cancelar",
  "coachScout.saveReport": "Guardar informe",
  "coachScout.browseTitle": "Explorar jugadores del Pasaporte Draffte",
  "coachScout.browseSubtitle": "{n} jugadores en la red Draffte · perfiles verificados",
  "coachScout.inPipeline": "✓ En la cartera",
  "coachScout.added": "✓ Añadido",
  "coachScout.addToPipeline": "+ Añadir a la cartera",
  "coachScout.noPlayersMatch": "Ningún jugador coincide con tus filtros.",
  "coachScout.playersAdded": "{n} jugador añadido a la cartera",
  "coachScout.playersAddedPlural": "{n} jugadores añadidos a la cartera",
  "coachScout.selectPlayersToAdd": "Selecciona jugadores para añadirlos a tu cartera de ojeo",
  "coachScout.done": "Listo",
  "coachScout.addedFromNetwork": "Añadido desde la red del Pasaporte Draffte.",
  "coachScout.coachFallback": "Entrenador",
  "coachAnalytics.title": "Analíticas del equipo",
  "coachAnalytics.subtitle": "Inteligencia de rendimiento · Temporada {y}",
  "coachAnalytics.notTracked": "sin seguimiento",
  "coachAnalytics.speedNotTracked": "La velocidad máxima requiere seguimiento de jugadores desde el video del partido — registramos toques del balón, no posiciones. Aquí no hay nada estimado.",
  "coachAnalytics.rowShotsPg": "Remates p/P",
  "coachAnalytics.rowGoalsPg": "Goles p/P",
  "coachAnalytics.rowDuelsWon": "Duelos ganados p/P",
  "coachAnalytics.export": "Exportar",
  "coachAnalytics.refresh": "Actualizar",
  "coachAnalytics.emptyTitle": "Aún no hay analíticas",
  "coachAnalytics.emptyDesc": "Añade jugadores y registra partidos para ver las analíticas.",
  "coachAnalytics.kpiSquadAvgOvr": "OVR medio de la plantilla",
  "coachAnalytics.kpiImproving": "Mejorando",
  "coachAnalytics.kpiDeclining": "Empeorando",
  "coachAnalytics.kpiAvgTopSpeed": "Velocidad punta media",
  "coachAnalytics.kpiThisMonth": "{n} este mes",
  "coachAnalytics.kpiTrendingUp": "jugadores en alza",
  "coachAnalytics.kpiNeedAttention": "requieren atención",
  "coachAnalytics.kpiKmhAcrossSquad": "km/h en la plantilla (est.)",
  "coachAnalytics.squadOvrProgression": "Progresión del OVR de la plantilla",
  "coachAnalytics.avgOverallLast8": "Valoración media general — últimos 8 meses",
  "coachAnalytics.ovr": "OVR",
  "coachAnalytics.teamAttributeProfile": "Perfil de atributos del equipo",
  "coachAnalytics.vsLeagueBenchmark": "frente a la referencia de la liga",
  "coachAnalytics.legendSquad": "Plantilla",
  "coachAnalytics.legendBenchmark": "Referencia",
  "coachAnalytics.attrSpd": "VEL",
  "coachAnalytics.attrFin": "TIR",
  "coachAnalytics.attrPly": "CRE",
  "coachAnalytics.attrWrk": "TRA",
  "coachAnalytics.attrDef": "DEF",
  "coachAnalytics.attrStr": "FÍS",
  "coachAnalytics.posForwards": "Delanteros",
  "coachAnalytics.posMidfielders": "Centrocampistas",
  "coachAnalytics.posDefenders": "Defensas",
  "coachAnalytics.posGoalkeeper": "Portero",
  "coachAnalytics.nPlayers": "{n} jugadores",
  "coachAnalytics.avgOvr": "OVR medio",
  "coachAnalytics.avgSpeed": "Velocidad media",
  "coachAnalytics.avgFinishing": "Definición media",
  "coachAnalytics.playerBreakdown": "Desglose por jugador",
  "coachAnalytics.searchPlaceholder": "Buscar…",
  "coachAnalytics.filterAll": "Todos",
  "coachAnalytics.groupPerformance": "Rendimiento",
  "coachAnalytics.groupPhysical": "Físico",
  "coachAnalytics.groupTactical": "Táctico",
  "coachAnalytics.sortOvr": "Ordenar: OVR",
  "coachAnalytics.sortTrend": "Ordenar: Tendencia",
  "coachAnalytics.sortSpeed": "Ordenar: Velocidad",
  "coachAnalytics.sortWorkRate": "Ordenar: Trabajo",
  "coachAnalytics.sortName": "Ordenar: Nombre",
  "coachAnalytics.thPlayer": "Jugador",
  "coachAnalytics.thOvr": "OVR",
  "coachAnalytics.thTrend": "Tendencia",
  "coachAnalytics.thTopSpeed": "Velocidad punta",
  "coachAnalytics.thKmSession": "Km/sesión",
  "coachAnalytics.thSessionsMo": "Sesiones/mes",
  "coachAnalytics.thMinsPlayed": "Min. jugados",
  "coachAnalytics.thPassAcc": "Prec. pases",
  "coachAnalytics.thShotsOT": "Tiros a puerta",
  "coachAnalytics.thTackles": "Entradas",
  "coachAnalytics.thAerials": "Aéreos",
  "coachAnalytics.thForm": "Forma",
  "coachAnalytics.noPlayersMatch": "Ningún jugador coincide con tus filtros.",
  "coachAnalytics.aiInsightTitle": "Análisis de rendimiento con IA",
  "coachAnalytics.aiInsightLead": "El OVR medio de tu plantilla ha mejorado en",
  "coachAnalytics.aiInsightDelta": "{n} puntos",
  "coachAnalytics.aiInsightMid": "en los últimos 8 meses. La creación de juego en el centro del campo es tu mayor fortaleza frente a la referencia, mientras que la defensa se beneficiaría de sesiones específicas adicionales.",
  "coachAnalytics.aiInsightAnd": "y",
  "coachAnalytics.aiInsightTail": "muestran una tendencia a la baja — considera revisar su carga de entrenamiento.",
  "coachAnalytics.age": "Edad",
  "coachAnalytics.thirtyDayTrend": "Tendencia a 30 días",
  "coachAnalytics.attributeRadar": "Radar de atributos",
  "coachAnalytics.coreAttributes": "Atributos principales",
  "coachAnalytics.physicalMetrics": "Métricas físicas",
  "coachAnalytics.physTopSpeed": "Velocidad punta",
  "coachAnalytics.physKmSession": "Km/sesión",
  "coachAnalytics.physSessions": "Sesiones",
  "coachAnalytics.unitKmh": "km/h est.",
  "coachAnalytics.unitKm": "km est.",
  "coachAnalytics.unitPerMo": "/mes",
  "coachAnalytics.gkStats": "Estadísticas de portero",
  "coachAnalytics.attackingStats": "Estadísticas ofensivas (por partido)",
  "coachAnalytics.midfieldStats": "Estadísticas de mediocampo (por partido)",
  "coachAnalytics.defensiveStats": "Estadísticas defensivas (por partido)",
  "coachAnalytics.rowReflexes": "Reflejos (DEF)",
  "coachAnalytics.rowDistribution": "Distribución %",
  "coachAnalytics.rowAerialClaims": "Balones aéreos atrapados",
  "coachAnalytics.rowSweeping": "Juego de líbero",
  "coachAnalytics.rowHandling": "Blocaje (FÍS)",
  "coachAnalytics.rowShotsOnTarget": "Tiros a puerta",
  "coachAnalytics.rowEstGoals": "Goles est./partido",
  "coachAnalytics.rowKeyPasses": "Pases clave",
  "coachAnalytics.rowAerialDuels": "Duelos aéreos",
  "coachAnalytics.rowPressing": "Presión (Ent.)",
  "coachAnalytics.rowPassAccuracy": "Precisión de pases %",
  "coachAnalytics.rowTacklesPg": "Entradas p/P",
  "coachAnalytics.rowBallRecoveries": "Recuperaciones",
  "coachAnalytics.rowAerialWins": "Duelos aéreos ganados",
  "coachAnalytics.rowInterceptions": "Intercepciones",
  "coachAnalytics.rowShotsBlocked": "Tiros bloqueados",
  "coachAnalytics.statSpeed": "Velocidad",
  "coachAnalytics.statFinishing": "Definición",
  "coachAnalytics.statPlaymaking": "Creación",
  "coachAnalytics.statWorkRate": "Trabajo",
  "coachAnalytics.statDefending": "Defensa",
  "coachAnalytics.statStrength": "Fuerza",
  "coachAnalytics.statTopSpeed": "Velocidad punta",
  "coachAnalytics.statKmSession": "Km/sesión",
  "coachAnalytics.statPassAcc": "Prec. pases",
  "coachAnalytics.statShotsOT": "Tiros a puerta",
  "coachAnalytics.statTackles": "Entradas",
  "coachAnalytics.statAerialW": "Aéreos gan.",
  "coachAI.title": "Análisis IA",
  "coachAI.subtitle": "Motor de inteligencia Draffte · Actualizado hace 2 horas",
  "coachAI.liveAnalysis": "Análisis en vivo",
  "coachAI.pillCritical": "{n} críticos",
  "coachAI.pillWarnings": "{n} advertencias",
  "coachAI.pillPositives": "{n} positivos",
  "coachAI.pillPlayerReports": "{n} informes de jugadores",
  "coachAI.tabInsights": "Hallazgos",
  "coachAI.tabAskAI": "Preguntar a la IA",
  "coachAI.tabPlayers": "Informes de jugadores",
  "coachAI.tabTactical": "Informes tácticos",
  "coachAI.catTactical": "Táctico",
  "coachAI.catPhysical": "Físico",
  "coachAI.catPsychology": "Psicología",
  "coachAI.catPerformance": "Rendimiento",
  "coachAI.catInjury": "Riesgo de lesión",
  "coachAI.riskLow": "Bajo",
  "coachAI.riskMedium": "Medio",
  "coachAI.riskHigh": "Alto",
  "coachAI.pinned": "Fijado",
  "coachAI.pin": "Fijar",
  "coachAI.recommendedAction": "Acción recomendada",
  "coachAI.aiConfidence": "Confianza de la IA",
  "coachAI.ovr": "OVR",
  "coachAI.aiRating": "Valoración IA",
  "coachAI.thirtyDayProj": "Proy. 30 días",
  "coachAI.injRisk": "Riesgo lesión",
  "coachAI.strength": "Fortaleza",
  "coachAI.weakness": "Debilidad",
  "coachAI.aiRecommendation": "Recomendación de la IA",
  "coachAI.formTrend_rising": "Forma en alza",
  "coachAI.formTrend_stable": "Forma estable",
  "coachAI.formTrend_declining": "Forma en descenso",
  "coachAI.tacticalReport": "Informe táctico",
  "coachAI.suggestedFormation": "Formación sugerida:",
  "coachAI.strengthsToExploit": "Fortalezas a explotar",
  "coachAI.threatsToManage": "Amenazas a gestionar",
  "coachAI.aiSuggestions": "Sugerencias de la IA",
  "coachAI.chatPlaceholder": "Pregunta sobre tu plantilla, táctica, estado físico de jugadores…",
  "coachAI.chatHint": "Pulsa Intro para enviar · Mayús+Intro para nueva línea",
  "coachAI.playersDesc": "Evaluaciones generadas por IA a partir del seguimiento por cámara, métricas de sesión y vídeo del partido.",
  "coachAI.tacticalDesc": "Planes tácticos generados por IA a partir del análisis del rival, la disponibilidad de la plantilla y la forma reciente.",
  "coachAI.explainerTitle": "Cómo genera la IA de Draffte los informes tácticos",
  "coachAI.explainerBody": "Los informes se generan combinando el seguimiento por cámara de tu equipo, los registros de eventos del partido, el historial de formaciones y las estadísticas del rival de bases de datos públicas. La IA identifica patrones en todas las fuentes de datos para producir recomendaciones contextuales específicas para tu plantilla.",
  "coachAI.sourceCameraTracking": "Seguimiento por cámara",
  "coachAI.sourceMatchEvents": "Eventos del partido",
  "coachAI.sourceVideoAnalysis": "Análisis de vídeo",
  "coachAI.sourceOpponentData": "Datos del rival",
  "coachAI.suggestion1": "¿Qué jugadores tienen ahora mismo mayor riesgo de lesión?",
  "coachAI.suggestion2": "¿Qué formación debería usar contra una presión alta?",
  "coachAI.suggestion3": "¿Qué jugadores rinden por debajo de su potencial?",
  "coachAI.suggestion4": "¿Cómo ha mejorado nuestra presión esta temporada?",
  "coachAI.suggestion5": "¿A quién debería dar descanso antes del próximo partido?",
  "coachAI.suggestion6": "¿Cuál es nuestra mayor vulnerabilidad táctica?",
  "coachSetup.title": "Configuración del partido",
  "coachSetup.subtitle": "Configura las camisetas para un seguimiento preciso de los jugadores",
  "coachSetup.backToMatch": "Volver al partido",
  "coachSetup.step1Colors": "1. Colores",
  "coachSetup.step2Lineup": "2. Alineación",
  "coachSetup.whyColorsTitle": "¿Por qué los colores de las camisetas?",
  "coachSetup.whyColorsBody": "Nuestra cámara usa los colores de las camisetas para identificar al instante a qué equipo pertenece cada jugador. Sin esto, necesitamos ~4 segundos de vídeo para detectarlo automáticamente, y la precisión baja con colores similares.",
  "coachSetup.homeJersey": "Camiseta local",
  "coachSetup.awayJersey": "Camiseta visitante",
  "coachSetup.goalkeeper": "Portero",
  "coachSetup.sameColorWarning": "Los colores local y visitante son iguales: al seguimiento le costará distinguir los equipos.",
  "coachSetup.cameraPreview": "Vista previa de la cámara",
  "coachSetup.yourTeam": "Tu equipo",
  "coachSetup.vs": "VS",
  "coachSetup.opponent": "Rival",
  "coachSetup.continueToLineup": "Continuar a la alineación",
  "coachSetup.duplicateWarning": "Números de camiseta duplicados detectados: {nums}. Cada jugador debe tener un número único.",
  "coachSetup.startingXI": "Once inicial ({n}/11)",
  "coachSetup.selectMore": "Selecciona {n} más",
  "coachSetup.gkBadge": "POR",
  "coachSetup.substitutes": "Suplentes ({n})",
  "coachSetup.savedRedirecting": "Guardado — Redirigiendo a subir...",
  "coachSetup.saving": "Guardando...",
  "coachSetup.saveContinue": "Guardar y continuar a subir",
  "coachSetup.customColor": "Color personalizado",
  "coachSetup.colorRed": "Rojo",
  "coachSetup.colorOrange": "Naranja",
  "coachSetup.colorAmber": "Ámbar",
  "coachSetup.colorYellow": "Amarillo",
  "coachSetup.colorGreen": "Verde",
  "coachSetup.colorTeal": "Verde azulado",
  "coachSetup.colorSkyBlue": "Azul cielo",
  "coachSetup.colorBlue": "Azul",
  "coachSetup.colorIndigo": "Índigo",
  "coachSetup.colorPurple": "Morado",
  "coachSetup.colorPink": "Rosa",
  "coachSetup.colorRose": "Rosa fuerte",
  "coachSetup.colorWhite": "Blanco",
  "coachSetup.colorGray": "Gris",
  "coachSetup.colorBlack": "Negro",
  "coachSetup.colorBrown": "Marrón",
  "coachUpload.title": "Subir vídeo del partido",
  "coachUpload.subtitle": "Sube la grabación completa del partido para el análisis",
  "coachUpload.back": "Atrás",
  "coachUpload.pipelineUpload": "Subir vídeo",
  "coachUpload.pipelineProcessing": "Procesando",
  "coachUpload.pipelineFullDetails": "Detalles completos",
  "coachUpload.dropTitle": "Arrastra y suelta aquí el vídeo de tu partido",
  "coachUpload.dropSubtitle": "o haz clic para buscar — compatible con MP4, MOV, MKV",
  "coachUpload.max4gb": "Máx. 4 GB",
  "coachUpload.min720p": "720p mínimo",
  "coachUpload.fullMatchPreferred": "Partido completo preferible",
  "coachUpload.uploading": "Subiendo {name}",
  "coachUpload.uploadComplete": "Subida completa — iniciando análisis...",
  "coachUpload.analyzing": "Analizando el vídeo del partido...",
  "coachUpload.analyzingSubtitle": "Siguiendo jugadores, detectando eventos, generando estadísticas",
  "coachUpload.stepDetecting": "Detectando jugadores y balón",
  "coachUpload.stepClassifying": "Clasificando equipos",
  "coachUpload.stepTracking": "Siguiendo posiciones",
  "coachUpload.stepComputing": "Calculando estadísticas",
  "coachUpload.stepGenerating": "Generando hallazgos",
  "coachUpload.analysisComplete": "¡Análisis completado!",
  "coachUpload.redirectingFullDetails": "Redirigiendo a detalles completos...",
  "coachUpload.processingFailedShort": "Falló el procesamiento",
  "coachUpload.tryAgain": "Reintentar",
  "coachUpload.tipsTitle": "Consejos para mejores resultados",
  "coachUpload.tip1": "Usa una cámara fija y de gran angular que cubra todo el campo",
  "coachUpload.tip2": "Asegúrate de que las camisetas tengan colores distintos (local vs visitante)",
  "coachUpload.tip3": "Graba todo el partido sin mover la cámara",
  "coachUpload.tip4": "Una buena iluminación mejora la precisión de detección de jugadores",
  "coachUpload.tip5": "Asigna los números de camiseta antes de subir para una mejor identificación de jugadores",
  "coachUpload.skipViewStats": "Omitir — Ver estadísticas existentes",
  "coachUpload.errorProcessingFailed": "Falló el procesamiento. Inténtalo de nuevo.",
  "coachUpload.errorSelectVideo": "Selecciona un archivo de vídeo (MP4, MOV, etc.)",
  "coachInvolve.back": "Atrás",
  "coachInvolve.title": "Participación del jugador",
  "coachInvolve.aiTag": "IA",
  "coachInvolve.subtitle": "Toques con balón, mapas de calor y momentos destacados auto-editados del vídeo del partido",
  "coachInvolve.clockCalibrated": "reloj calibrado",
  "coachInvolve.loading": "Cargando…",
  "coachInvolve.noAnalysisTitle": "Aún no hay análisis",
  "coachInvolve.noAnalysisBody": "Ejecuta la canalización de IA sobre el vídeo de este partido para obtener la participación con balón por jugador, mapas de calor de posición y vídeos de momentos destacados.",
  "coachInvolve.preparing": "Preparando…",
  "coachInvolve.startAnalysis": "Iniciar análisis IA",
  "coachInvolve.processingMatch": "Procesando el partido…",
  "coachInvolve.processingSubtitle": "Normalmente tarda de 1 a 2 horas para un partido completo. La página se actualiza automáticamente.",
  "coachInvolve.onBallInvolvement": "Participación con balón",
  "coachInvolve.framesAnalyzed": "· {n} fotogramas analizados",
  "coachInvolve.unknown": "Desconocido",
  "coachInvolve.touches": "toques",
  "coachInvolve.detailSummary": "{touches} toques · {moments} momentos con balón",
  "coachInvolve.heatmapAlt": "mapa de calor #{n}",
  "coachInvolve.noHighlight": "No hay vídeo de momentos destacados para este jugador (solo los mejores).",
  "coachInvolve.podCommandLabel": "Pega esto en un terminal de pod GPU para ejecutar el trabajo:",
  "coachInvolve.copyCommand": "Copiar comando",
  "coachInvolve.failedToLoad": "Error al cargar",
  "coachInvolve.failedToStart": "Error al iniciar",
  // ── Nav / header ──
  "nav.overview": "Resumen",
  "nav.squad": "Plantilla",
  "nav.sessions": "Entrenamientos",
  "nav.matches": "Partidos",
  "nav.analytics": "Analíticas",
  "nav.myTeams": "Mis equipos",
  "coachTeams.title": "Mis equipos",
  "coachTeams.subtitle": "Tu equipo y los equipos que otros entrenadores compartieron contigo.",
  "coachTeams.loading": "Cargando equipos…",
  "coachTeams.loadFailed": "No se pudieron cargar tus equipos. Inténtalo de nuevo en un momento.",
  "coachTeams.demoNote": "La cuenta de demostración no puede unirse a equipos ni compartirlos.",
  "coachTeams.active": "Activo",
  "coachTeams.leaveFailed": "No se pudo salir de este equipo.",
  "coachTeams.activeDirected": "Tu panel está apuntando a {team}, un equipo que diriges. La plantilla, los partidos, el día de partido y la cámara siguen a ese equipo. Los ajustes de equipo de abajo solo aplican a los equipos que te pertenecen; cambia a uno propio para acceder a ellos.",
  "coachTeams.switchTo": "Cambiar a este equipo",
  "coachTeams.switching": "Cambiando…",
  "coachTeams.switchFailed": "No se pudo cambiar de equipo. Inténtalo de nuevo en un momento.",
  "coachTeams.createTeam": "Crear un equipo nuevo",
  "coachTeams.createTitle": "Crear un equipo nuevo",
  "coachTeams.teamName": "Nombre del equipo",
  "coachTeams.country": "País",
  "coachTeams.squadLevel": "Nivel del equipo",
  "coachTeams.countryPlaceholder": "Selecciona país",
  "coachTeams.levelPlaceholder": "Selecciona nivel",
  "coachTeams.searchCountry": "Buscar país…",
  "coachTeams.levelUnset": "— Sin nivel",
  "coachTeams.noCountry": "No se encontró ningún país.",
  "coachTeams.create": "Crear equipo",
  "coachTeams.createFailed": "No se pudo crear el equipo. Inténtalo de nuevo en un momento.",
  "coachTeams.roleOwner": "Propietario",
  "coachTeams.roleViewer": "Observador",
  "coachTeams.roleDirector": "Director",
  "coachTeams.readOnly": "Solo lectura",
  "coachTeams.players": "jugadores",
  "coachTeams.coachBy": "Entrenador:",
  "coachTeams.rename": "Renombrar equipo",
  "coachTeams.renameFailed": "No se pudo renombrar el equipo.",
  "coachTeams.inviteTitle": "Código de invitación para entrenadores",
  "coachTeams.inviteBody": "Comparte este código con otro entrenador para que pueda VER este equipo (plantilla y resultados, solo lectura). Nunca le permite editar nada, y es independiente del código de tus jugadores.",
  "coachTeams.showCode": "Mostrar código de entrenador",
  "coachTeams.codeFailed": "No se pudo obtener el código de entrenador.",
  "coachTeams.copy": "Copiar",
  "coachTeams.copied": "Copiado",
  "coachTeams.rotate": "Nuevo código",
  "coachTeams.rotateTip": "Genera un código nuevo — el anterior deja de funcionar",
  "coachTeams.joinAnother": "Unirse al equipo de otro entrenador",
  "coachTeams.joinTitle": "Unirse a un equipo",
  "coachTeams.joinBody": "Introduce el código de 6 caracteres que otro entrenador compartió contigo. Tendrás acceso de solo lectura a su plantilla y resultados.",
  "coachTeams.join": "Unirse",
  "coachTeams.checking": "Comprobando código…",
  "coachTeams.notFound": "No se encontró ningún equipo con ese código.",
  "coachTeams.joinFailed": "No se pudo unir a ese equipo.",
  "coachTeams.view": "Ver",
  "coachTeams.leave": "Salir de este equipo",
  "coachTeams.viewFailed": "No se pudo cargar este equipo.",
  "coachTeams.backToTeams": "Volver a Mis equipos",
  "coachTeams.squad": "Plantilla",
  "coachTeams.matches": "Partidos",
  "coachTeams.noPlayers": "Aún no hay jugadores.",
  "coachTeams.noMatches": "Aún no hay partidos.",
  "coachTeams.vs": "vs",
  "coachTeams.at": "en casa de",
  "coachTeams.upcoming": "próximo",
  "squad.edit": "Editar",
  "squad.editPlayer": "Editar jugador",
  "squad.editName": "Nombre",
  "squad.editJersey": "Dorsal",
  "squad.editPosition": "Posición",
  "squad.editSave": "Guardar",
  "squad.editCancel": "Cancelar",
  "squad.editFailed": "No se pudieron guardar los cambios.",
  "squad.editNameRequired": "El jugador necesita un nombre.",
  "squad.editJerseyRange": "El dorsal debe estar entre 1 y 99.",
  "squad.editConnectedHint": "Este jugador está vinculado a una cuenta de Draffte — las actualizaciones de su propio perfil pueden sobrescribir estos campos.",
  "squad.editJerseyKeep": "Si lo dejas vacío se mantiene el #{n}.",
  "coachTeams.accessTitle": "Entrenadores con acceso",
  "coachTeams.revoke": "Revocar",
  "coachTeams.roleOwnerYou": "Propietario — tú",
  "coachTeams.makeOwner": "Hacer propietario",
  "coachTeams.makeOwnerTip": "Transferir la propiedad de este equipo a este entrenador",
  "coachTeams.transferTitle": "Transferir la propiedad del equipo",
  "coachTeams.transferBody": "{coach} pasará a ser el propietario de {team}. Tú quedarás como observador de solo lectura: ya no podrás editar la plantilla, los partidos ni los códigos para compartir.",
  "coachTeams.transferWarning": "No podrás deshacer esta acción. Solo el nuevo propietario podría devolverte el equipo.",
  "coachTeams.transferTypeToConfirm": "Escribe {team} para confirmar",
  "coachTeams.transferConfirm": "Transferir propiedad",
  "coachTeams.cancel": "Cancelar",
  "coachTeams.transferFailed": "No se pudo transferir la propiedad. Inténtalo de nuevo en un momento.",
  "coachTeams.viewerBannerTitle": "Vista de solo lectura.",
  "coachTeams.viewerBannerBody": "Estás viendo {team}, un equipo que otro entrenador compartió contigo. Todo lo que aparece en esta página pertenece a ese equipo — aquí no se muestra ningún dato de tu propio equipo y nada se puede editar.",
  "coachTeams.viewStats": "Ver estadísticas del partido",
  "nav.scouting": "Ojeo",
  "nav.aiAnalysis": "Análisis IA",
  "nav.coach": "Entrenador",
  "nav.signOut": "Cerrar sesión",
  "theme.light": "Modo claro",
  "theme.dark": "Modo oscuro",
  "theme.toLight": "Cambiar a modo claro",
  "theme.toDark": "Cambiar a modo oscuro",
  "lang.toggle": "Cambiar idioma",
  "lang.toSpanish": "Cambiar a Español",
  "lang.toEnglish": "Switch to English",

  // ── Dashboard ──
  "dash.welcomeBack": "Bienvenido de nuevo,",
  "dash.seasonOverview": "Resumen de la temporada",
  "dash.yourTeam": "Tu equipo",
  "dash.squad": "Plantilla",
  "dash.activePlayers": "Jugadores activos",
  "dash.average": "Media",
  "dash.teamAverage": "Media del equipo",
  "dash.sessionsStat": "Entrenamientos",
  "dash.thisWeek": "Esta semana",
  "dash.topPerformers": "Mejores jugadores",
  "dash.thisSeason": "Esta temporada",
  "dash.addPlayersTop": "Añade jugadores para ver los mejores",
  "dash.formAlert": "Alerta de forma:",
  "dash.formAlertBody": "— por debajo del rendimiento esperado en la última sesión. Considera ajustar la carga esta semana.",
  "dash.welcomeCoach": "¡Bienvenido, entrenador!",
  "dash.setupBody": "Configura tu equipo para empezar con la gestión de la plantilla, el seguimiento de partidos y las analíticas.",
  "dash.setupTeam": "Configurar equipo",
  "dash.squadRoster": "Plantilla del equipo",
  "dash.playersSortedOvr": "jugadores · ordenados por MED",
  "dash.noPlayersYet": "Aún no hay jugadores añadidos.",
  "dash.noPlayersFilter": "Ningún jugador coincide con este filtro.",
  "dash.addPlayers": "Añadir jugadores",
  "dash.teamPerformance": "Rendimiento del equipo",
  "dash.squadAttrAverages": "Medias de atributos de la plantilla",
  "dash.yourTeamLegend": "Tu equipo",
  "dash.radarEmpty": "Las medias de atributos aparecerán cuando tus jugadores sean valorados a partir del vídeo de los partidos.",
  "dash.recentSessions": "Entrenamientos recientes",
  "dash.trainingLoadWeek": "Carga de entrenamiento esta semana",
  "dash.noSessionsYet": "Aún no hay entrenamientos registrados.",
  "dash.upcomingMatches": "Próximos partidos",
  "dash.nextFixtures": "Próximos 3 encuentros",
  "dash.noUpcoming": "No hay próximos partidos programados.",
  "dash.home": "LOCAL",
  "dash.away": "VISITANTE",
  "dash.kickoffTbc": "Hora por confirmar",
  "dash.aiInsight": "Análisis IA",
  "dash.new": "Nuevo",
  "dash.fullAnalysis": "Análisis completo",
  "dash.aiEmpty": "Los análisis de IA aparecerán cuando tus partidos hayan sido procesados.",
  "dash.aiFormLine": "Tus últimos {n} partidos: {w}V-{d}E-{l}D ({gf} GF / {ga} GC).",
  "dash.aiFormLineOne": "Tu último partido: {w}V-{d}E-{l}D ({gf} GF / {ga} GC).",
  "dash.aiAttrLine": "{strong} ({strongVal}) es tu mayor fortaleza, mientras que {weak} ({weakVal}) es donde hay más margen de mejora.",
  "dash.aiTopLine": "{p1} ({o1}) y {p2} ({o2}) son tus jugadores mejor valorados.",
  "dash.aiTopOneLine": "{p1} ({o1}) es tu jugador mejor valorado.",
  "dash.aiDemoAnd": "y",
  "dash.aiDemoMid": "son tus dos mediocampistas de mejor rendimiento esta semana. Su nivel de trabajo combinado",
  "dash.aiDemoTail": "está muy por encima de la media de tu plantilla — alinearlos como doble pivote el domingo ayudaría a absorber la presión de Rival FC y a dominar el ritmo del segundo tiempo.",
  "dash.aiTagPressing": "Resistencia a la presión",
  "dash.aiTagSetPiece": "Envío a balón parado",
  "dash.aiTagRuns": "Carreras de alta intensidad",
  "dash.aiTagTempo": "Control del ritmo",
  "dash.activeSeason": "Temporada activa",
  "dash.manOfMatch": "Jugador del partido",
  "dash.noLicence": "Sin licencia",

  // ── Attribute labels ──
  "attr.speed": "Velocidad",
  "attr.finishing": "Definición",
  "attr.playmaking": "Creación",
  "attr.workRate": "Trabajo",
  "attr.defense": "Defensa",
  "attr.strength": "Fuerza",

  // ── Match detail / stats ──
  "stats.backToMatches": "Volver a partidos",
  "stats.playerInvolvementAi": "IA de participación del jugador",
  "stats.exportPdf": "Exportar PDF",

  // ── Informe oficial del partido (Exportar PDF) ──
  "report.back": "Volver",
  "report.saveAsPdf": "Guardar como PDF",
  "report.loading": "Generando el informe del partido…",
  "report.loadFailed": "No se pudo cargar este partido.",
  "report.notReady": "Informe aún no disponible",
  "report.notReadyBody": "Un informe necesita un resultado confirmado y una planilla etiquetada. A este partido le falta:",
  "report.blocker.no_result": "Todavía no se ha generado ningún análisis de este partido.",
  "report.blocker.no_player_stats": "Sin estadísticas por jugador — el partido no ha sido etiquetado.",
  "report.blocker.no_team_stats": "Sin estadísticas del equipo.",
  "report.blocker.no_score": "El marcador final no ha sido confirmado.",
  "report.sheetSummary": "Resumen del partido",
  "report.sheetLineups": "Alineaciones",
  "report.sheetTimeline": "Sucesos del partido",
  "report.sheetTeamStats": "Estadísticas del equipo",
  "report.sheetPassing": "Pases",
  "report.sheetAttacking": "Ataque",
  "report.sheetTerritory": "Territorio",
  "report.sheetPlayers": "Estadísticas por jugador",
  "report.sheetMoments": "Momentos clave",
  "report.sheetMethod": "Método y cobertura",
  "report.date": "Fecha",
  "report.competition": "Competición",
  "report.venue": "Condición",
  "report.home": "Local",
  "report.away": "Visitante",
  "report.formation": "Formación",
  "report.fullTime": "Final",
  "report.matchFacts": "Datos del partido",
  "report.xgTotal": "Goles esperados totales (xG)",
  "report.shotsRecorded": "remates registrados",
  "report.teamSheet": "Planilla",
  "report.startingXI": "Once inicial",
  "report.substitutes": "Suplentes",
  "report.squad": "Plantel — once inicial no registrado",
  "report.player": "Jugador",
  "report.pos": "Pos",
  "report.min": "Min",
  "report.rating": "Nota",
  "report.matchEvents": "Sucesos del partido",
  "report.event": "Suceso",
  "report.team": "Equipo",
  "report.evGoal": "Gol",
  "report.evShot": "Remate",
  "report.evShotOn": "Remate a puerta",
  "report.evYellow": "Tarjeta amarilla",
  "report.evRed": "Tarjeta roja",
  "report.evSub": "Cambio",
  "report.evOther": "Suceso",
  "report.teamComparison": "Comparación de equipos",
  "report.possession": "Posesión",
  "report.shots": "Remates",
  "report.onTarget": "A puerta",
  "report.offTarget": "Fuera",
  "report.opponent": "Rival",
  "report.passes": "Pases",
  "report.passesCompleted": "Pases completados",
  "report.passAccuracy": "Precisión de pase",
  "report.keyPasses": "Pases clave",
  "report.recoveries": "Recuperaciones",
  "report.turnovers": "Pérdidas",
  "report.corners": "Córners",
  "report.freeKicks": "Tiros libres",
  "report.fouls": "Faltas",
  "report.offsides": "Fueras de juego",
  "report.fieldTilt": "Dominio territorial",
  "report.passDistribution": "Distribución de pases",
  "report.byDirection": "Por dirección",
  "report.byLength": "Por longitud",
  "report.byThird": "Por zona",
  "report.forward": "Hacia adelante",
  "report.sideways": "Lateral",
  "report.backward": "Hacia atrás",
  "report.short": "Corto",
  "report.medium": "Medio",
  "report.long": "Largo",
  "report.defThird": "Zona defensiva",
  "report.midThird": "Zona media",
  "report.finalThird": "Zona de ataque",
  "report.completed": "Comp",
  "report.attempted": "Int",
  "report.passNetwork": "Red de pases",
  "report.topCombinations": "Principales combinaciones de pase",
  "report.combination": "Combinación",
  "report.shotMap": "Mapa de remates",
  "report.distance": "Dist",
  "report.goal": "Gol",
  "report.moreShots": "remates más no listados",
  "report.momentum": "Momentum",
  "report.us": "NOS",
  "report.them": "ELLOS",
  "report.possessionZones": "Posesión por zona",
  "report.attackingDirection": "Dirección de ataque",
  "report.playerStatistics": "Estadísticas por jugador",
  "report.touches": "Toq",
  "report.goals": "G",
  "report.assists": "A",
  "report.passesShort": "Pases",
  "report.keyPassesShort": "PC",
  "report.recoveriesShort": "Rec",
  "report.turnoversShort": "Pér",
  "report.duelsShort": "Duelos",
  "report.offDuelsShort": "DuO",
  "report.defDuelsShort": "DuDG",
  "report.duelsFootnote": "DuO = duelos ofensivos disputados · DuDG = duelos defensivos ganados. Incluyen duelos estimados según quién tenía el balón, así que no hay porcentaje.",
  "report.keyMoments": "Momentos clave",
  "report.moment": "Momento",
  "report.ofMoments": "más significativos de",
  "report.howThisWasMeasured": "Cómo se midió",
  "report.methodIntro": "Cada número de este informe proviene de este partido. Los sucesos se etiquetaron desde el video grabado y las posiciones se ubicaron con el seguimiento del balón. Cuando una cifra es una estimación o cubre solo parte del partido, se indica en la hoja correspondiente — y lo que no se midió aparece como un guion, nunca como cero.",
  "report.methodSource": "Fuente",
  "report.methodSourceVal": "Video del partido etiquetado",
  "report.methodEvents": "Sucesos etiquetados",
  "report.methodPasses": "Pases ubicados en el campo",
  "report.methodPlaced": "ubicados",
  "report.methodLengths": "Longitud de pases",
  "report.methodEstimated": "Estimada sobre un campo nominal (sin calibración de campo)",
  "report.methodMetric": "Métrica (campo calibrado)",
  "report.methodMinutes": "Minutos jugados",
  "report.methodMinutesOk": "Calculados desde el reloj del partido, el once y los cambios",
  "report.methodMinutesPartial": "Parciales — el reloj del partido no se marcó por completo",
  "report.methodClock": "Marcas de reloj",
  "report.methodOpponent": "Detalle del rival",
  "report.methodOppPlayers": "Por jugador",
  "report.methodOppTeam": "Solo a nivel de equipo",
  "report.limitations": "Limitaciones",
  "report.limitXg": "Los goles esperados (xG) son un modelo basado en la distancia. No tiene en cuenta el ángulo, la posición del portero ni la presión defensiva.",
  "report.limitEstimated": "Las longitudes de pase y las zonas son estimaciones: esta sede no tiene calibración de campo, por lo que las distancias se miden sobre un tamaño de campo nominal.",
  "report.limitOpponent": "El rival se etiquetó como equipo, no jugador por jugador, así que su planilla tiene menos cifras por diseño.",
  "report.limitAbsent": "Un guion (—) significa que la cifra no se midió en este partido. No significa cero.",
  "report.generated": "Generado",
  "report.note.estimated_lengths": "Longitudes estimadas — sin calibración de campo",
  "report.note.opponent_team_level": "Rival etiquetado a nivel de equipo",
  "report.note.opponent_missing": "Sin estadísticas del rival registradas",
  "report.note.placed_only": "Muestra solo los pases que el seguimiento del balón pudo ubicar",
  "report.note.unrated": "Este plantel no tiene notas para este partido",
  "report.note.xg_is_distance_model": "xG es una estimación basada en la distancia",
  "report.note.formation_unplotted": "Posiciones de formación no representadas",

  "stats.shareLink": "Compartir enlace",
  "stats.exportCsv": "Exportar CSV",
  "stats.linkCopied": "¡Enlace copiado al portapapeles!",
  "stats.comingSoon": "— ¡próximamente!",
  "stats.home": "Local",
  "stats.away": "Visitante",
  "stats.loadingMatch": "Cargando datos del partido...",
  "tag.minutesWarnTitle": "Los minutos no se calcularán hasta corregir esto",
  "tag.minutesWarnSubs": "{n} cambio(s) no tienen registrado al jugador que salió — vuelve a etiquetarlos para que se puedan calcular los minutos.",
  "tag.minutesWarnClock": "Etiqueta el saque inicial, el entretiempo y el final del partido para que se puedan calcular los minutos.",
  "tag.minutesWarnXI": "Define los titulares para que se puedan calcular los minutos.",
  "tag.minutesWarnGeneric": "No se pueden calcular los minutos con las etiquetas actuales — revisa las marcas de reloj, el once inicial y los cambios.",
  "tag.minutesWarnDismiss": "Descartar",
  "stats.gameVideo": "Vídeo del partido",
  "stats.gameVideoDownload": "Descargar el vídeo completo ↓",
  "stats.videoSourceTip": "Fuente de vídeo — el seguimiento 1080p o la panorámica; los saltos a eventos se mantienen sincronizados en ambas",
  "stats.fullscreen": "Pantalla completa",
  "stats.analysisRunning": "El análisis de vídeo sigue en curso — estas estadísticas son definitivas; los mapas de calor por jugador y el mapa de posiciones aún pueden completarse.",
  "stats.processingTitle": "Procesando vídeo del partido",
  "stats.processingBody": "Esto puede tardar unos minutos. La página se actualizará automáticamente.",
  "stats.failedTitle": "El análisis no se completó",
  "stats.failedBody": "Algo salió mal al procesar este partido. Nuestro equipo ya fue notificado — inténtalo de nuevo en un momento.",
  "stats.loadFailedTitle": "No se pudo cargar este partido",
  "stats.loadFailedBody": "Hubo un problema al cargar las estadísticas de este partido. Actualiza la página o inténtalo de nuevo en un momento.",
  "stats.autoScore": "Marcador detectado automáticamente:",
  "stats.confirmOrCorrect": "Por favor confirma o corrige",
  "stats.confirmScore": "Confirmar marcador",
  "stats.editLater": "Editar más tarde",
  "stats.scoreConfirmed": "¡Marcador confirmado!",
  "stats.couldNotConfirm": "No se pudo confirmar — inténtalo de nuevo",
  "stats.noteSaved": "Nota guardada",
  "stats.fullMatch": "Partido completo",

  // Penalty shootout
  "stats.shootout": "Tanda de penales",
  "stats.shootoutWon": "ganó en los penales",
  "stats.shootoutScored": "Anotó",
  "stats.shootoutMissed": "Falló",
  "stats.shootoutPens": "pen.",
  "stats.noLineup": "No se guardó una alineación titular para este partido. Defínela en Alineación el día del partido y aparecerá aquí.",

  // Main tabs
  "stats.tab.team": "Estadísticas de equipo",
  "stats.tab.player": "Estadísticas de jugador",
  "stats.tab.video": "Vídeo del partido",
  "stats.tab.insights": "Análisis de datos",
  "stats.tab.heatmaps": "Mapas de calor",
  "stats.tab.formation": "Formación",
  "stats.tab.momentum": "Momentum",
  "stats.tab.highlights": "Highlights",
  "hl.moments": "momentos",
  "hl.good": "buenas",
  "hl.bad": "a mejorar",
  "hl.rosterOnly": "sin alineación etiquetada — nombres según plantilla",
  "hl.reel": "Resumen del partido",
  "hl.reelSub": "los mejores momentos, en orden de juego",
  "hl.playAll": "Ver resumen",
  "hl.stop": "Detener",
  "hl.srcFollow": "Cámara IA",
  "hl.srcWide": "Vista amplia",
  "hl.teamMoment": "Momento del equipo",
  "hl.timeline": "Línea de tiempo",
  "hl.timelineSub": "todos los momentos del partido",
  "hl.clickToPlay": "toca un punto para verlo",
  "hl.byPlayer": "Por jugador",
  "hl.allPlayers": "Todos",
  "hl.pickPlayer": "Elige un jugador para dividir sus momentos en buenos y a mejorar.",
  "hl.pane.good": "Buenos momentos",
  "hl.pane.improve": "A mejorar",
  "hl.pane.none": "Nada aquí en este partido.",
  "hl.playingMoment": "Momento",
  "hl.detail.counter": "contraataque",
  "hl.detail.ledToShot": "costó un tiro",
  "hl.empty.title": "Aún no hay highlights",
  "hl.empty.body": "El estudio aún no ha etiquetado este partido. Los highlights aparecen automáticamente cuando lo esté.",
  "hl.type.goal": "Gol",
  "hl.type.assist": "Asistencia",
  "hl.type.shot_on_target": "Tiro al arco",
  "hl.type.shot_off": "Tiro desviado",
  "hl.type.shot_blocked": "Tiro bloqueado",
  "hl.type.key_pass": "Pase clave",
  "hl.type.recovery": "Recuperación",
  "hl.type.turnover": "Pérdida",
  "hl.type.turnover_punished": "Pérdida castigada",
  "hl.type.save_moment": "Atajada",
  "hl.type.chance_against": "Ocasión rival",
  "hl.type.foul": "Falta",
  "hl.type.yellow_card": "Tarjeta amarilla",
  "hl.type.red_card": "Tarjeta roja",
  "hl.type.moment": "Momento",

  // Stat group tabs
  "stats.group.summary": "Resumen",
  "stats.group.distribution": "Distribución",
  "stats.group.offensive": "Ofensiva",
  "stats.group.defensive": "Defensiva",
  "stats.group.goalkeeper": "Portero",
  "stats.group.sprint": "Sprint/Distancia",
  "stats.group.ranking": "Ranking",
  "ranking.metric.rating": "Valoración",
  "ranking.metric.passes": "Pases",
  "ranking.metric.recoveries": "Recuperaciones",
  "ranking.metric.duels": "Duelos",
  "ranking.mvp": "MVP",
  "ranking.jointMvp": "MVP compartido",
  "ranking.joint": "Empate en #{rank}",
  "ranking.notMeasured": "Sin medir",
  "ranking.fullList": "Rendimiento del equipo",
  "ranking.passHint": "Ordenado por pases completados.",
  "ranking.duelHint": "Ordenado por duelos ganados: disputas por el balón estimadas según quién se quedó con él, más regates y entradas etiquetados.",
  "ranking.empty": "El ranking está en camino",
  "ranking.emptyBody": "Los jugadores aparecerán aquí cuando este partido tenga valoraciones o estadísticas etiquetadas.",
  "ranking.metricEmpty": "Aún no hay datos de {metric}",
  "ranking.metricEmptyBody": "Elige otra estadística para ver los datos disponibles de este partido.",
  "ranking.source": "El mismo ranking que tus jugadores ven en la app, hecho con las valoraciones y estadísticas registradas de este partido. Los valores iguales comparten posición.",

  // Summary stats
  "stat.goals": "Goles",
  "stat.possessions": "Posesión",
  "stat.shotsTotal": "Tiros totales",
  "stat.fouls": "Faltas",
  "stat.offsides": "Fueras de juego",
  "stat.freeKicks": "Tiros libres",
  "stat.corners": "Córners",
  "stat.avgRating": "Valoración media de los jugadores",
  // Offensive
  "stat.shotsOn": "Tiros a puerta",
  "stat.shotsOff": "Tiros fuera",
  "stat.shotsBlocked": "Tiros bloqueados",
  "stat.shotsInsidePA": "Tiros dentro del área",
  "stat.shotsOutsidePA": "Tiros fuera del área",
  "stat.successfulTakeOns": "Regates exitosos",
  "stat.allTakeOns": "Regates totales",
  "stat.shotConversionRate": "Tasa de conversión de tiros",
  // Defensive
  "stat.tacklesWon": "Entradas ganadas",
  "stat.allTackles": "Entradas totales",
  "stat.interceptions": "Intercepciones",
  "stat.recoveries": "Recuperaciones",
  "stat.turnovers": "Balones perdidos",
  "stat.clearances": "Despejes",
  "stat.blocks": "Bloqueos",
  "stat.aerialsWon": "Duelos aéreos ganados",
  "stat.allAerials": "Duelos aéreos totales",
  "stat.duelsWon": "Duelos ganados",
  "stat.allDuels": "Duelos totales",
  // Distribution
  "stat.successfulPasses": "Pases completados",
  "stat.passes": "Pases",
  "stat.passAccuracy": "Precisión de pase",
  "stat.keyPasses": "Pases clave",
  "stat.successfulPassesFinal": "Pases completados en el último tercio",
  "stat.allPassesFinal": "Pases totales en el último tercio",
  "stat.successfulPassesMid": "Pases completados en el tercio medio",
  "stat.allPassesMid": "Pases totales en el tercio medio",
  "stat.successfulPassesDef": "Pases completados en el tercio defensivo",
  "stat.allPassesDef": "Pases totales en el tercio defensivo",
  "stat.successfulLong": "Pases largos completados",
  "stat.allLong": "Pases largos totales",
  "stat.successfulMedium": "Pases medios completados",
  "stat.allMedium": "Pases medios totales",
  "stat.successfulShort": "Pases cortos completados",
  "stat.allShort": "Pases cortos totales",
  "stat.successfulForward": "Pases hacia adelante completados",
  "stat.allForward": "Pases hacia adelante totales",
  "stat.successfulSideways": "Pases laterales completados",
  "stat.allSideways": "Pases laterales totales",
  "stat.successfulBackward": "Pases hacia atrás completados",
  "stat.allBackward": "Pases hacia atrás totales",
  "stat.successfulCrosses": "Centros completados",
  "stat.allCrosses": "Centros totales",
  "stat.fieldTilt": "Dominio territorial",
  "stats.keyPassesLabel": "pases clave",

  // Pass network
  "pn.network": "Red",
  "pn.pass": "Pase",
  "pn.passesMadeSuffix": "pases dados",
  "pn.passesReceivedSuffix": "pases recibidos",
  "pn.tapPlayerHint": "Toca cualquier jugador para ver cada pase que dio — o recibió",
  "pn.tapPassWent": "Toca un pase abajo para trazar a dónde fue el balón",
  "pn.tapPassCame": "Toca un pase abajo para trazar de dónde vino el balón",
  "pn.attacking": "Ataque",
  "pn.middle": "Medio",
  "pn.defensive": "Defensa",
  "pn.thirdWord": "tercio",
  "pn.playerInvolvement": "Jugador (tamaño = participación)",
  "pn.connectionVolume": "Conexión (grosor = volumen)",
  "pn.forward": "Adelante",
  "pn.sideways": "Lateral",
  "pn.backward": "Atrás",
  "pn.passDetail": "Detalle del pase",
  "pn.completed": "Completado",
  "pn.lost": "Perdido",
  "pn.direction": "Dirección",
  "pn.length": "Distancia",
  "pn.into": "Hacia",
  "pn.lane": "Carril",
  "pn.minute": "Minuto",
  "pn.outcome": "Resultado",
  "pn.retained": "Conservado",
  "pn.turnover": "Pérdida",
  "pn.left": "Izquierda",
  "pn.central": "Central",
  "pn.right": "Derecha",
  "pn.prev": "‹ Anterior",
  "pn.backToList": "Volver a la lista",
  "pn.next": "Siguiente ›",
  "pn.backToOverview": "Volver al resumen del equipo",
  "pn.passesMade": "Pases dados",
  "pn.passesReceived": "Pases recibidos",
  "pn.passesMadeBy": "pases dados",
  "pn.passesReceivedBy": "pases recibidos",
  "pn.received": "Recibidos",
  "pn.passesWord": "Pases",
  "pn.avgLength": "Distancia media",
  "pn.dirIntoHim": "Dirección de los balones hacia él",
  "pn.dirOfPlay": "Dirección de juego",
  "pn.everyPassMade": "Cada pase dado",
  "pn.everyPassReceived": "Cada pase recibido",
  "pn.all": "Todos",
  "pn.noPassesMade": "No hay pases dados en este filtro",
  "pn.noPassesReceived": "No hay pases recibidos en este filtro",
  "pn.aiRead": "Lectura IA",
  "pn.totalPasses": "Pases totales",
  "pn.completion": "Completados",
  "pn.progressive": "Progresivos",
  "pn.intoEachThird": "Hacia cada tercio",
  "pn.passLength": "Distancia de pase",
  "pn.short": "Cortos",
  "pn.medium": "Medios",
  "pn.long": "Largos",
  "pn.topCombos": "Mejores combinaciones",
  "pn.mostMade": "Más pases dados",
  "pn.mostReceived": "Más pases recibidos",
  "pn.networkRead": "Lectura de la red",

  // Shot map
  "sm.both": "Ambos",
  "sm.allShots": "Todos los tiros",
  "sm.full": "Completo",
  "sm.goal": "Gol",
  "sm.onTarget": "A puerta",
  "sm.offTarget": "Fuera",
  "sm.blocked": "Bloqueado",
  "sm.unplaced": "{n} remates fuera del mapa — están etiquetados, pero el seguimiento del balón no tiene su posición, así que no llevan xG:",
  "sm.unplacedOne": "1 remate fuera del mapa — está etiquetado, pero el seguimiento del balón no tiene su posición, así que no lleva xG:",
  "sm.attack": "ataca",
  "sm.sizeXgTap": "tamaño = xG · toca un tiro",
  "sm.shotDetail": "Detalle del tiro",
  "sm.distance": "Distancia",
  "sm.half": "Tiempo",
  "sm.first": "Primero",
  "sm.second": "Segundo",
  "sm.quality": "Calidad",
  "sm.bigChance": "Ocasión clara",
  "sm.decent": "Decente",
  "sm.halfChance": "Media ocasión",
  "sm.backToShotMap": "Volver al mapa de tiros",
  "sm.shots": "Tiros",
  "sm.on": "A puerta",
  "sm.goals": "Goles",
  "sm.conversion": "conversión",
  "sm.xgRace": "Carrera de xG (acumulada)",
  "sm.goalMarker": "● = gol",
  "sm.shotOutcomes": "Resultados de los tiros (ambos equipos)",
  "sm.topShooters": "Máximos tiradores (por xG)",
  "sm.shotRead": "Lectura de tiros",
  "sm.showingShots": "Mostrando tiros de",
  "sm.shotsClear": "— quitar",

  // Viz pending
  "viz.generatingFromVideo": "— generando desde el vídeo del partido",
  "viz.generatingBody": "La cámara IA de Draffte está procesando este partido para situar cada tiro, pase y toque en su posición real sobre el campo. Esta visualización se desbloquea automáticamente cuando se completa el análisis de visión por computadora.",
  "viz.aiProcessing": "Procesando con IA",

  // Analyst chat
  "analyst.askAnalyst": "Pregunta al analista",
  "analyst.grounded": "respuestas basadas en los datos medidos de este partido",
  "analyst.placeholder": "Pregunta lo que quieras sobre este partido…",
  "analyst.unavailable": "El analista no está disponible ahora mismo — inténtalo de nuevo en un momento.",
  "analyst.q1": "¿Dónde perdimos más el balón?",
  "analyst.q2": "¿Quién fue nuestro jugador más peligroso?",
  "analyst.q3": "¿En qué deberíamos centrarnos en el entrenamiento esta semana?",
  "reel.aiHighlight": "Resumen de jugadas IA",
  "reel.cvHeatmap": "Mapa de calor de posición (visión)",

  // H2H summary
  "h2h.played": "Jugados",
  "h2h.wins": "Victorias",
  "h2h.draws": "Empates",
  "h2h.losses": "Derrotas",
  "h2h.win": "VICT",
  "h2h.draw": "EMP",
  "h2h.loss": "DER",
  "h2h.headToHead": "Cara a cara vs",
  "h2h.pending": "Aún no hay enfrentamientos previos registrados con este rival — el historial se construye a medida que juegan.",
  "h2h.goalsFor": "GF",
  "h2h.goalsAgainst": "GC",
  "h2h.homeShort": "L",
  "h2h.awayShort": "V",

  // Player-table column glossary (header tooltips)
  "col.shirtNumber.name": "Dorsal",
  "col.shirtNumber.desc": "El número de camiseta del jugador",
  "col.player.name": "Jugador",
  "col.player.desc": "Nombre del jugador",
  "col.position.name": "Posición",
  "col.position.desc": "Posición que ocupa en el campo",
  "col.rating.name": "Valoración del partido",
  "col.rating.desc": "Puntuación global del rendimiento sobre 10",
  "col.minutes.name": "Minutos jugados",
  "col.minutes.desc": "Minutos totales en el campo",
  "col.goals.name": "Goles",
  "col.goals.desc": "Goles marcados",
  "col.assists.name": "Asistencias",
  "col.assists.desc": "Pases que llevaron directamente a un gol",
  "col.shots.name": "Tiros",
  "col.shots.desc": "Intentos de tiro totales",
  "col.keyPasses.name": "Pases clave",
  "col.keyPasses.desc": "Pases que crearon una ocasión de tiro",
  "col.fouls.name": "Faltas",
  "col.fouls.desc": "Faltas cometidas",
  "col.yellowCards.name": "Tarjetas amarillas",
  "col.yellowCards.desc": "Amonestaciones recibidas",
  "col.redCards.name": "Tarjetas rojas",
  "col.redCards.desc": "Expulsiones recibidas",
  "col.shotsOnTarget.name": "Tiros a puerta",
  "col.shotsOnTarget.desc": "Tiros que fueron a puerta",
  "col.offDuels.tag": "Desp.",
  "col.offDuels.name": "Desposeído (duelos ofensivos perdidos)",
  "col.offDuels.desc": "Tenía el balón y un rival se lo quitó (estimado del partido, más regates perdidos etiquetados). Los duelos que ganó conservando el balón solo cuentan si se etiquetaron, así que no se muestra un porcentaje",
  "col.defDuels.tag": "Duelos def.",
  "col.defDuels.name": "Duelos defensivos ganados",
  "col.defDuels.desc": "El rival tenía el balón y él lo disputó: veces que lo recuperó (estimado, más entradas etiquetadas). Los perdidos no se muestran: un defensor superado no deja etiqueta",
  "col.dribbles.name": "Regates",
  "col.dribbles.desc": "Regates: ganados / intentados (etiquetados)",
  "col.tackles.name": "Entradas",
  "col.tackles.desc": "Entradas: ganadas / intentadas (etiquetadas)",
  "col.interceptions.name": "Intercepciones",
  "col.interceptions.desc": "Pases del rival cortados",
  "col.clearances.name": "Despejes",
  "col.clearances.desc": "Despejes defensivos del balón",
  "col.blocks.name": "Bloqueos",
  "col.blocks.desc": "Tiros o centros bloqueados",
  "col.passesCompleted.name": "Pases completados",
  "col.passesCompleted.desc": "Pases que llegaron a un compañero",
  "col.passesAttempted.name": "Pases intentados",
  "col.passesAttempted.desc": "Pases intentados en total",
  "col.passAccuracy.name": "Precisión de pase",
  "col.passAccuracy.desc": "Proporción de pases completados",
  "col.crossesCompleted.name": "Centros completados",
  "col.crossesCompleted.desc": "Centros que encontraron a un compañero",
  "col.crossesAttempted.name": "Centros intentados",
  "col.crossesAttempted.desc": "Centros intentados en total",
  "col.goalsConceded.name": "Goles encajados",
  "col.goalsConceded.desc": "Goles encajados estando en el campo",
  "col.catches.name": "Atrapadas",
  "col.catches.desc": "Centros/tiros atrapados con seguridad",
  "col.parries.name": "Rechaces / Bloqueos",
  "col.parries.desc": "Tiros desviados al córner o devueltos a un rival",
  "col.gkFaced.name": "Tiros a puerta recibidos",
  "col.gkFaced.desc": "Tiros a puerta del rival mientras este portero estaba en la portería",
  "col.saves.name": "Atajadas",
  "col.saves.desc": "Tiros a puerta que no fueron gol. Entre paréntesis: aquellos en los que se etiquetó el toque del propio portero",
  "col.savePct.name": "% de atajadas",
  "col.savePct.desc": "Atajadas en juego abierto sobre tiros a puerta en juego abierto (sin penales). Se muestra desde tres tiros recibidos",
  "col.cleanSheet.name": "Portería a cero",
  "col.cleanSheet.desc": "Sin goles recibidos durante la mayor parte de su tiempo en portería. Requiere el reloj del partido",
  "col.gkKicks.name": "Saques de puerta conservados",
  "col.gkKicks.desc": "Saques de puerta tras los que conservamos el balón, sobre los resueltos (conservados o perdidos)",
  "col.gkBallWins.name": "Balones ganados",
  "col.gkBallWins.desc": "Veces que ganó el balón directamente a un rival",
  "col.penalties.name": "Penales atajados / recibidos",
  "col.penalties.desc": "Penales atajados sobre penales recibidos. Un guion significa que no recibió ninguno",
  "col.gkMinutes.name": "Minutos en portería",
  "col.gkMinutes.desc": "Minutos en la portería. Un guion significa que no se etiquetó el reloj del partido",
  "col.saves.tag": "Ata",
  "col.gkFaced.tag": "Rec",
  "col.savePct.tag": "Ata%",
  "col.cleanSheet.tag": "P0",
  "col.gkKicks.tag": "SP",
  "col.gkBallWins.tag": "BG",
  "col.penalties.tag": "Pen",
  "stats.gkNotIdentified": "No se pudo identificar al portero de este partido, por eso no se muestran estadísticas de portero.",
  "stats.gkAmbiguous": "Había dos porteros en el once inicial y las etiquetas no dicen cuál estuvo en la portería, así que no se acredita a ninguno.",
  "stats.gkNoClock": "No se etiquetó el reloj del partido: los minutos en portería y las porterías a cero no están disponibles.",
  "stats.gkFootnote": "Las atajadas son tiros a puerta que no fueron gol; el número entre paréntesis indica cuántas llevaron el toque del propio portero. El % de atajadas es solo en juego abierto. Un guion significa que no se mide. Los centros atrapados, los puñetazos, las salidas y los mano a mano aún no se miden.",
  "games.shotsFaced": "Tiros recibidos",
  "games.savePct": "% atajadas",
  "games.cleanSheet": "Portería a cero",
  "games.yes": "Sí",
  "games.no": "No",
  "games.gkPensNote": "Penales recibidos: {saved} atajados de {faced}",
  "col.goalKicksSuccessful.name": "Saques de puerta acertados",
  "col.goalKicksSuccessful.desc": "Saques de puerta que llegaron a un compañero",
  "col.goalKicksAttempted.name": "Saques de puerta intentados",
  "col.goalKicksAttempted.desc": "Saques de puerta realizados en total",
  "col.gkIntSuccessful.name": "Intercepciones acertadas",
  "col.gkIntSuccessful.desc": "Intercepciones/salidas exitosas del portero",
  "col.gkIntAttempted.name": "Intercepciones intentadas",
  "col.gkIntAttempted.desc": "Intentos de intercepción del portero en total",

  // Video event types
  "ev.goal": "Gol",
  "ev.shot": "Tiro",
  "ev.shotOnTarget": "Tiro a puerta",
  "ev.yellowCard": "Tarjeta amarilla",
  "ev.redCard": "Tarjeta roja",
  "ev.substitution": "Sustitución",
  "ev.corner": "Córner",
  "ev.freeKick": "Tiro libre",
  "ev.keyPass": "Pase clave",
  "ev.tackle": "Entrada",
  "ev.save": "Parada",
  "ev.offside": "Fuera de juego",
  "ev.foul": "Falta",
  "ev.event": "Evento",

  // Stats — misc hardcoded strings
  "stats.halfTime": "DESC",
  "stats.reelAutoCut": "Recortado automáticamente por Draffte Vision a partir de la grabación completa del partido — momentos en los que el #{n} tuvo el balón.",
  "stats.thirdAttacking": "Ataque",
  "stats.thirdMiddle": "Medio",
  "stats.thirdDefensive": "Defensivo",
  "stats.thirdSuffix": "tercio",
  // Pass legend / pass explorer
  "stats.outgoing": "Salientes",
  "stats.incoming": "Entrantes",
  "stats.failed": "Fallados",
  "stats.arrowToReceiver": "La flecha apunta al receptor",
  "stats.eachColourConnection": "Cada color = una conexión (ver lista)",
  "stats.bandsThirds": "Franjas = % de pases hacia cada tercio",
  "stats.clickPlayerIsolate": "· toca un jugador para aislarlo",
  "stats.passesIntoThirdBody": "Proporción de pases completados que terminan en cada tercio del campo — dónde este equipo construyó y finalizó su juego.",
  "stats.completed": "Completados",
  "stats.received": "Recibidos",
  "stats.accuracy": "Precisión",
  "stats.connectionsSuffix": "Conexiones",
  "stats.allConnections": "Todas las conexiones",
  "stats.passesArrow": "pases →",
  "stats.colourPairVolume": "color = pareja · grosor = volumen",
  "stats.passProfile": "Perfil de pases",
  "stats.passProfileMain": "{player} completó {out} pases con {failed} fallados ({pct}% de precisión). ",
  "stats.passProfileBest": "Conexión más fiable: {to} ({pct}%). ",
  "stats.passProfileWorst": "Le costó llegar a {to} ({pct}% — {failed} fallados).",
  "stats.momentumHowTo": "Cómo leer esto:",
  "stats.momentumHowToBody": "cada barra es un minuto de juego. Las barras que suben sobre la línea son minutos en los que {home} dominó — con el balón arriba en la cancha y creando ocasiones; las que bajan son minutos en los que {away} tuvo el control. Cuanto más alta la barra, más claro el dominio. Un balón sobre un poste blanco marca un gol, del lado del equipo que lo anotó.",
  "stats.momentumGoal": "Gol",
  // Possession key-insight (generated prose)
  "stats.chanLeft": "izquierdo",
  "stats.chanCentral": "central",
  "stats.chanRight": "derecho",
  "stats.keyInsightBody": "El {topPct}% de la actividad con balón medida fue por el carril {topChan}",
  "stats.keyInsightImbalanced": " mientras que la banda {lowChan} solo vio el {lowPct}% — considera cambiar el juego de banda más a menudo",
  "stats.keyInsightBalanced": " — un reparto equilibrado por todo el campo",
  // Momentum AI story (generated prose)
  "stats.momStoryControlled": "{team} controló el ritmo general",
  "stats.momStoryBalanced": ", aunque se mantuvo muy igualado",
  "stats.momStoryHomeSpell": "El mejor momento de {home} llegó en torno al minuto {min}, ",
  "stats.momStoryHomeSustained": "con unos {mins} minutos de presión sostenida",
  "stats.momStoryHomeBursts": "a ráfagas",
  "stats.momStoryAwayPush": "{away} respondió con más fuerza en torno al minuto {min}",
  "stats.momStoryAwayClaim": ", reclamando unos {mins} minutos propios",
  "stats.momStoryXg": "Al final del partido el reparto de goles esperados fue de {hxg}–{axg}, ",
  "stats.momStoryXgHome": "reflejando el mayor peligro de {home}",
  "stats.momStoryXgAway": "favoreciendo las ocasiones de {away}",
  "stats.momStoryXgEven": "un duelo parejo",

  // Player table / video / insights / heatmaps
  "stats.noPlayerData": "Sin datos de jugadores",
  "stats.aiReel": "RESUMEN IA",
  "stats.matchVideoHere": "El vídeo del partido aparecerá aquí",
  "stats.possession": "Posesión",
  "stats.shots": "Tiros",
  "stats.eventsAll": "Todos",
  "stats.keyMoments": "Momentos clave",
  "stats.goalsFilter": "Goles",
  "stats.shotsFilter": "Tiros",
  "stats.cardsFilter": "Tarjetas",
  "stats.fullMatchFilter": "Partido completo",
  "stats.firstHalf": "1er tiempo",
  "stats.secondHalf": "2do tiempo",
  "stats.events": "Eventos",
  "stats.clickToJump": "Clic para saltar",
  "stats.play": "VER",
  "stats.opp": "RIV",
  "stats.aiMatchAnalysis": "Análisis IA del partido",
  "stats.generatedFromData": "Generado a partir de los datos del partido",
  "stats.strengths": "Fortalezas",
  "stats.areasToImprove": "Áreas de mejora",
  "stats.tacticalInsights": "Análisis táctico",
  "stats.insightsPending": "Los análisis de IA del partido aparecerán aquí una vez procesado el vídeo. Mientras tanto puedes preguntarle al analista arriba sobre este partido.",
  "stats.momentumPending": "La línea de momentum y xG se genera del análisis del vídeo — aparecerá aquí una vez procesado este partido.",
  "stats.recommendations": "Recomendaciones para el próximo partido",
  "stats.possessionZones": "Zonas de posesión",
  "shape.title": "Forma del equipo",
  "shape.caption": "Dónde estuvieron los jugadores de campo de {team} con el balón en juego, medido en el video del partido. Los dos tiempos están girados para que el equipo ataque hacia arriba.",
  "shape.attack": "ATAQUE ↑",
  "shape.measured": "Medido",
  "shape.thirds": "Tiempo por tercio",
  "shape.attThird": "Tercio ofensivo",
  "shape.midThird": "Tercio medio",
  "shape.defThird": "Tercio defensivo",
  "shape.lanes": "Tiempo por carril",
  "shape.left": "Izquierda",
  "shape.centre": "Centro",
  "shape.right": "Derecha",
  "shape.note": "En promedio seguimos a {n} de nuestros jugadores de campo a la vez durante {m} minutos de juego. El portero usa otro uniforme y no está incluido. Esto es el equipo, no jugadores individuales: lejos de la cámara vemos a los jugadores pero no podemos leer sus números.",
  "stats.shotMap": "Mapa de tiros",
  "stats.teamStats": "Estadísticas de equipo",
  "stats.passNetwork": "Red de pases",
  "stats.passZones": "Zonas",
  "stats.passPlayers": "Jugadores",
  "stats.estimated": "Estimado",
  "running.title": "Carrera (est.)",
  "running.none": "Aún no hay datos de carrera para este partido. Se miden con el video de nuestro equipo cuando el partido está terminado.",
  "running.distance": "Distancia (est.)",
  "running.perMin": "m/min",
  "running.topSpeed": "Velocidad máx.",
  "running.measured": "medido en {n} de {m} min",
  "running.measuredOnly": "medido en {n} min",
  "running.note": "Estimado con la cámara panorámica solo en parte del partido.",
  "running.careerTitle": "Carrera · {n} partidos medidos",
  "running.careerTitleOne": "Carrera · {n} partido medido",
  "running.avgDistance": "Distancia media / partido (est.)",
  "running.avgPerMin": "m/min medios (est.)",
  "running.bestTop": "Mejor velocidad máx. (est.)",
  "stats.passSplitsNote": "Tipos de pase estimados desde el video (el tagger solo registró totales)",
  "stats.needsVideo": "Estas estadísticas provienen del análisis de video — próximamente.",
  "stats.possessionHeatmap": "Mapa de calor de posesión",
  "stats.lowActivity": "Actividad baja",
  "stats.mediumActivity": "Media",
  "stats.highActivity": "Actividad alta",
  "stats.heatZonesCaption": "Actividad con balón por zona de la cancha — cada círculo es un área, no un jugador.",
  "stats.heatZonesLegend": "porcentaje de la actividad del equipo con balón",
  "stats.heatZonesLegendBoth": "porcentaje de toda la actividad con balón (ambos equipos)",
  "stats.heatZonesLegendTeam": "porcentaje de la actividad con balón de {team}",
  "stats.bothTeams": "Ambos equipos",
  "viz.notAvailable": "{feature} no está disponible para este partido",
  "viz.notAvailableBody": "Esta vista se construye a partir del video analizado del partido, y este partido no fue analizado.",
  "stats.hotspotBreakdown": "Desglose de zonas calientes",
  "stats.leftSide": "Banda izquierda",
  "stats.centralSide": "Centro",
  "stats.rightSide": "Banda derecha",
  "stats.keyInsight": "Dato clave",
  "stats.shotDetails": "Detalles de los tiros",
  "stats.noShotsFilter": "No hay tiros para este filtro.",
  "stats.totalXg": "xG total",
  "stats.conversion": "Conversión",
  "stats.avgDistance": "Distancia media",
  "stats.insideBox": "Dentro del área",
  "stats.selectPlayer": "Seleccionar jugador",
  "stats.allPlayers": "Todos los jugadores",
  "stats.passesIntoThird": "Pases hacia cada tercio",
  "stats.attackingThird": "Tercio de ataque",
  "stats.middleThird": "Tercio medio",
  "stats.defensiveThird": "Tercio defensivo",
  "stats.coachNotes": "Notas del entrenador",
  "stats.addNote": "Añadir una nota…",
  "stats.saveNote": "Guardar nota",
  "stats.savedNotes": "Notas guardadas",
  "stats.noNotes": "Aún no hay notas.",
  "stats.formationTitle": "Formación",
  "stats.momentumTitle": "Momentum del partido",
  "stats.homeMomentum": "Momentum local",
  "stats.momentumLegend": "Momentum",
  "stats.homeXg": "xG local",
  "stats.awayXg": "xG visitante",
  "stats.actualGoals": "Goles reales",
  "stats.aiReadMomentum": "Lectura IA: historia del momentum",
  "stats.notesSaved": "guardadas · almacenadas en este dispositivo",
  "stats.notesPlaceholder": "Añade tus notas tácticas, observaciones y tareas...",
  "stats.deleteNote": "Eliminar nota",

  // ── Matches list ──
  "matches.title": "Partidos",
  "matches.seasonSub": "temporada de Liga Regional",
  "matches.addGameCalendar": "Añadir calendario de partidos",
  "matches.addMatch": "Añadir partido",
  "matches.noMatchesYet": "Aún no hay partidos registrados",
  "matches.noMatchesBody": "Empieza a seguir el rendimiento de tu equipo añadiendo tu primer resultado o próximo partido.",
  "matches.played": "Jugados",
  "matches.points": "Puntos",
  "matches.won": "Ganados",
  "matches.drawn": "Empatados",
  "matches.lost": "Perdidos",
  "matches.goalDifference": "Diferencia de goles",
  "matches.seasonRecord": "Balance de la temporada",
  "matches.formLast5": "Forma — Últimos 5",
  "matches.mostRecentLeft": "El más reciente a la izquierda",
  "matches.upcomingFixtures": "Próximos partidos",
  "matches.results": "Resultados",
  "matches.noMatchesFilter": "Ningún partido coincide con este filtro",
  "matches.pointsProgression": "Progresión de puntos",
  "matches.matchEvents": "Eventos del partido",
  "matches.playerRatings": "Valoraciones de jugadores",
  "matches.coachNotes": "Notas del entrenador",
  "matches.fullGameRecording": "Grabación del partido completo",
  "matches.viewFullDetails": "Ver detalles completos",
  "matches.fullDetails": "Detalles completos",
  "matches.overview": "Resumen",
  "matches.editTeamName": "Editar nombre del equipo",
  "matches.saveTeamName": "Guardar nombre del equipo",
  "matches.watchGame": "Ver partido ▶",
  "matches.watchGameAfter": "Ver partido está disponible después de jugarse el encuentro.",
  "matches.all": "Todos",
  "matches.upcoming": "Próximos",
  "matches.win": "G",
  "matches.draw": "E",
  "matches.loss": "P",

  // ── Formation builder (upcoming-match lineup tab) ──
  "matches.buildFormation": "Arma tu formación",
  "formation.autoPick": "Auto-alinear",
  "formation.bench": "Suplentes",
  "formation.benchEmpty": "Todos están en la cancha.",
  "formation.captain": "Capitán",
  "formation.demoNote": "Modo demo — los cambios de alineación no se guardan.",
  "formation.emptySlotHint": "Toca una posición para elegir un jugador",
  "formation.loadError": "No se pudo cargar tu plantilla.",
  "formation.loading": "Cargando plantilla…",
  "formation.makeCaptain": "Hacer capitán",
  "formation.noPlayers": "Aún no hay jugadores en tu plantilla. Agrégalos primero en la página de Plantilla.",
  "formation.pickerTitle": "Elige un jugador",
  "formation.matchFormat": "Formato del partido",
  "formation.formatHelp": "Elige el formato y toca cada posición para seleccionar tu equipo titular.",
  "formation.backendUnavailable": "Este servidor aún no admite los formatos infantiles. Tu alineación actual está segura.",
  "formation.benchHelp": "Toca para elegir suplentes. Los jugadores sin seleccionar no están convocados.",
  "formation.reviewCallUp": "Revisar y avisar a jugadores",
  "formation.reviewChanged": "El equipo cambió. Cierra esta revisión y comprueba la alineación antes de avisar a los jugadores.",
  "formation.publishHelp": "Guardar mantiene un borrador. Los avisos se envían solo al confirmar el equipo completo.",
  "formation.publishUnavailable": "Este servidor aún no admite avisos de convocatoria.",
  "formation.confirmHelp": "Confirma tu equipo {format}: {selected} convocados y {others} sin convocar. Los jugadores con cuenta vinculada reciben su aviso individual en la app. Los jugadores sin cuenta de jugador vinculada no pueden recibirlo.",
  "formation.confirmPublish": "Confirmar y avisar",
  "formation.cancel": "Seguir editando",
  "formation.published": "Convocatoria publicada",
  "formation.alreadyPublished": "Esta convocatoria ya está publicada. No se enviaron avisos duplicados.",
  "formation.delivery": "{selected} avisos de convocatoria y {others} de no convocatoria entregados en la app. {unlinked} jugadores no tienen cuenta de jugador vinculada.",
  "formation.demoPublished": "Convocatoria demo revisada. No se enviaron avisos.",
  "formation.publishError": "No se pudo publicar la convocatoria. Tu alineación está guardada como borrador; inténtalo de nuevo.",
  "formation.wrongTeam": "Cambia al equipo de este partido antes de editar su alineación.",
  "formation.frozen": "Este partido ya comenzó. Su convocatoria original y formato están bloqueados.",
  "formation.placed": "{n} de {total} posiciones completas",
  "formation.setupLoadError": "No se pudo cargar la configuración. Inténtalo de nuevo.",
  "formation.setupSaveError": "No se pudieron guardar los dorsales. Tus cambios siguen aquí.",
  "formation.jerseyHelp": "Los dorsales corresponden a los titulares y suplentes seleccionados. Guarda la alineación primero y asigna un dorsal único a cada jugador.",
  "formation.jerseys": "Dorsales del partido",
  "formation.numbersMissing": "Cada jugador seleccionado necesita un dorsal único del 1 al 99.",
  "formation.continueUpload": "Continuar a subir el partido",
  "formation.remove": "Quitar",
  "formation.removeCaptain": "Quitar capitán",
  "formation.retry": "Reintentar",
  "formation.save": "Guardar alineación",
  "formation.saveError": "No se pudo guardar la alineación — tus cambios siguen aquí. Inténtalo de nuevo.",
  "formation.saved": "Alineación guardada",
  "formation.saving": "Guardando…",

  // ── Sessions (coach-home/sessions) ──
  "sessions.title": "Sesiones",
  "sessions.trainingLog": "Registro de entrenamientos",
  "sessions.attendance": "Asistencia",
  "sessions.newSession": "Nueva sesión",
  "sessions.noSessionsTitle": "Aún no hay sesiones de entrenamiento",
  "sessions.noSessionsBody": "Crea tu primera sesión para planificar entrenamientos y registrar la asistencia de tu plantilla.",
  "sessions.totalSessions": "Sesiones totales",
  "sessions.allTime": "Histórico",
  "sessions.thisWeek": "Esta semana",
  "sessions.sessionsSub": "Sesiones",
  "sessions.avgAttendance": "Asistencia media",
  "sessions.perSession": "Por sesión",
  "sessions.squadSize": "Tamaño de la plantilla",
  "sessions.players": "Jugadores",
  "sessions.upcoming": "Próximas",
  "sessions.trainingLogHeading": "Registro de entrenamientos",
  "sessions.sessionsCount": "sesiones",
  "sessions.noFilterMatch": "Ninguna sesión coincide con este filtro",
  "sessions.all": "Todas",
  // AI training focus
  "sessions.aiTrainingFocus": "Foco de entrenamiento IA",
  "sessions.aiTrainingSub": "En qué trabajar esta semana — a partir de tu último partido",
  "sessions.group.defense": "Defensa",
  "sessions.group.midfield": "Centro del campo",
  "sessions.group.forwards": "Delanteros",
  "sessions.group.goalkeepers": "Porteros",
  "sessions.fallbackSummary": "Según el último partido, la prioridad es afinar las transiciones defensivas y convertir las ocasiones que generamos. Organiza las sesiones de esta semana en torno a recuperar la forma tras perder el balón y definir con calma en el último tercio.",
  "sessions.fallbackDefense": "Trabaja la carrera de recuperación y la reorganización tras las pérdidas — concedimos espacios a la espalda en el contraataque. Añade defensa en el 1v1 y comunicación en la línea defensiva.",
  "sessions.fallbackMidfield": "Trabaja los disparadores del contrapresión y una circulación de balón más rápida. Se superó al centro del campo con demasiada facilidad; ensaya la compacidad y las rotaciones de presión y cobertura.",
  "sessions.fallbackForwards": "Definición bajo presión y movimientos a la espalda del último defensa. Generamos suficiente pero fuimos imprecisos — repetición de remates de primeras y temporización de los desmarques.",
  "sessions.fallbackGoalkeepers": "Distribución para iniciar los ataques y dominio del área en los centros. Añade trabajo de paradas de reacción y de salida a la espalda de una línea adelantada.",
  // Session types (display labels; ids stay English)
  "sessions.type.Tactical": "Táctica",
  "sessions.type.Physical": "Física",
  "sessions.type.Technical": "Técnica",
  "sessions.type.Recovery": "Recuperación",
  "sessions.type.MatchPrep": "Preparación de partido",
  "sessions.defaultFocus": "Sesión de entrenamiento",
  // Session card
  "sessions.tomorrow": "Mañana",
  "sessions.today": "Hoy",
  "sessions.inDays": "En {n} días",
  "sessions.planned": "Planificada",
  "sessions.present": "presentes",
  "sessions.viewDetails": "Ver detalles",
  // Detail panel
  "sessions.percentPresent": "% presentes",
  "sessions.absent": "ausentes",
  "sessions.coachNotes": "Notas del entrenador",
  "sessions.tapToToggle": "toca para alternar",
  "sessions.presentBadge": "Presente",
  "sessions.absentBadge": "Ausente",
  "sessions.attendanceNote": "Nota de asistencia",
  "sessions.saved": "Guardado",
  "sessions.attendanceNotePlaceholder": "Quién llegó tarde, lesionado o con permiso — y por qué…",
  // Attendance modal
  "sessions.takeAttendance": "Pasar lista",
  "sessions.uncheckAbsent": "desmarca a los ausentes",
  "sessions.presentAbsentSummary": "{present} presentes · {absent} ausentes",
  "sessions.uncheckAll": "Desmarcar todos",
  "sessions.checkAll": "Marcar todos",
  "sessions.attendanceSaved": "¡Asistencia guardada!",
  "sessions.saveAttendance": "Guardar asistencia",
  // New session modal
  "sessions.newSessionTitle": "Nueva sesión",
  "sessions.planNextTraining": "Planifica tu próximo entrenamiento",
  "sessions.date": "Fecha",
  "sessions.durationMin": "Duración (min)",
  "sessions.sessionType": "Tipo de sesión",
  "sessions.sessionFocus": "Foco de la sesión",
  "sessions.focusPlaceholder": "p. ej. Disparadores de presión alta y forma defensiva",
  "sessions.notesOptional": "Notas (opcional)",
  "sessions.notesPlaceholder": "Notas o instrucciones previas a la sesión...",
  "sessions.expectedPlayers": "Jugadores esperados",
  "sessions.deselectAll": "Deseleccionar todos",
  "sessions.selectAll": "Seleccionar todos",
  "sessions.ofSelected": "{n} de {total} seleccionados",
  "sessions.sessionScheduled": "¡Sesión programada!",
  "sessions.scheduleSession": "Programar sesión",

  // ── Squad / roster ──
  "squad.management": "Gestión de plantilla",
  "squad.playersRegistered": "jugadores registrados · Temporada 2025/26",
  "squad.lineup": "Alineación",
  "squad.resetLineup": "Restablecer alineación",
  "squad.searchPlayers": "Buscar jugadores…",
  "squad.noPlayersYet": "Aún no hay jugadores",
  "squad.noPlayersBody": "Añade jugadores a tu plantilla para empezar a gestionar formaciones, seguir el rendimiento y analizar estadísticas.",
  "squad.bench": "Banquillo",
  "squad.noBench": "Sin suplentes",
  "squad.formation": "Formación",
  "squad.physicalProfile": "Físico y perfil",
  "squad.thisWeek": "Esta semana",
  "squad.attributes": "Atributos",
  "squad.passportConnectors": "Conexiones de Pasaporte",
  "squad.kitTitle": "Equipaciones",
  "squad.kitBody": "Diseña tus equipaciones de local, visitante y portero — camiseta, diseño, pantalón y medias. Toca una equipación para personalizarla.",
  "squad.kitHome": "Local",
  "squad.kitAway": "Visitante",
  "squad.kitGk": "Portero",
  "squad.kitGkHome": "Portero local",
  "squad.kitGkAway": "Portero visit.",
  "squad.kitEdit": "Editar",
  "squad.kitEditing": "Editando",
  "squad.kitPreviewHint": "Vista previa",
  "squad.kitPattern": "Diseño",
  "squad.kitShirt": "Color de camiseta",
  "squad.kitAccent": "Detalle / ribete",
  "squad.kitShorts": "Pantalón",
  "squad.kitSocks": "Medias",
  "squad.kitSave": "Guardar equipación",
  "squad.kitCancel": "Cancelar",
  "squad.kitSaved": "Guardado",
  "squad.kitExtraAdd": "Añadir equipación",
  "squad.kitExtraName": "Nombre de la equipación",
  "squad.kitExtraDefaultName": "Nueva equipación",
  "squad.kitExtraRemove": "Eliminar esta equipación",
  "squad.kitExtraDeleteTitle": "¿Eliminar esta equipación?",
  "squad.kitExtraDeleteBody": "el diseño se elimina definitivamente, aquí y en la app. Tus cuatro equipaciones estándar no se ven afectadas.",
  "squad.kitExtraDeleteConfirm": "Eliminar equipación",
  "squad.logoTitle": "Escudo del equipo",
  "squad.logoBody": "Sube el escudo de tu club — aparece en tu plantilla y en toda la app.",
  "squad.logoUpload": "Subir escudo",
  "squad.logoReplace": "Cambiar escudo",
  "squad.logoUploading": "Subiendo…",
  "squad.logoAlt": "Escudo del equipo",
  "squad.patSolid": "Liso",
  "squad.patStripes": "Rayas",
  "squad.patHoops": "Aros",
  "squad.patSash": "Banda",
  "squad.patHalves": "Mitades",
  "squad.patChecks": "Cuadros",
  // ── Signup (web) ──
  "signup.signIn": "Inicia sesión",
  "signup.eyebrow": "Empecemos",
  "signup.whoTitle": "¿Quién eres?",
  "signup.whoSub": "Elige tu tipo de cuenta para tener la experiencia adecuada",
  "signup.langLabel": "Idioma",
  "signup.player": "Jugador",
  "signup.playerDesc": "Sigue tu rendimiento, crea tu tarjeta Passport y haz que te fichen",
  "signup.coach": "Entrenador",
  "signup.coachDesc": "Gestiona tu plantilla, analiza los datos de los partidos y sigue el desarrollo de cada jugador",
  "signup.getStarted": "Empezar",
  "signup.haveAccount": "¿Ya tienes una cuenta?",
  "signup.stepWord": "Paso",
  "signup.genderTitle": "Género",
  "signup.genderSub": "Selecciona tu género",
  "signup.genderMale": "Masculino",
  "signup.genderFemale": "Femenino",
  "signup.nameTitle": "Tu nombre",
  "signup.nameSub": "Como aparece en tu camiseta",
  "signup.firstName": "Nombre",
  "signup.firstNamePh": "Nombre",
  "signup.lastName": "Apellido",
  "signup.lastNamePh": "Apellido",
  "signup.heightTitle": "Estatura",
  "signup.heightSub": "Selecciona tu estatura",
  "signup.unitCm": "CM",
  "signup.unitFeetInches": "Pies y pulgadas",
  "signup.weightTitle": "Peso",
  "signup.weightSub": "Equilibra tus estadísticas",
  "signup.unitKg": "KG",
  "signup.unitLbs": "Lbs",
  "signup.footTitle": "Pie preferido",
  "signup.footSub": "Selecciona tu pie más fuerte",
  "signup.footLeft": "Izquierdo",
  "signup.footRight": "Derecho",
  "signup.footBoth": "Ambos",
  "signup.dobTitle": "Fecha de nacimiento",
  "signup.dobSub": "¿Cuándo naciste?",
  "signup.dobDay": "Día",
  "signup.dobMonth": "Mes",
  "signup.dobYear": "Año",
  "signup.positionTitle": "Posiciones",
  "signup.positionSub": "Toca todas las posiciones que puedas jugar",
  "signup.positionHint": "Elige una o más en la cancha — tu primera elección es tu posición principal.",
  "signup.jerseyTitle": "Equipo y camiseta",
  "signup.jerseySub": "Únete a tu equipo y elige tu número — ayuda a que las cámaras te sigan con precisión",
  "signup.teamCode": "Código del equipo",
  "signup.teamCodePh": "ej. AB12CD",
  "signup.teamChecking": "Comprobando…",
  "signup.teamJoining": "Te unes a",
  "signup.teamNotFound": "No se encontró ningún equipo con ese código — verifícalo con tu entrenador.",
  "signup.teamCodeHelper": "Pídele a tu entrenador el código de 6 caracteres para aparecer en su plantilla. Puedes omitir esto y agregarlo después.",
  "signup.teamCodeHelperRequired": "Pídele a tu entrenador el código de 6 caracteres para aparecer en su plantilla. Se requiere al menos un equipo válido — agrega más códigos para unirte a varios equipos a la vez.",
  "signup.teamCodeAddAnother": "Agregar otro código de equipo",
  "signup.teamCodeRemove": "Quitar código de equipo",
  "signup.jerseyNumber": "Tu número de camiseta",
  "signup.jerseyNumberHelp": "Ingresa el número que usas en tu camiseta. Esto ayuda a que nuestra IA te identifique durante los partidos — incluso cuando corres detrás de otros jugadores.",
  "signup.jerseyColor": "El color de la camiseta de tu equipo",
  "signup.jerseyColorSkip": "Puedes omitir esto — tu entrenador también puede configurarlo.",
  "signup.jerseyColorByCoach": "Tu entrenador define los colores de la equipación.",
  "signup.nationalityTitle": "Nacionalidad",
  "signup.nationalitySub": "Busca y selecciona tu país",
  "signup.searchCountry": "Buscar país...",
  "signup.teamsTitle": "Historial de equipos",
  "signup.teamsSub": "Clubes actuales y anteriores",
  "signup.noCurrentClub": "No tengo un club actual",
  "signup.clubSingular": "club",
  "signup.clubsPlural": "clubes",
  "signup.scrollToSeeAll": "desplázate para ver todos",
  "signup.current": "Actual",
  "signup.past": "Anterior",
  "signup.selectCountry": "Selecciona el país",
  "signup.clubName": "Nombre del club",
  "signup.fromYear": "Desde (año)",
  "signup.yearPh2019": "ej. 2019",
  "signup.yearErrFull": "Debe ser XXXX (ej. 2019)",
  "signup.toYear": "Hasta (año)",
  "signup.present": "Presente",
  "signup.yearPh2023": "ej. 2023",
  "signup.yearErrShort": "Debe ser XXXX",
  "signup.yearErrOrder": "Hasta no puede ser antes que Desde",
  "signup.currentClub": "Club actual",
  "signup.scrollMoreClubs": "↓ Desplázate hacia abajo para ver más clubes",
  "signup.addAnotherClub": "Agregar otro club",
  "signup.photoTitle": "Foto de perfil",
  "signup.photoSub": "Foto profesional para tu Passport",
  "signup.photoHint": "Usa el área de carga de la derecha.",
  "signup.accountTitle": "Crear cuenta",
  "signup.accountSub": "Correo y contraseña para iniciar sesión",
  "signup.email": "Correo electrónico",
  "signup.emailPh": "tu@correo.com",
  "signup.password": "Contraseña (mín. 8)",
  "signup.confirmPassword": "Confirmar contraseña",
  "signup.passwordsMismatch": "Las contraseñas no coinciden",
  "signup.vGenderHint": "Selecciona tu género a la izquierda",
  "signup.vYourName": "Tu nombre",
  "signup.vFootHint": "Elige tu pie a la izquierda",
  "signup.vNationalityHint": "Selecciona un país a la izquierda",
  "signup.vYourClubs": "Tus clubes",
  "signup.vAddClubsHint": "Agrega clubes a la izquierda — aparecerán aquí",
  "signup.vTapToUpload": "Toca para subir",
  "signup.vSecureAccount": "Cuenta segura",
  "signup.back": "Atrás",
  "signup.next": "Siguiente",
  "signup.createPassport": "Crear mi Passport",
  "signup.submitPortrait": "Creando tu retrato…",
  "signup.submitJoining": "Uniéndote a tu equipo…",
  "signup.submitCreating": "Creando…",
  "signup.successTitle": "Estás en la lista",
  "signup.successSub": "Tu Passport está listo. Míralo abajo.",
  "signup.goToPassport": "Ir a mi Passport",
  "signup.backToHome": "Volver al inicio",
  "signup.errEmailInUse": "Ya existe una cuenta con este correo. Por favor inicia sesión.",
  "signup.errWeakPassword": "La contraseña debe tener al menos 6 caracteres.",
  "signup.errCreateFailed": "No se pudo crear la cuenta. Inténtalo de nuevo.",
  // ── Player settings (web) ──
  "settings.title": "Configuración",
  "settings.subtitle": "Gestiona tu perfil, idioma y cuenta",
  "settings.photoChange": "Cambiar foto",
  "settings.photoCreating": "Creando tu retrato…",
  "settings.photoHint": "Toca la cámara para actualizar tu retrato",
  "settings.profileTitle": "Perfil",
  "settings.profileSub": "Edita los datos que ingresaste al registrarte",
  "settings.firstName": "Nombre",
  "settings.lastName": "Apellido",
  "settings.gender": "Género",
  "settings.genderMale": "Masculino",
  "settings.genderFemale": "Femenino",
  "settings.dob": "Fecha de nacimiento",
  "settings.nationality": "Nacionalidad",
  "settings.position": "Posición",
  "settings.jersey": "Número de camiseta",
  "settings.height": "Altura (cm)",
  "settings.weight": "Peso (kg)",
  "settings.foot": "Pie preferido",
  "settings.footLeft": "Izquierdo",
  "settings.footRight": "Derecho",
  "settings.footBoth": "Ambos",
  "settings.save": "Guardar cambios",
  "settings.saving": "Guardando…",
  "settings.saved": "Guardado",
  "settings.langTitle": "Idioma",
  "settings.legalTitle": "Legal",
  "settings.terms": "Términos y condiciones",
  "settings.privacy": "Política de privacidad",
  "settings.cookies": "Política de cookies",
  "settings.accountTitle": "Cuenta",
  "settings.signOut": "Cerrar sesión",
  "settings.deleteAccount": "Eliminar cuenta",
  "settings.deleteConfirmTitle": "¿Eliminar tu cuenta?",
  "settings.deleteConfirmBody": "Esto elimina permanentemente tu Passport y todos tus datos. No se puede deshacer.",
  "settings.deleteConfirm": "Eliminar para siempre",
  "settings.deleting": "Eliminando…",
  "settings.cancel": "Cancelar",
  // ── My Teams (player) ──
  "settings.teamsTitle": "Mis equipos",
  "settings.teamsSub": "Todos los equipos en los que juegas",
  "settings.teamsLoading": "Cargando tus equipos…",
  "settings.teamsEmpty": "Aún no te has unido a un equipo. Ingresa un código abajo para unirte.",
  "settings.teamsMember": "Integrante de la plantilla",
  "settings.teamsJoinTitle": "Unirte a otro equipo",
  "settings.teamsJoinPh": "Código del equipo",
  "settings.teamsJoinBtn": "Unirme",
  "settings.teamsChecking": "Comprobando…",
  "settings.teamsJoining": "Te unes a",
  "settings.teamsNotFound": "No se encontró ningún equipo con ese código — verifícalo con tu entrenador.",
  "settings.teamsKeepHistory": "Unirte a un equipo nuevo conserva todos tus equipos y tu historial de partidos.",
  // ── Career (player) ──
  "career.title": "Carrera",
  "career.subtitle": "Tu rendimiento, partido a partido",
  "career.noRatedMatches": "Aún no hay partidos calificados.",
  "career.chartAria": "Progresión de la valoración OVR por partido",
  "career.firstMatch": "Primer partido",
  "career.matchSingular": "partido",
  "career.matchPlural": "partidos",
  "career.latest": "Último",
  "career.noGamesTitle": "Aún no hay partidos",
  "career.noGamesBody": "Juega un partido calificado con tu entrenador para crear tu perfil de carrera.",
  "career.uploadMatch": "Ver mis partidos",
  "career.overall": "General",
  "career.avgRating": "Valoración prom.",
  "career.matches": "Partidos",
  "career.potential": "Potencial",
  "career.topPct": "Top {n}%",
  "career.percentile": "Percentil",
  "career.byTheNumbers": "En cifras",
  "career.matchesPlayed": "Partidos jugados",
  "career.goals": "Goles",
  "career.assists": "Asistencias",
  "career.playerOfMatch": "Figura del partido",
  "career.ovrProgression": "Progresión OVR",
  "career.whatsDriving": "Qué impulsa tus estadísticas",
  "career.careerHighs": "Mejores marcas",
  "career.bestRating": "Mejor valoración",
  "career.vs": "vs",
  "career.mostGoalsGame": "Más goles (partido)",
  "career.inSingleMatch": "En un solo partido",
  "career.biggestJump": "Mayor salto",
  "career.matchOnMatchGain": "Ganancia de OVR entre partidos",
  "career.seasonRecap": "Resumen de la temporada",
  // ── Achievements (player) ──
  "ach.title": "Logros",
  "ach.subtitle": "Insignias, rachas y récords personales",
  "ach.close": "Cerrar",
  "ach.unlocked": "Desbloqueado",
  "ach.locked": "Bloqueado",
  "ach.progress": "Progreso",
  "ach.inARow": "seguidos",
  "ach.noAchievementsTitle": "Aún no hay logros",
  "ach.noAchievementsBody": "Juega partidos calificados para empezar a desbloquear insignias.",
  "ach.uploadMatch": "Ver mis partidos",
  "ach.scoringStreak": "Racha goleadora",
  "ach.manOfMatch": "Figura del partido",
  "ach.unbeatenRun": "Racha invicta",
  "ach.trophyCabinet": "Vitrina de trofeos",
  "ach.of": "de",
  "ach.unlockedLower": "desbloqueados",
  "ach.badges": "Insignias",
  "ach.activeStreaks": "Rachas activas",
  "ach.personalRecords": "Récords personales",
  "ach.bestRating": "Mejor valoración",
  "ach.vs": "vs",
  "ach.mostGoalsGame": "Más goles (partido)",
  "ach.biggestJump": "Mayor salto",
  "ach.nextUp": "A continuación",
  // ── Scout report (player) ──
  "scout.title": "Informe de Scout",
  "scout.subtitle": "La lectura de un scout profesional sobre tu juego",
  "scout.locked": "Informe de scout bloqueado",
  "scout.lockedBody": "Juega algunos partidos calificados y tu informe de scout con IA aparecerá aquí.",
  "scout.uploadMatch": "Ver mis partidos",
  "scout.scoutGrade": "Nota del scout",
  "scout.overall": "General",
  "scout.potential": "Potencial",
  "scout.position": "Posición",
  "scout.percentile": "Percentil",
  "scout.topPct": "Top {n}%",
  "scout.rankedPrefix": "Puesto",
  "scout.of": "de",
  "scout.playersSuffix": "jugadores.",
  "scout.scoutCall": "Veredicto del scout",
  "scout.recommendation": "Recomendación",
  "scout.scoutVerdict": "Veredicto del scout",
  "scout.strengths": "Fortalezas",
  "scout.areasToDevelop": "Aspectos a mejorar",
  "scout.playingStyle": "Estilo de juego",
  "scout.outlook": "Proyección",
  "scout.projection": "Proyección",
  "scout.comparison": "Comparación",
  "scout.backToHome": "Volver al inicio",
  // ── Training (player) ──
  "train.typePhysical": "Físico",
  "train.typeTechnical": "Técnico",
  "train.typeTactical": "Táctico",
  "train.typeRecovery": "Recuperación",
  "train.typeMatchPrep": "Prep. de partido",
  "train.typeTraining": "Entrenamiento",
  "train.dateTbc": "Fecha por confirmar",
  "train.min": "min",
  "train.attended": "Asististe",
  "train.missed": "Faltaste",
  "train.loading": "Cargando tu entrenamiento…",
  "train.loadFailed": "No se pudo cargar el entrenamiento",
  "train.backToPassport": "Volver al Passport",
  "train.title": "Entrenamiento",
  "train.subtitle": "Tu asistencia y próximas sesiones",
  "train.notOnTeam": "Aún no estás en un equipo",
  "train.notOnTeamBody": "Pídele a tu entrenador un código de equipo para unirte a su plantilla y ver las sesiones de entrenamiento.",
  "train.attendance": "Asistencia",
  "train.attendanceScore": "Puntaje de asistencia",
  "train.sessions": "sesiones",
  "train.inARow": "seguidas",
  "train.upcomingSessions": "Próximas sesiones",
  "train.noUpcoming": "No hay sesiones próximas programadas",
  "train.recentSessions": "Sesiones recientes",
  "train.noPast": "Aún no hay sesiones anteriores",
  // ── Passport home (player) ──
  "home.heroTitle": "Tu Pasaporte",
  "home.heroSubtitle": "Tus estadísticas reales, tu tarjeta Draffte. Comparte un solo enlace con entrenadores y ojeadores.",
  "home.tileMyGames": "Mis partidos",
  "home.tileCareer": "Carrera",
  "home.tileAchievements": "Logros",
  "home.tileScoutReport": "Informe de ojeador",
  "home.tileTraining": "Entrenamiento",
  "home.tileRanking": "Clasificación",
  "home.highlightVideos": "Videos destacados",
  "home.highlightVideosSub": "Tus mejores momentos de los partidos recientes",
  "home.uploadMatchVideo": "Subir video del partido",
  "home.highlight": "Destacada",
  "home.highlightVideosNote": "Conecta tu cámara Draffte para generar destacados automáticamente después de cada partido",
  "home.ovrProgression": "Progresión general de OVR",
  "home.ovrProgressionSub": "Tu OVR (valoración de la tarjeta) en los partidos recientes",
  "home.ovrExampleNote": "Progresión de ejemplo — sube partidos para ver datos reales",
  "home.gameAxis": "Partido →",
  "home.game": "Partido",
  "home.teamHistory": "Historial de equipos",
  "home.teamHistorySub": "Clubes y selecciones que has representado",
  "home.teamEmpty": "Agrega tus clubes en el perfil para ver tu trayectoria",
  "home.club": "Club",
  "home.current": "Actual",
  "home.performance": "Rendimiento",
  "home.performanceSub": "Ritmo, distancia e intensidad de tus partidos",
  "home.performanceEmpty": "Conecta la cámara y los wearables para ver estadísticas en vivo",
  "home.sharePassport": "Comparte tu Pasaporte",
  "home.sharePassportSub": "Un solo enlace. Entrenadores y ojeadores ven tu tarjeta — sin necesidad de app.",
  "home.copyLink": "Copiar enlace",
  "home.copied": "Copiado",
  "home.player": "Jugador",
  "home.notYetRated": "Sin calificar",
  "home.yearsOld": "años",
  "home.statsSynced": "Estadísticas sincronizadas",
  "home.form": "forma",
  "home.synced": "Al día",
  "home.tier.elite": "Élite",
  "home.tier.gold": "Oro",
  "home.tier.silver": "Plata",
  "home.tier.bronze": "Bronce",
  "home.foot.left": "Zurdo",
  "home.foot.both": "Ambos",
  "home.foot.right": "Diestro",
  // ── Ranking (player) ──
  "rank.title": "Clasificación",
  "rank.subtitle": "Tu posición por país y a nivel mundial.",
  "rank.fairnessNote": "Esta clasificación se basa en tu edad y posición para una comparación justa con jugadores de tu categoría.",
  "rank.rankingLabel": "Clasificación",
  "rank.country": "País",
  "rank.standings": "Tabla de posiciones",
  "rank.standingsSub": "Alterna entre la vista por país y la mundial",
  "rank.tabCountry": "País",
  "rank.tabGlobal": "Mundial",
  "rank.globalRanking": "Clasificación mundial",
  "rank.emptyTitle": "Aún no hay clasificación",
  "rank.emptySub": "Sube y procesa un partido para obtener tu primera clasificación.",
  "rank.gold": "Oro",
  "rank.silver": "Plata",
  "rank.bronze": "Bronce",
  "rank.demoTopPlayer": "Mejor jugador",
  "rank.demoSecond": "Segundo",
  "rank.demoThird": "Tercero",
  "rank.demoYou": "Tú",
  // ── Analytics (player) ──
  "analytics.eyebrow": "Inteligencia de rendimiento",
  "analytics.title": "Analíticas",
  "analytics.heroLead": "Mira cómo los",
  "analytics.heroRealData": "datos reales del partido",
  "analytics.heroMiddle": "se convierten en tus seis estadísticas y tu",
  "analytics.heroTrailing": "Simple, transparente y listo para ojeadores.",
  "analytics.yourActualStats": "Tus estadísticas reales",
  "analytics.currentStatsNote": "Estas son las estadísticas actuales de tu tarjeta. La fórmula de abajo muestra cómo se promedian para dar tu OVR.",
  "analytics.flowGames": "Tus partidos",
  "analytics.flowStats": "6 estadísticas",
  "analytics.howOvrTitle": "Cómo se calcula tu OVR",
  "analytics.howOvrSub": "Promedio de tus seis estadísticas de la tarjeta — justo y fácil de entender",
  "analytics.whatDrivesTitle": "Qué impulsa tus estadísticas",
  "analytics.whatDrivesLead": "Tus estadísticas se calibran según tu",
  "analytics.whatDrivesGender": "género",
  "analytics.whatDrivesAnd": "y tu",
  "analytics.whatDrivesAge": "edad",
  "analytics.whatDrivesRest": "para que las comparaciones sean justas entre jugadores. Las métricas en bruto de la cancha — velocidad máxima, resistencia, distancia recorrida, carreras de alta intensidad y más — las captura la cámara Draffte y los wearables, y luego se traducen en tus seis estadísticas de la tarjeta (SPD, FIN, PLY, WRK, DEF, STR) y tu forma general.",
  "analytics.liveDataFrom": "Datos en vivo de",
  "analytics.processedMatchSingular": "partido procesado",
  "analytics.processedMatchesPlural": "partidos procesados",
  "analytics.exampleValuesNote": "Por ahora estos son valores de ejemplo. Cuando entres en vivo, las mismas métricas (velocidad, resistencia, distancia, etc.) vendrán de los datos reales de tus partidos — cámara y wearables — y alimentarán las mismas seis estadísticas y tu OVR. La misma lógica, con números reales.",
  "analytics.statsExplainedTitle": "Estadísticas explicadas",
  "analytics.statsExplainedSub": "Qué significa cada abreviatura y cómo se calcula a partir de los datos de tus partidos.",
  "analytics.howCalculated": "Cómo se calcula",
  "analytics.backToHome": "Volver al inicio",
  "analytics.feeds": "Alimenta",
  "analytics.feedsAll": "Todas",
  "analytics.metric_speed_label": "Velocidad máxima",
  "analytics.metric_speed_desc": "Velocidad máxima alcanzada en los partidos",
  "analytics.metric_stamina_label": "Resistencia",
  "analytics.metric_stamina_desc": "Esfuerzo sostenido durante los 90 min",
  "analytics.metric_acceleration_label": "Aceleración",
  "analytics.metric_acceleration_desc": "Explosividad en el arranque",
  "analytics.metric_distance_label": "Distancia por partido",
  "analytics.metric_distance_desc": "Distancia total recorrida",
  "analytics.metric_intensity_label": "Alta intensidad",
  "analytics.metric_intensity_desc": "Sprints y carreras de alta intensidad",
  "analytics.metric_consistency_label": "Consistencia de forma",
  "analytics.metric_consistency_desc": "Rendimiento estable entre partidos",
  "analytics.stat_SPD_name": "Velocidad",
  "analytics.stat_SPD_desc": "Tu velocidad máxima y aceleración en la cancha. Refleja qué tan rápido corres en tu punto máximo.",
  "analytics.stat_SPD_calc": "Se calcula a partir de la velocidad máxima (km/h) que alcanzas durante el partido, medida por el seguimiento de la cámara y los wearables. Se calibra por grupo de edad para que los jugadores más jóvenes y los mayores se comparen de forma justa.",
  "analytics.stat_FIN_name": "Definición",
  "analytics.stat_FIN_desc": "Calidad y eficacia de cara al arco — precisión de remate y temple en situaciones de gol.",
  "analytics.stat_FIN_calc": "Se construye a partir de la precisión de remate, la conversión en momentos clave y las acciones de peligro de gol. A medida que agreguemos más eventos del partido (remates, xG), esta estadística reflejará tus datos reales de definición.",
  "analytics.stat_PLY_name": "Creación de juego",
  "analytics.stat_PLY_desc": "Visión y ejecución en la salida: precisión de pases, pases clave y progresión del balón.",
  "analytics.stat_PLY_calc": "Se basa en pases completados, creación de ocasiones y participación en jugadas peligrosas. Se afinará con el tipo de pase, la dirección y la presión a partir de la cámara y los wearables.",
  "analytics.stat_WRK_name": "Despliegue",
  "analytics.stat_WRK_desc": "Esfuerzo sin balón: presión, repliegue e intensidad cuando no tienes la posesión.",
  "analytics.stat_WRK_calc": "Se impulsa por las carreras de alta intensidad, los sprints y el movimiento cuando el equipo defiende. Combina la distancia a alta velocidad con la recuperación y los esfuerzos repetidos.",
  "analytics.stat_DEF_name": "Defensa",
  "analytics.stat_DEF_desc": "Impacto defensivo: duelos, entradas, intercepciones y posicionamiento cuando el rival tiene el balón.",
  "analytics.stat_DEF_calc": "A partir de las acciones defensivas, los duelos ganados y el posicionamiento durante la posesión rival. Se enriquecerá con la detección de eventos (entradas, despejes) a medida que mejore nuestro seguimiento.",
  "analytics.stat_STR_name": "Fuerza",
  "analytics.stat_STR_desc": "Cuántas veces sales de un mano a mano con el balón: quitándoselo a un rival o conservándolo cuando te presionan.",
  "analytics.stat_STR_calc": "Duelos ganados sobre el total de tus duelos: entradas y regates marcados en el video del partido, más las veces que el balón cambió de equipo justo desde los pies de un jugador. Se compara con jugadores de tu posición; aparece cuando has disputado al menos cinco duelos.",
  // ── My Games (player) ──
  "games.title": "Mis partidos",
  "games.subtitle": "Cada partido que has jugado",
  "games.subtitleCount": "{n} partidos jugados",
  "games.rating": "Nota",
  "games.home": "Local",
  "games.away": "Visitante",
  "games.motm": "Figura",
  "games.vs": "vs",
  "games.attacking": "Ataque",
  "games.minutes": "Minutos",
  "games.shots": "Remates",
  "games.onTarget": "Al arco",
  "games.keyPasses": "Pases clave",
  "games.dribbles": "Regates",
  "games.goalsLabel": "Goles",
  "games.passing": "Pases",
  "games.passesCompleted": "Pases",
  "games.passesAttempted": "Intentados",
  "games.passAccuracy": "Precisión",
  "games.defending": "Defensa",
  "games.tackles": "Entradas",
  "games.interceptions": "Intercep.",
  "games.clearances": "Despejes",
  "games.goalkeeping": "Portería",
  "games.saves": "Atajadas",
  "games.catches": "Atrapadas",
  "games.conceded": "Recibidos",
  "games.noDetail": "Las estadísticas detalladas vienen del video del partido — este lo registró tu entrenador manualmente.",
  "games.distanceHeatmapNote": "La distancia y el mapa de calor vienen del análisis del video — próximamente para partidos individuales.",
  "games.emptyTitle": "Aún no hay partidos",
  "games.emptyBody": "Tus partidos aparecerán aquí cuando tu entrenador califique un partido que jugaste.",
  "games.pageOf": "Página {p} de {total}",
  "games.backToPassport": "Volver al Passport",
  // ── Player nav (web shell menu) ──
  "pnav.home": "Inicio",
  "pnav.myGames": "Mis partidos",
  "pnav.career": "Carrera",
  "pnav.achievements": "Logros",
  "pnav.scout": "Informe de ojeador",
  "pnav.training": "Entrenamiento",
  "pnav.ranking": "Clasificación",
  "pnav.analytics": "Analíticas",
  "pnav.settings": "Configuración",
  "pnav.menu": "Menú",
  "pnav.close": "Cerrar",

  // Generic
  "common.loading": "Cargando…",
  // ── i18n batch: squad/matches/stats/passnet/shotmap ──
  "col.assists.tag": "A",
  "col.blocks.tag": "Bloq",
  "col.catches.tag": "Atr",
  "col.clearances.tag": "Desp",
  "col.crossesAttempted.tag": "CI",
  "col.crossesCompleted.tag": "CC",
  "col.dribbles.tag": "Reg",
  "col.fouls.tag": "Fa",
  "col.gkIntAttempted.tag": "Ii",
  "col.gkIntSuccessful.tag": "Ia",
  "col.goalKicksAttempted.tag": "SPi",
  "col.goalKicksSuccessful.tag": "SPa",
  "col.goals.tag": "G",
  "col.goalsConceded.tag": "GE",
  "col.interceptions.tag": "Int",
  "col.keyPasses.tag": "PC",
  "col.minutes.tag": "Min",
  "col.runDistance.tag": "Dist",
  "col.runDistance.name": "Distancia recorrida",
  "col.runDistance.desc": "Kilómetros estimados, escalados de los minutos en que pudimos seguir al jugador a los minutos que jugó",
  "col.runPerMin.tag": "m/min",
  "col.runPerMin.name": "Metros por minuto",
  "col.runPerMin.desc": "Ritmo de trabajo medio en el campo",
  "col.runTop.tag": "Vel",
  "col.runTop.name": "Velocidad máxima",
  "col.runTop.desc": "Sprint más rápido medido, solo donde se leyó el dorsal",
  "col.parries.tag": "Rech",
  "col.passAccuracy.tag": "Prec%",
  "col.passesAttempted.tag": "PI",
  "col.passesCompleted.tag": "PC",
  "col.player.tag": "Jugador",
  "col.position.tag": "Pos",
  "col.rating.tag": "Val",
  "col.redCards.tag": "TR",
  "col.shirtNumber.tag": "Nº",
  "col.shots.tag": "Tir",
  "col.shotsOnTarget.tag": "TaP",
  "col.tackles.tag": "Ent",
  "col.recoveries.tag": "Rec",
  "col.recoveries.name": "Recuperaciones",
  "col.recoveries.desc": "Veces que este jugador recuperó el balón del rival (dato etiquetado real — incluye entradas e intercepciones).",
  "col.turnovers.tag": "Perd",
  "col.turnovers.name": "Pérdidas",
  "col.turnovers.desc": "Veces que este jugador perdió el balón ante el rival (dato etiquetado real).",
  "col.yellowCards.tag": "TA",
  "heat.zone.centralAttThird": "Tercio de ataque central",
  "heat.zone.centralDefensive": "Zona defensiva central",
  "heat.zone.centralMidfield": "Mediocampo central",
  "heat.zone.leftChannelMid": "Carril izquierdo (medio)",
  "heat.zone.leftDefensive": "Zona defensiva izquierda",
  "heat.zone.leftWingAtt": "Banda izquierda (ataque)",
  "heat.zone.rightDefensive": "Zona defensiva derecha",
  "heat.zone.rightMidfield": "Mediocampo derecho",
  "heat.zone.rightWingAtt": "Banda derecha (ataque)",
  "matches.inDays": "En {days} días",
  "matches.pens": "PEN",
  "matches.scorerNotSet": "Goleador sin registrar",
  "matches.resultDraw": "EMPATE",
  "matches.resultLoss": "DERROTA",
  "matches.resultWin": "VICTORIA",
  "matches.statCorners": "Córners",
  "matches.statPassAcc": "Prec. pases",
  "matches.statPossession": "Posesión",
  "matches.statShotsOn": "Tiros a puerta",
  "matches.statShotsTotal": "Tiros totales",
  "matches.teamStats": "Estadísticas del equipo",
  "matches.today": "Hoy",
  "matches.tomorrow": "Mañana",
  "matches.vs": "vs",
  "pn.networkRead.body1": "dirigió la salida del balón ({out} pases dados), siendo {topTarget} el destino más buscado ({inn} recibidos).",
  "pn.networkRead.body2": "El {acc}% de los pases encontró a un compañero, y el {fwd}% fue hacia adelante{longest}.",
  "pn.networkRead.identityPossession": "Una identidad paciente y de posesión — reciclando el balón para mantener el control.",
  "pn.networkRead.identityVertical": "Una identidad de pase progresiva y vertical.",
  "pn.networkRead.longest": " — el más largo, un balón de {lenM} m de {from} a {to}",
  "pn.note.intoDeep": "al pie en zonas retrasadas para iniciar jugadas",
  "pn.note.intoHigh": "arriba en zonas de ataque",
  "pn.note.lengthLong": "con preferencia por balones largos que rompen líneas",
  "pn.note.lengthShort": "manteniéndolo corto y sencillo en su mayoría",
  "pn.note.made": "{player} dio {n} pases con un {acc}% de acierto desde una posición {zone} — {style}, {length}. El {fwd}% fue hacia adelante y el {back}% hacia atrás, con una media de {avgLen} m.{topSentence} {rel}",
  "pn.note.received": "{player} recibió {n} pases ({acc}% le llegaron) en una posición {zone} — encontrado {into}. El {fwd}% de los balones hacia él fueron hacia adelante, con una media de {avgLen} m.{topSentence} {rel}",
  "pn.note.relMadeHigh": "Muy seguro con el balón.",
  "pn.note.relMadeLow": "Propenso a la pérdida — necesita opciones más seguras bajo presión.",
  "pn.note.relMadeMid": "Bastante fiable, con margen para afinar la elección de pase.",
  "pn.note.relRecvHigh": "Casi siempre lo encuentra con limpieza.",
  "pn.note.relRecvLow": "A menudo pasados de fuerza o cortados antes de llegarle.",
  "pn.note.relRecvMid": "Suele llegarle, aunque se pierden algunos balones en el camino.",
  "pn.note.strongestConn": " Su conexión más fuerte: {topR} ({count} pases).",
  "pn.note.styleBalanced": "un eslabón equilibrado en la salida del balón",
  "pn.note.styleProgressive": "un distribuidor progresivo y vertical",
  "pn.note.styleRecycler": "un jugador que recicla y mantiene viva la posesión",
  "pn.note.suppliedBy": " Su principal abastecedor fue {topS} ({count} pases).",
  "pn.note.zoneAdvanced": "adelantada",
  "pn.note.zoneDeep": "retrasada",
  "pn.note.zoneMidfield": "de mediocampo",
  "pn.watchPass": "Ver este pase",
  "pn.close": "Cerrar",
  "pn.play": "Reproducir",
  "pn.pause": "Pausa",
  "pn.replay": "Repetir",
  "pn.srcFollow": "Video 1080p con seguimiento",
  "pn.srcWide": "Panorámica",
  "pn.estimatedNote": "Posiciones estimadas — ubicadas por el recorrido del balón, no por tracking medido",
  "pn.approxNote": "Vista aproximada — este partido es anterior al registro por pase; las posiciones son esquemáticas",
  "sm.estimatedTooltip": "El resultado es real (etiquetado); la posición en el campo es estimada",
  "sm.shotRead.aheadOfNumbers": "— un resultado por encima de lo que dicen los números.",
  "sm.shotRead.bigChances": "{n} ocasiones claras (≥0,3 xG).",
  "sm.shotRead.body1": "{goals} goles de {shots} tiros ({xg} xG combinados).",
  "sm.shotRead.body2": "{team} generó las mejores ocasiones ({xg} xG)",
  "sm.shotRead.inLine": "— en líneas generales acorde con las ocasiones generadas.",
  "sm.xgPerShot": "xG/tiro",
  "squad.addFirstPlayer": "Añadir primer jugador",
  "squad.attackDirection": "Ataque ↑",
  "squad.availAvailable": "Disponible",
  "squad.availDoubtful": "En duda",
  "squad.availInjured": "Lesionado",
  "squad.benchDragHelp": "Arrastra a un suplente sobre una posición del campo para sustituir",
  "squad.benchDragging": "Arrastrando — suelta en el campo para sustituir",
  "squad.colDistance": "Distancia (est.)",
  "squad.colForm": "Forma",
  "squad.colOvr": "VAL",
  "squad.colPlayer": "Jugador",
  "squad.colSpeed": "Velocidad (est.)",
  "squad.colStatus": "Estado",
  "squad.connectedCount": "conectados",
  "squad.copied": "¡Copiado!",
  "squad.copyInviteLink": "Copiar enlace de invitación",
  "squad.teamCode": "Código de equipo",
  "squad.teamCodeHint": "Los jugadores introducen este código en la app Draffte Passport para unirse a tu plantilla.",
  "squad.copyCode": "Copiar código",
  "squad.copyMessage": "Copiar mensaje de invitación",
  "squad.newCode": "Nuevo código",
  "squad.regenWarn": "¿Generar un código nuevo? El actual dejará de funcionar.",
  "squad.inviteMessage": "¡Únete a mi equipo en Draffte! Descarga la app Draffte Passport e introduce el código de equipo: {code}",
  "squad.noCodeYet": "Crea tu equipo para obtener un código de invitación.",
  "squad.dragHintSwap": "Suelta sobre una posición del campo para sustituir",
  "squad.filterAll": "Todos",
  "squad.footBoth": "Ambos",
  "squad.footLeft": "Izquierdo",
  "squad.footRight": "Derecho",
  "squad.formStable": "Estable",
  "squad.formTrend": "Tendencia de forma",
  "squad.genderFemale": "Femenino",
  "squad.genderMale": "Masculino",
  "squad.inviteFootnote": "Los jugadores que usen tu código o enlace se unen a tu equipo automáticamente, sin club que elegir ni introducir.",
  "squad.passportConnected": "Pasaporte Draffte conectado",
  "squad.passportConnectedDesc": "Las estadísticas se sincronizan tras cada entrenamiento y partido",
  "squad.passportConnectorsBody": "tienen un Pasaporte Draffte. Comparte tu enlace: los asigna automáticamente a tu equipo.",
  "squad.passportNone": "Sin cuenta de Pasaporte",
  "squad.passportNoneDesc": "Invita a este jugador a crear un Pasaporte",
  "squad.pendingCount": "pendientes",
  "squad.physAge": "Edad",
  "squad.physFoot": "Pie",
  "squad.physGender": "Sexo",
  "squad.physHeight": "Altura",
  "squad.physNation": "País",
  "squad.physWeight": "Peso",
  "squad.playersWord": "jugadores",
  "squad.posCAM": "Mediapunta",
  "squad.posCB": "Defensa central",
  "squad.posCDM": "Mediocentro defensivo",
  "squad.posCM": "Mediocentro",
  "squad.posGK": "Portero",
  "squad.posLB": "Lateral izquierdo",
  "squad.posLW": "Extremo izquierdo",
  "squad.posRB": "Lateral derecho",
  "squad.posRW": "Extremo derecho",
  "squad.posST": "Delantero",
  "squad.roleDefender": "Defensa",
  "squad.roleForward": "Delantero",
  "squad.roleMidfielder": "Centrocampista",
  "squad.rolePortero": "Portero",
  "squad.sendPassportInvite": "Enviar invitación de Pasaporte",
  "squad.cancel": "Cancelar",
  "squad.removePlayer": "Quitar del plantel",
  "squad.removeConfirmTitle": "¿Quitar a {name} del plantel?",
  "squad.removeConfirmBody": "Sale del plantel activo. Sus partidos calificados y todo su Pasaporte se conservan — si lo vuelves a agregar, recupera su historial.",
  "squad.removeConfirmAction": "Sí, quitar",
  "squad.removing": "Quitando…",
  "squad.statRun": "DIST",
  "squad.statSpd": "VEL",
  "squad.statWrk": "TRA",
  "squad.step1Desc": "Envía a los jugadores tu código de equipo o enlace de invitación",
  "squad.step1Title": "Comparte el código",
  "squad.step2Desc": "Los jugadores introducen el código en la app Draffte Passport para unirse a tu plantilla",
  "squad.step2Title": "Se unen",
  "squad.step3Desc": "Las estadísticas aparecen aquí tras cada partido y entrenamiento",
  "squad.step3Title": "Sincronización automática",
  "squad.unitYrs": "años",
  "squad.weekDistance": "Distancia",
  "squad.weekSessions": "Sesiones",
  "squad.weekFromVideo": "La velocidad, distancia y carga vienen del video del partido",
  "squad.weekFromVideoSub": "Se desbloquea cuando la cámara IA analiza un partido grabado.",
  "squad.weekTopSpeed": "Vel. máxima",
  "squad.runningLast": "último: vs {opp}",
  "card.provisional": "prov.",
  "card.provisionalHint": "Provisional: velocidad máxima medida en el video del partido, en una escala fija. Pasa a ser un ranking frente a jugadores de su posición cuando seis de ellos estén medidos.",
  "stats.insight.clinicalFinishing.detail": "21% de tasa de conversión de tiros, muy por encima de la media de la liga del 12%.",
  "stats.insight.clinicalFinishing.title": "Definición certera",
  "stats.insight.defensiveTransitions.detail": "Gol encajado tras un tiro libre rápido; faltó atención en el 45'.",
  "stats.insight.defensiveTransitions.title": "Transiciones defensivas",
  "stats.insight.leftFlankDominance.detail": "El 68% de los ataques con éxito salieron por la banda izquierda a través de Amy L. y Luca F.",
  "stats.insight.leftFlankDominance.title": "Dominio de la banda izquierda",
  "stats.insight.leonDiscipline.detail": "Tarjeta amarilla por una entrada imprudente: 3 faltas en 67 minutos antes de ser sustituido.",
  "stats.insight.leonDiscipline.title": "Disciplina de Leon D.",
  "stats.insight.midfieldControl.detail": "Tom K. completó el 87% de sus pases y generó 3 ocasiones claras.",
  "stats.insight.midfieldControl.title": "Control del mediocampo",
  "stats.insight.rightSideVulnerable.detail": "Solo el 32% de las acciones defensivas por la derecha; Kai W. quedó adelantado.",
  "stats.insight.rightSideVulnerable.title": "Banda derecha vulnerable",
  "stats.insight.setPieceThreat.detail": "2 goles llegaron directamente desde acciones a balón parado. El envío de Sarah T. fue excepcional.",
  "stats.insight.setPieceThreat.title": "Peligro a balón parado",
  "stats.recommendation.exploitLeft": "Explota más el carril izquierdo: la combinación Amy L. + Luca F. fue letal",
  "stats.recommendation.quickTransitions": "Trabaja las transiciones rápidas tras tiros libres para evitar encajar",
  "stats.recommendation.rightBackPositioning": "Corrige la posición del lateral derecho cuando el equipo tiene la posesión",
  "stats.recommendation.setPieceVariations": "Las jugadas a balón parado funcionan bien; añade variantes al primer palo",
  "stats.recommendation.startNina": "Plantea alinear a Nina A. junto a Tom K. para mejorar la retención del balón",
  "stats.tacticalInsight.leftVsRightChances": "Tu equipo generó 3× más ocasiones por la izquierda que por la derecha",
  "stats.tacticalInsight.pressBroken": "La presión del rival se superó en menos de 3 pases el 78% de las veces",
  "stats.tacticalInsight.recoveryTime": "El tiempo medio de recuperación del balón fue de 4,2 segundos, 1,8 s más rápido que el rival",
  "stats.tacticalInsight.shotsInsideBox": "El 60% de los tiros llegaron desde dentro del área",
  "stats.tacticalInsight.vulnerableWindow": "El rival fue más vulnerable entre los minutos 55 y 70",
  // ── sessions demo content ──
  "sessions.focus.matchPrepRival": "Repaso final vs Rival FC — balón parado y disparadores defensivos",
  "sessions.focus.preMatchShape": "Ensayo de estructura prepartido — 4-2-3-1 bloque alto",
  "sessions.focus.setPiecesShape": "Balón parado y consolidación de la estructura defensiva",
  "sessions.focus.sprintIntervals": "Series de velocidad de alta intensidad — 8×40m + puertas de agilidad",
  "sessions.focus.passingRondos": "Precisión de pase bajo presión — rondos y juego de posición",
  "sessions.focus.highPressTriggers": "Disparadores de presión alta 4-2-3-1 — compactación y contrapresión",
  "sessions.focus.postMatchRecovery": "Recuperación pospartido — estiramientos, hidroterapia y pase ligero",
  "sessions.focus.oppositionAnalysis": "Análisis del rival — reconocimiento de estructura y disparadores de transición",
  "sessions.focus.sprintEndurance": "Resistencia de velocidad y agilidad — carreras de ida y vuelta + ejercicios de presión 1v1",
  "sessions.focus.finishingCrossing": "Ejercicios de definición y centros — juego en el área y combinaciones por banda",
  "sessions.notes.leonHighPress": "A Leon le costó mantener la presión alta en la segunda parte. Considerar un rol modificado.",
  "sessions.notes.strongWorkRate": "Gran intensidad de trabajo en toda la plantilla. Sarah lideró los ejercicios de carrera.",
  "sessions.notes.bestPassing": "Plantilla completa. La mejor sesión de pase del mes. Nina mostró gran serenidad.",
  "sessions.notes.activeRecovery": "Solo recuperación activa. Carlos y Alex acudieron a fisioterapia por separado.",
  "sessions.notes.wellPrepared": "Buen trabajo táctico. El equipo llega bien preparado para el domingo.",
  "sessions.notes.combination": "Marcus y Amy excelentes en la combinación. Nina mostró mejor juego asociativo.",

  // ── Coach signup ──
  "coachSignup.eyebrow": "Cuenta de Entrenador",
  "coachSignup.roleTitle": "¿Cuál es tu rol?",
  "coachSignup.roleSub": "Selecciona tu posición principal como entrenador",
  "coachSignup.nameTitle": "Tu nombre",
  "coachSignup.nameSub": "Cómo te conocerá tu plantilla",
  "coachSignup.dobSub": "Se usa para los filtros por categoría de edad en el sistema de ranking de Draffte",
  "coachSignup.natSub": "Tu país de origen",
  "coachSignup.licenseTitle": "Licencia de entrenador",
  "coachSignup.licenseSub": "Tu máxima titulación como entrenador",
  "coachSignup.experienceTitle": "Experiencia",
  "coachSignup.experienceSub": "Años de entrenamiento + categorías con las que has trabajado",
  "coachSignup.yearsExp": "Años de experiencia",
  "coachSignup.yearsUnit": "años",
  "coachSignup.ageGroupsLabel": "Categorías entrenadas",
  "coachSignup.clubTitle": "Club actual",
  "coachSignup.clubSub": "¿Entrenas actualmente a un equipo?",
  "coachSignup.clubYes": "Sí, entreno",
  "coachSignup.clubNo": "Ahora mismo no",
  "coachSignup.clubNameLabel": "Nombre del club / equipo",
  "coachSignup.clubNamePh": "ej. FC Barcelona Academy",
  "coachSignup.clubCountry": "País",
  "coachSignup.searchShort": "Buscar…",
  "coachSignup.squadLevelLabel": "Nivel de la plantilla",
  "coachSignup.historyTitle": "Historial de clubes",
  "coachSignup.historySub": "Equipos que has entrenado — el más reciente primero",
  "coachSignup.clubN": "Club",
  "coachSignup.rolePh": "Tu rol (ej. Entrenador principal)",
  "coachSignup.currentlyHere": "Entreno aquí actualmente",
  "coachSignup.photoSub": "Opcional — tu foto en el panel de entrenador",
  "coachSignup.tapToChange": "Toca para cambiar",
  "coachSignup.uploadPhoto": "Sube una foto",
  "coachSignup.accountTitle": "Crear cuenta",
  "coachSignup.accountSub": "Tus credenciales de acceso a Draffte Coach",
  "coachSignup.emailLabel": "Correo electrónico",
  "coachSignup.emailPh": "entrenador@club.com",
  "coachSignup.passwordLabel": "Contraseña",
  "coachSignup.passwordPh": "Mín. 8 caracteres",
  "coachSignup.confirmPh": "Repite la contraseña",
  "coachSignup.byCreating": "Al crear una cuenta aceptas los",
  "coachSignup.termsOfService": "Términos del Servicio",
  "coachSignup.and": "y la",
  "coachSignup.privacyPolicy": "Política de Privacidad",
  "coachSignup.hidePassword": "Ocultar contraseña",
  "coachSignup.showPassword": "Mostrar contraseña",
  "coachSignup.change": "Cambiar",
  "coachSignup.createCoachAccount": "Crear cuenta de Entrenador",
  "coachSignup.continue": "Continuar",
  "coachSignup.alreadyHave": "¿Ya tienes una cuenta?",
  "coachSignup.signUpAsPlayer": "Regístrate como Jugador",
  "coachSignup.welcome": "Bienvenido, Entrenador {name}",
  "coachSignup.successSub": "Tu panel de entrenador está listo. Empieza a construir tu plantilla.",
  "coachSignup.goToDashboard": "Ir a mi Panel",
  "coachSignup.stepWord": "Paso",
  // Visual panel
  "coachSignup.vDraffteCoach": "Draffte Coach",
  "coachSignup.vCoachFallback": "Entrenador",
  "coachSignup.vYearsOld": "{n} años",
  "coachSignup.vYearsCoaching": "Años entrenando",
  "coachSignup.vBeginner": "Principiante",
  "coachSignup.vExperienced": "Experimentado",
  "coachSignup.vVeteran": "Veterano",
  "coachSignup.vAgeGroupsSelected": "Categorías seleccionadas",
  "coachSignup.vClubPlaceholder": "Tu club actual aparecerá aquí",
  "coachSignup.vHistoryPlaceholder": "Tu historial aparecerá aquí",
  "coachSignup.vSigningUpAs": "Te registras como",
  // Role option descriptions
  "coachSignup.role.head_coach": "Entrenador principal",
  "coachSignup.role.assistant_coach": "Entrenador asistente",
  "coachSignup.role.gk_coach": "Entrenador de porteros",
  "coachSignup.role.fitness_coach": "Preparador físico",
  "coachSignup.role.technical_director": "Director técnico",
  "coachSignup.role.scout": "Ojeador",
  "coachSignup.role.academy_coach": "Entrenador de cantera",
  "coachSignup.role.team_manager": "Delegado del equipo",
  "coachSignup.roleDesc.head_coach": "Dirige la estrategia y la convocatoria del equipo",
  "coachSignup.roleDesc.assistant_coach": "Apoya al entrenador principal en todas las áreas",
  "coachSignup.roleDesc.gk_coach": "Desarrollo especializado de porteros",
  "coachSignup.roleDesc.fitness_coach": "Preparación física y prevención de lesiones",
  "coachSignup.roleDesc.technical_director": "Supervisa la filosofía futbolística del club",
  "coachSignup.roleDesc.scout": "Identificación y reclutamiento de jugadores",
  "coachSignup.roleDesc.academy_coach": "Desarrollo de jugadores juveniles",
  "coachSignup.roleDesc.team_manager": "Logística, horarios y trámites del equipo",
  // Licence group headers
  "coachSignup.licenseGroup.entry": "Sin licencia / Nivel inicial",
  "coachSignup.licenseGroup.uefa": "UEFA",
  "coachSignup.licenseGroup.ussf": "USSF (EE. UU.)",
  "coachSignup.licenseGroup.concacaf": "CONCACAF",
  "coachSignup.licenseGroup.afc": "AFC",
  "coachSignup.licenseGroup.caf": "CAF (África)",
  "coachSignup.licenseGroup.other": "Otra",
  // Licence labels
  "coachSignup.license.none": "Sin licencia",
  "coachSignup.license.grassroots": "Base / Nivel inicial",
  "coachSignup.license.uefa_c": "Licencia UEFA C",
  "coachSignup.license.uefa_b": "Licencia UEFA B",
  "coachSignup.license.uefa_a": "Licencia UEFA A",
  "coachSignup.license.uefa_pro": "Licencia UEFA Pro",
  "coachSignup.license.ussf_d": "Licencia USSF D",
  "coachSignup.license.ussf_c": "Licencia USSF C",
  "coachSignup.license.ussf_b": "Licencia USSF B",
  "coachSignup.license.ussf_a": "Licencia USSF A",
  "coachSignup.license.ussf_pro": "Licencia USSF Pro",
  "coachSignup.license.concacaf_b": "Licencia CONCACAF B",
  "coachSignup.license.concacaf_a": "Licencia CONCACAF A",
  "coachSignup.license.afc_a": "Licencia AFC A",
  "coachSignup.license.caf_a": "Licencia CAF A",
  "coachSignup.license.nscaa_national": "Diploma Nacional NSCAA",
  "coachSignup.license.other": "Otra",
  // Age group labels
  "coachSignup.age.u6_u9": "Sub-6 – Sub-9",
  "coachSignup.age.u10_u12": "Sub-10 – Sub-12",
  "coachSignup.age.u13_u16": "Sub-13 – Sub-16",
  "coachSignup.age.u17_u21": "Sub-17 – Sub-21",
  "coachSignup.age.senior_amateur": "Absoluto amateur",
  "coachSignup.age.senior_semi_pro": "Absoluto semipro",
  "coachSignup.age.senior_professional": "Absoluto profesional",
  "coachSignup.age.womens": "Femenino",
  "coachSignup.age.futsal": "Fútbol sala",
  // Squad level labels
  "coachSignup.squad.amateur": "Amateur",
  "coachSignup.squad.semi_pro": "Semiprofesional",
  "coachSignup.squad.professional": "Profesional",
  "coachSignup.squad.academy": "Cantera",
  "coachSignup.squad.national_team": "Selección nacional",
  // ── Tarjeta de entrenador + modal de perfil ──
  "coachCard.tapToEdit": "Editar",
  "coachCard.title": "Tu perfil de entrenador",
  "coachCard.sub": "Aparece en tu tarjeta y para los jugadores que se unen a tu equipo.",
  "coachCard.firstName": "Nombre",
  "coachCard.lastName": "Apellido",
  "coachCard.role": "Rol",
  "coachCard.license": "Licencia",
  "coachCard.experience": "Años de experiencia",
  "coachCard.club": "Club",
  "coachCard.nationality": "Nacionalidad",
  "coachCard.bio": "Sobre ti",
  "coachCard.bioPlaceholder": "Estilo de juego, filosofía, por qué quieres que se reconozca a tu equipo…",
  "coachCard.photo": "Cambiar foto",
  "coachCard.save": "Guardar cambios",
  "coachCard.saving": "Guardando…",
  "coachCard.cancel": "Cancelar",
  "coachCard.saveFailed": "No se pudo guardar. Revisa la conexión e inténtalo de nuevo.",
  // ── Líderes de la plantilla (Resumen) ──
  "dash.leaderPassTitle": "Mejor pase",
  "dash.leaderPassSub": "Precisión de pase",
  // ── Resumen: secciones de vídeo del equipo ──
  // ── Códigos de posición, como se usan de verdad en el fútbol hispano. ──
  "pos.GK": "POR",  "pos.CB": "DFC",  "pos.LB": "LI",   "pos.RB": "LD",
  "pos.LWB": "CAI", "pos.RWB": "CAD", "pos.CDM": "MCD", "pos.CM": "MC",
  "pos.CAM": "MP",  "pos.LM": "MI",   "pos.RM": "MD",   "pos.LW": "EI",
  "pos.RW": "ED",   "pos.ST": "DC",   "pos.CF": "SD",
  // Nombre completo, para el tooltip de cada abreviatura.
  "posName.GK": "Portero",             "posName.CB": "Defensa central",
  "posName.LB": "Lateral izquierdo",   "posName.RB": "Lateral derecho",
  "posName.LWB": "Carrilero izquierdo","posName.RWB": "Carrilero derecho",
  "posName.CDM": "Mediocentro defensivo", "posName.CM": "Mediocentro",
  "posName.CAM": "Mediapunta",         "posName.LM": "Medio izquierdo",
  "posName.RM": "Medio derecho",       "posName.LW": "Extremo izquierdo",
  "posName.RW": "Extremo derecho",     "posName.ST": "Delantero centro",
  "posName.CF": "Segundo delantero",
  "squad.colDuelOff": "Duelos of.",
  "squad.colDuelDef": "Duelos def.",
  "squad.duelTipOne": "1 disputado — ganados\u2013perdidos",
  "squad.duelTip": "{a} disputados — ganados\u2013perdidos",
  "squad.duelTipUnresolved": "{a} disputados · {n} que el vídeo no resolvió, no cuentan a ningún lado",
  "squad.duelTipUnresolvedOne": "1 disputado · el vídeo no lo resolvió, no cuenta a ningún lado",
  "squad.duelTipEstimated": "Incluye {n} estimados según quién tenía el balón (casi no se etiquetan duelos). El vídeo solo muestra un lado de esos, así que no hay porcentaje",
  "squad.duelWon": "ganados",
  "squad.duelContested": "disputados",
  // ── Clips del analista (Pregunta al analista → vídeo) ──
  "clip.kind.goal": "Goles",          "clip.kind.shot": "Remates",
  "clip.kind.save": "Atajadas",       "clip.kind.assist": "Asistencias",
  "clip.kind.key_pass": "Pases clave","clip.kind.recovery": "Recuperaciones",
  "clip.kind.turnover": "Pérdidas",   "clip.kind.foul": "Faltas",
  "clip.kind.card": "Tarjetas",       "clip.kind.chance_against": "Ocasiones en contra",
  "clip.kind.pass": "Pases",          "clip.kind.duel": "Duelos",
  "clip.third.defensive": "tercio defensivo", "clip.third.middle": "tercio medio",
  "clip.third.attacking": "tercio de ataque",
  "clip.dir.forward": "hacia adelante", "clip.dir.sideways": "lateral", "clip.dir.backward": "hacia atrás",
  "clip.out.completed": "completado", "clip.out.lost": "perdido",
  "clip.out.won": "ganado", "clip.out.unknown": "sin resolver",
  "clip.showingOf": "mostrando {n} de {total}",
  "clip.minuteRange": "minutos {a}\u2013{b}",
  "clip.ignored": "no se pudo filtrar por {f} en este tipo",
  "clip.none": "No hay nada de ese tipo en este partido",
  "clip.noVideo": "Este partido no tiene vídeo reproducible",
  "clip.play": "Reproducir",
  "clip.unnamed": "sin identificar",
  // ── Club (Mis equipos → la capa por encima del equipo) ──
  "club.noneTitle": "¿Diriges un club entero?",
  "club.noneBody": "Un club agrupa tus categorías bajo un mismo techo. Como director ves cada categoría — su entrenador, su cuerpo técnico y su plantilla — y puedes añadir más. Cada entrenador sigue mandando en su propia plantilla.",
  "club.create": "Crear un club",
  "club.createAction": "Crear",
  "club.namePlaceholder": "Nombre del club, p. ej. Envigado Fútbol Club",
  "club.join": "Unirme con un código",
  "club.joinAction": "Unirme",
  "club.codePlaceholder": "CÓDIGO",
  "club.joinHint": "Tu director comparte este código. Al unirte, TU equipo entra en su club: él lo ve, no lo edita.",
  "club.youDirect": "Diriges este club",
  "club.directedBy": "Dirigido por {n}",
  "club.categories": "Categorías",
  "club.players": "Jugadores",
  "club.staff": "Cuerpo técnico",
  "club.playersLower": "jugadores",
  "club.yours": "Tuya",
  "club.youManage": "Diriges",
  "club.readOnly": "Solo lectura",
  "club.empty": "Aún no hay categorías. Añade una o comparte el código del club con tus entrenadores.",
  "club.addCategory": "Añadir categoría",
  "club.addAction": "Añadir",
  "club.categoryPlaceholder": "Categoría, p. ej. Sub 17",
  "club.coachEmailPlaceholder": "Correo del entrenador (opcional)",
  "club.addHint": "Con un correo, ese entrenador es el DUEÑO de la nueva categoría y edita su plantilla; tú mantienes la supervisión. Sin correo, es tuya hasta que se la entregues.",
  "club.codeHint": "Comparte este código con los entrenadores de cada categoría. Lo introducen una vez y su equipo entra al club: tú lo ves, ellos lo siguen editando.",
  "club.rotate": "Código nuevo",
  "club.failed": "No se pudo. Inténtalo de nuevo.",
  // ── Team invites: hand ONE category to ONE coach (migration 036) ──────────
  "teamActions.enter": "Entrar al equipo",
  "teamActions.enterSub": "Abre el panel de esta categoría: plantilla, partidos, sesiones.",
  "teamActions.enterReadOnlySub": "Ábrela en solo lectura. Puedes ver esta categoría, no editarla.",
  "teamActions.add": "Añadir",
  "teamActions.addSub": "Invita a un entrenador o a un delegado. Tendrá permiso para editar esta categoría.",
  "teamActions.whoTitle": "¿A quién vas a añadir?",
  "teamActions.asCoach": "Entrenador",
  "teamActions.asCoachSub": "Dirige la plantilla: alineaciones, partidos y sesiones.",
  "teamActions.asManager": "Delegado del equipo",
  "teamActions.asManagerSub": "Organiza el equipo: logística, horarios y trámites.",
  "teamActions.sameAccess": "Ambos tienen el mismo acceso a {team}, con el mismo enlace de invitación.",
  "teamActions.addingCoach": "Añadir un entrenador",
  "teamActions.addingManager": "Añadir un delegado",
  "teamActions.emailLabelManager": "Correo del delegado",
  "teamActions.emailPlaceholderManager": "delegado@club.com",
  "teamActions.emailLabel": "Correo del entrenador",
  "teamActions.emailPlaceholder": "entrenador@club.com",
  "teamActions.emailHint": "Opcional. Con un correo, solo esa cuenta puede usar el enlace: si lo reenvían, no funciona.",
  "teamActions.grantNote": "Quien abra este enlace pasa a ser director de {team}: podrá editar esta plantilla. Nunca será su dueño, sirve una sola vez y puedes cancelarlo.",
  "teamActions.create": "Crear enlace de invitación",
  "teamActions.createYes": "Sí, crearlo",
  "teamActions.cancel": "Cancelar",
  "teamActions.linkTitle": "Enlace de invitación",
  "teamActions.noMailer": "Todavía no enviamos correos, así que no se envió nada. Copia este enlace y mándalo tú: por WhatsApp, por mensaje, como prefieras.",
  "teamActions.mailSent": "Enviado a {e}. Copia también el enlace, por si no llega.",
  "teamActions.liveTitle": "Otras invitaciones activas",
  "teamActions.revoke": "Cancelar esta invitación",
  "teamActions.revokeConfirm": "¿Cancelar esta invitación? El enlace deja de funcionar de inmediato.",
  "teamActions.revokeYes": "Cancelarla",
  "teamActions.anyoneWithLink": "Cualquiera con el enlace",
  "teamActions.expires": "Vence el {d}",
  "teamActions.failed": "No se pudo. Inténtalo de nuevo.",
  "teamActions.back": "Volver",
  "invitePage.loading": "Comprobando la invitación…",
  "invitePage.invalid": "Este enlace de invitación no es válido.",
  "invitePage.invalidBody": "Revisa que copiaste el enlace completo, o pide uno nuevo.",
  "invitePage.expired": "Esta invitación venció. Pide un enlace nuevo.",
  "invitePage.revoked": "Esta invitación fue cancelada. Pide un enlace nuevo.",
  "invitePage.accepted": "Esta invitación ya se usó.",
  "invitePage.eyebrow": "Te invitaron a",
  "invitePage.invitedBy": "Te invitó {n}",
  "invitePage.forEmail": "Este enlace es para {e}",
  "invitePage.grantNote": "Vas a poder dirigir esta plantilla: jugadores, partidos y sesiones. No pasas a ser su dueño, y este enlace sirve una sola vez.",
  "invitePage.join": "Unirme a este equipo",
  "invitePage.joining": "Uniéndote…",
  "invitePage.joined": "Listo",
  "invitePage.signUp": "Crear mi cuenta de entrenador",
  "invitePage.signIn": "Ya tengo cuenta",
  "invitePage.signedOutNote": "No te pediremos crear un equipo: te estás uniendo a uno que ya existe.",
  "invitePage.expiresOn": "Este enlace vence el {d}",
  "invitePage.failed": "No se pudo unir a ese equipo. Inténtalo de nuevo.",
  "coachSignup.invitedTo": "Te unes a {team}",
  "coachSignup.invitedNote": "No te pediremos crear un equipo: esta cuenta se une a uno que ya existe.",
  "coachSignup.invitedNoteClub": "En {club}. No te pediremos crear un equipo: esta cuenta se une a uno que ya existe.",
  "coachSignup.invitedSuccessSub": "Listo. {team} ya está en tu panel.",
  "coachSignup.inviteFailed": "Tu cuenta está lista, pero no se completó la unión al equipo. Abre otra vez el enlace de invitación cuando hayas iniciado sesión.",
  "vid.title": "Vídeos del equipo",
  "vid.filmed": "De {n} partidos grabados",
  "vid.loading": "Cargando clips…",
  "vid.goalsTitle": "Goles",
  "vid.goalsSub": "Todos los goles de tu equipo en partidos grabados. Cada clip empieza donde nació la jugada, no en el remate.",
  "vid.savesTitle": "Atajadas",
  "vid.savesSub": "Todos los remates rivales a puerta que el portero detuvo. Sin nombre del portero: vemos la atajada, no con certeza quién la hizo.",
  "vid.recovTitle": "Recuperaciones",
  "vid.recovSub": "Donde tu equipo recuperó el balón. Ordenadas por lo que pasó después, así las que acabaron en ataque van primero.",
  "vid.playAll": "Ver todos",
  "vid.viewFollow": "Cámara balón",
  "vid.viewPano": "Panorámica",
  "vid.noFollow": "Este partido no tiene cámara balón — se muestra la panorámica",
  "vid.noPano": "Este partido no tiene panorámica — se muestra la cámara balón",
  "vid.stop": "Parar",
  "vid.pickAClip": "Elige un clip para verlo aquí",
  "vid.nothingHere": "Aún no hay nada en esta sección",
  "vid.sectionEmpty": "Todavía no hay clips de este tipo en los partidos grabados.",
  "vid.showingOf": "Mostrando {shown} de {total}",
  "vid.ledToShot": "acabó en remate",
  "vid.teamSave": "Atajada",
  "vid.unnamed": "Sin identificar",
  "vid.emptyTitle": "Aún no hay vídeo",
  "vid.emptyBody": "Los clips salen de partidos grabados y etiquetados. {played} partidos jugados, {tagged} etiquetados — ninguno con vídeo que podamos reproducir todavía.",
  "vid.backToWall": "Todas las secciones",
  "vid.expand": "Ampliar",
  "vid.thisSeason": "esta temporada",
  "vid.paneClips": "{n} para ver",
  "vid.paneNone": "Aún ninguno en vídeo",
  "vid.couldNotLoad": "No se pudo cargar — inténtalo de nuevo",
  "vid.paneUnplayable": "ninguno reproducible aún",
  "vid.playAllOf": "Ver todos — {section}",
  "vid.moments": "Momentos",
  "dash.leaderAssistTitle": "Máximo asistente",
  "dash.leaderAssistSub": "Asistencias",
  "dash.leaderTiedOne": "empatado con 1 jugador más",
  "dash.leaderTied": "empatado con {n} jugadores más",
  "dash.leaderGoalsTitle": "Máximo goleador",
  "dash.leaderGoalsSub": "Goles",
  "dash.leaderDefTitle": "Mejor defensa",
  "dash.leaderDefSub": "Entradas ganadas + balones recuperados (intercepciones incluidas)",
  "dash.leaderEmpty": "Aún sin medir",
  "dash.leaderEmptyBody": "La clasificación aparece cuando se etiquete un partido.",
  "dash.leaderPasses": "{n} pases",
  "dash.leaderGames": "{n} partidos",
  "dash.leaderActions": "{n} acciones",
  // ── Tabla de temporada de la plantilla ──
  "squad.colCalledUp": "Convocado",
  "squad.colStarted": "Titular",
  "squad.colBench": "Suplente",
  "squad.colUnused": "Sin jugar",
  "squad.colMinutes": "Minutos",
  "squad.colGoals": "Goles",
  "squad.colAssists": "Asistencias",
  "squad.colPassAcc": "% Pase",
  "squad.colYellow": "T. Amarillas",
  "squad.colRed": "T. Rojas",
  "squad.colRating": "Valoración",
  "squad.showPct": "Ver %",
  "squad.seasonSource": "Convocatorias de {l} de {m} alineaciones guardadas · minutos y pases de {s} partidos etiquetados",
  "squad.seasonLoading": "Cargando el registro de la temporada…",
  // ── Analítica ──
  "coachAnalytics.kpiFastest": "Los 3 más rápidos",
  "coachAnalytics.kpiFastestSub": "km/h, estimado por la valoración",

  // ── Entrenador › Cámara ───────────────────────────────────────────────────
  "locale.bcp47": "es-CO",

  "nav.camera": "Cámara",

  "camList.eyebrow": "Tu equipo",
  "camList.title": "Cámara",
  "camList.subtitle": "Revisa el encuadre, administra la conexión y mira qué partidos siguen guardados en la cámara.",
  "camList.refresh": "Actualizar",
  "camList.loadFailed": "No pudimos cargar tus cámaras",
  "camList.emptyTitle": "Aún no tienes una cámara asignada",
  "camList.emptyBody": "Cuando Draffte asigne una cámara a tu club aparecerá aquí, con todo lo necesario para dejarla lista el día del partido.",
  "camList.unnamed": "Cámara",
  "camList.online": "En línea",
  "camList.offline": "Sin conexión",
  "camList.unknown": "Sin revisar",
  "camList.lastSeen": "Vista por última vez hace",
  "camList.never": "nunca",
  "camList.justNow": "un momento",

  "camDetail.back": "Todas las cámaras",
  "camDetail.loading": "Cargando…",
  "camDetail.refresh": "Actualizar",
  "camDetail.checking": "Revisando…",
  "camDetail.online": "En línea",
  "camDetail.offline": "Sin conexión",
  "camDetail.offlineHint": "Draffte no puede comunicarse con la cámara en este momento. Enciéndela y espera unos dos minutos; si no vuelve, agrega el wifi de la cancha por Bluetooth o activa los datos móviles más abajo.",
  "camDetail.loadFailed": "No pudimos abrir esta cámara",

  "camPitch.title": "Revisa el encuadre",
  "camPitch.hint": "Una foto de lo que la cámara está viendo ahora mismo, para confirmar que apunta a la cancha antes del pitazo inicial.",
  "camPitch.cta": "Tomar una foto",
  "camPitch.again": "Tomar otra",
  "camPitch.empty": "Toma una foto para ver hacia dónde apunta la cámara.",
  "camPitch.working": "Pidiéndole una foto a la cámara…",
  "camPitch.workingHint": "Tarda unos veinte segundos. La cámara primero tiene que empezar a enviar video.",
  "camPitch.alt": "Lo que la cámara está enfocando en este momento",
  "camPitch.caption": "Esta es la vista del camarógrafo con IA, no todo el ancho que se graba. Confirma que la cámara está encendida y apuntando a una cancha; no confirma que la línea de banda más lejana quede dentro del cuadro.",
  "camPitch.timeout": "La cámara no envió la foto a tiempo. Puede que apenas esté encendiendo o que su conexión sea muy lenta para enviar video. Intenta de nuevo en un momento.",

  "camNet.title": "Conexión",
  "camNet.hint": "El wifi y los datos móviles funcionan al mismo tiempo: los datos son el respaldo, no el reemplazo.",
  "camNet.refresh": "Actualizar",
  "camNet.wifi": "Wifi",
  "camNet.mobile": "Datos móviles",
  "camNet.connected": "Conectada",
  "camNet.notConnected": "Sin conectar",
  "camNet.dialling": "Conectando",
  "camNet.off": "Apagados",
  "camNet.unreadable": "Sin lectura",
  "camNet.wifiUnreadableHint": "La cámara no respondió, así que no podemos decir a qué red está conectada. Normalmente eso significa que está apagada o fuera de alcance.",
  "camNet.network": "Red",
  "camNet.address": "Dirección",
  "camNet.signal": "Señal",
  "camNet.sim": "SIM",
  "camNet.simValid": "Chip detectado",
  "camNet.simInvalid": "No se puede leer el chip",
  "camNet.apn": "APN",
  "camNet.apnNote": "El APN pertenece al operador de tu SIM y Draffte lo configura una sola vez. Si los datos móviles no conectan con un chip puesto, avísanos de qué operador es.",
  "camNet.addWifi": "Agregar una red wifi por Bluetooth",
  "camNet.modemOn": "Datos móviles activados",
  "camNet.modemOff": "Datos móviles desactivados",
  "camNet.modemOnHint": "La cámara sigue disponible aunque se caiga el wifi de la cancha.",
  "camNet.modemOffHint": "Actívalos para que la cámara siga funcionando lejos de tu wifi de siempre.",
  "camNet.toggleFailed": "La cámara no aceptó el cambio",

  "camBle.title": "Agregar una red wifi",
  "camBle.subtitle": "Por Bluetooth, parado junto a la cámara",
  "camBle.introBody": "En una cancha nueva la cámara todavía no conoce ningún wifi, así que no hay red por donde avisarle de uno. El Bluetooth es la forma de entregarle la primera red.",
  "camBle.step1": "Ponte a pocos metros de la cámara y enciéndela.",
  "camBle.step2": "Elige la cámara en la ventana de Bluetooth que abre tu navegador.",
  "camBle.step3": "Escribe el nombre y la contraseña del wifi de la cancha, y la cámara se conecta.",
  "camBle.connectCta": "Conectar por Bluetooth",
  "camBle.connectingTitle": "Conectando con la cámara",
  "camBle.stepCreds": "Obteniendo los datos de esta cámara…",
  "camBle.stepPicker": "Elige la cámara en la ventana de Bluetooth…",
  "camBle.stepConnecting": "Abriendo la conexión…",
  "camBle.stepAuth": "Identificándonos con la cámara…",
  "camBle.linked": "Conectado con la cámara",
  "camBle.ssidLabel": "Nombre de la red wifi",
  "camBle.ssidPlaceholder": "Tal como aparece en tu celular",
  "camBle.pwdLabel": "Contraseña del wifi",
  "camBle.band24Note": "La cámara solo se conecta a redes de 2.4 GHz. Si el wifi de la cancha tiene un nombre aparte para 5 GHz, usa el de 2.4 GHz.",
  "camBle.sendCta": "Enviar a la cámara",
  "camBle.sendingTitle": "Enviando la red…",
  "camBle.sendingBody": "Deja esta ventana abierta y quédate cerca de la cámara.",
  "camBle.joiningTitle": "La cámara se está conectando…",
  "camBle.joiningBody": "Puede tardar hasta un minuto.",
  "camBle.joinedTitle": "Cámara conectada",
  "camBle.joinedBody": "Se conectó a la red y la usará de ahora en adelante.",
  "camBle.done": "Listo",
  "camBle.close": "Cerrar",
  "camBle.retry": "Intentar de nuevo",
  "camBle.unconfirmedTitle": "Enviada, pero sin confirmar",
  "camBle.unconfirmedBody": "La cámara recibió la red pero no se había conectado cuando dejamos de esperar. Puede que se conecte sola. Actualiza el panel de conexión en un minuto para ver si lo hizo; si no, revisa el nombre y la contraseña y envíalos otra vez.",
  "camBle.unsupportedTitle": "Este navegador no puede usar Bluetooth",
  "camBle.unsupportedBody": "El Bluetooth desde una página web funciona en Chrome o Edge en computador, y en Chrome en Android. Safari no lo permite, así que en iPhone o iPad usa la app de Draffte: hace exactamente lo mismo.",
  "camBle.errTitle": "No funcionó",
  "camBle.errNoPassword": "Esta cámara no tiene una contraseña guardada, así que no podemos identificarnos con ella por Bluetooth. Escríbele a Draffte.",
  "camBle.errSessionLost": "Se perdió la conexión Bluetooth. Cierra esto e intenta de nuevo.",
  "camBle.errSsidRequired": "Escribe el nombre de la red wifi.",
  "camBle.errJoinRejected": "La cámara no pudo conectarse a esa red. Casi siempre es la contraseña, o que la señal es muy débil donde está montada la cámara.",
  "camBle.errSendFailed": "No pudimos enviarle la red a la cámara.",

  "camGames.refresh": "Actualizar",
  "camGames.storageTitle": "Espacio en la cámara",
  "camGames.storageHint": "Cuánto se ha usado del almacenamiento de video de la cámara. Cuando se llena, la cámara graba encima del partido más antiguo.",
  "camGames.storageChecking": "Preguntándole a la cámara…",
  "camGames.storageUnknown": "La cámara no respondió, así que no sabemos cuánto espacio queda.",
  "camGames.storageOk": "Hay espacio para el próximo partido.",
  "camGames.storageFull": "El almacenamiento está lleno: el próximo partido se grabará encima del más antiguo. Envía primero lo que todavía necesites.",
  "camGames.recordingsOnCamera": "{n} en la cámara",
  "camGames.pendingTitle": "Todavía en la cámara",
  "camGames.pendingHint": "Partidos que ya jugaste y que aún no tienen video en Draffte.",
  "camGames.sendFromApp": "El envío de un partido se hace desde la app de Draffte, en un celular conectado al wifi de la propia cámara: el archivo se descarga directamente de la cámara, y eso un navegador web no lo puede hacer. Abre Cámara en la app para enviarlo.",
  "camGames.showingOf": "Mostrando los {n} más relevantes de {total}; los partidos que ya están en la cámara aparecen primero.",
  "camGames.pendingEmpty": "No hay nada pendiente. Todos los partidos que jugaste ya están en Draffte.",
  "camGames.vs": "vs",
  "camGames.at": "en casa de",
  "camGames.unknownOpponent": "Rival sin definir",
  "camGames.onCamera": "En la cámara",
  "camGames.maybeOnCamera": "Revisar la cámara",
  "camGames.notFound": "No encontramos el video",
  "camGames.inDraffteTitle": "Grabaciones en Draffte",
  "camGames.inDraffteHint": "Partidos que grabaste tú mismo con esta cámara y que ya llegaron. Míralos o descárgalos desde donde estés.",
  "camGames.inDraffteEmpty": "Aquí no hay nada todavía. Esta lista solo incluye los partidos que grabaste desde tu propia cuenta; los que Draffte filmó para ti están en tu página de Partidos.",
  "camGames.seeMatches": "Ir a Partidos",
  "camGames.watch": "Ver",
  // ── Informe de jugador (exportación para ojeadores) ───────────────────────
  "squad.export": "Exportar",
  "squad.exportPlayer": "Exportar el informe completo de este jugador",
  "pdossier.back": "Volver",
  "pdossier.saveAsPdf": "Guardar como PDF",
  "pdossier.loading": "Generando el informe…",
  "pdossier.loadFailed": "No pudimos cargar el informe de este jugador",
  "pdossier.noData": "Todavía no hay datos",
  "pdossier.noDataBody": "{player} aún no tiene partidos analizados, así que no hay nada que informar. El informe aparece cuando se etiquete un partido que haya jugado.",
  "pdossier.sheet.profile": "Perfil del jugador",
  "pdossier.sheet.output": "Rendimiento acumulado",
  "pdossier.sheet.matches": "Partido a partido",
  "pdossier.sheet.peers": "Frente a sus compañeros",
  "pdossier.sheet.provenance": "De dónde salen estos números",
  "pdossier.h.profile": "Perfil",
  "pdossier.h.record": "Registro",
  "pdossier.h.totals": "Totales",
  "pdossier.h.duels": "Duelos",
  "pdossier.h.goalkeeping": "Portería",
  "pdossier.k.shotsFaced": "Tiros a puerta recibidos",
  "pdossier.k.saves": "Atajadas",
  "pdossier.k.conceded": "Goles recibidos",
  "pdossier.k.savePct": "% atajadas (juego abierto)",
  "pdossier.k.cleanSheets": "Porterías a cero",
  "pdossier.k.minutesInGoal": "Minutos en portería",
  "pdossier.gkBasis": "{n} partidos en portería",
  "pdossier.gkNone": "aún no hay cifras de portero registradas — ver la nota",
  "pdossier.gkNote": "Cada cifra se suma solo sobre los partidos que la registraron. El % de atajadas es en juego abierto, sin penales, y exige tres tiros recibidos. Las porterías a cero se cuentan sobre los partidos con reloj etiquetado. Un guion significa que no se mide. Los centros atrapados, los puñetazos, las salidas y los mano a mano aún no se miden.",
  "pdossier.gkPens": "Penales recibidos: {saved} atajados de {faced}.",
  "pdossier.h.discipline": "Disciplina",
  "pdossier.h.everyMatch": "Todos los partidos registrados",
  "pdossier.h.percentile": "Percentil",
  "pdossier.h.measured": "Qué se mide",
  "pdossier.h.notMeasured": "Qué no, y por qué",
  "pdossier.h.dash": "Cómo leer un guion",
  "pdossier.k.matches": "Partidos",
  "pdossier.k.minutes": "Minutos",
  "pdossier.k.avgRating": "Nota media",
  "pdossier.k.goals": "Goles",
  "pdossier.k.assists": "Asistencias",
  "pdossier.k.motm": "MVP",
  "pdossier.k.yellow": "Tarjetas amarillas",
  "pdossier.k.red": "Tarjetas rojas",
  "pdossier.k.analysed": "Partidos analizados",
  "pdossier.k.age": "Edad",
  "pdossier.k.height": "Altura",
  "pdossier.k.weight": "Peso",
  "pdossier.k.foot": "Pie",
  "pdossier.k.nationality": "Nacionalidad",
  "pdossier.k.squad": "Equipo",
  "pdossier.k.status": "Estado",
  "pdossier.k.passport": "Pasaporte",
  "pdossier.k.duels": "Entradas y regates",
  "pdossier.k.physical": "Distancia recorrida y velocidad máxima",
  "pdossier.k.overall": "Media",
  "pdossier.notRated": "Aún sin valorar",
  "pdossier.inSquad": "En plantilla",
  "pdossier.leftSquad": "Fuera de la plantilla",
  "pdossier.connected": "Conectado",
  "pdossier.notConnected": "Sin conectar",
  "pdossier.matchOne": "partido",
  "pdossier.matchMany": "partidos",
  "pdossier.per90Basis": "† Un por-90 marcado aquí se calcula sobre menos partidos que el total que lo acompaña: una tasa usa solo los partidos que registraron el dato y el reloj, que son {n} de {total}.",
  "pdossier.provMinutes": "Registrado en {n} de {total} partidos. Un por-90 se calcula solo con los partidos que tienen el dato y el reloj, así que su base puede ser aún menor — la hoja de totales marca toda tasa en la que lo sea.",
  "pdossier.notComparable": "no comparable",
  "pdossier.col.metric": "Métrica",
  "pdossier.col.total": "Total",
  "pdossier.col.from": "De",
  "pdossier.col.per90": "Por 90",
  "pdossier.col.vsSquad": "vs plantilla",
  "pitch.title": "Calibración de la cancha",
  "pitch.calibrate": "Calibrar cancha",
  "pitch.exit": "Volver a etiquetar",
  "pitch.chipOk": "Cancha calibrada ±{m} m",
  "pitch.chipForced": "Cancha calibrada ±{m} m (aproximada, guardada igual)",
  "pitch.chipNone": "Cancha sin calibrar: toca ~16 puntos (1 min)",
  "pitch.chipChecking": "Revisando la calibración de la cancha…",
  "pitch.chipUnknown": "Estado de la calibración no disponible",
  "pitch.chipTip": "La calibración de la cancha de este partido. Todo lo que ubica un evento en la cancha (toques del portero, pases, mapas de calor) la usa.",
  "pitch.unsaved": "sin guardar",
  "pitch.q.excellent": "excelente",
  "pitch.q.good": "buena",
  "pitch.q.rough": "demasiado aproximada para medir",
  "pitch.q.running": "suficiente para distancia y velocidad",
  "pitch.q.unusable": "inservible",
  "pitch.banner": "CALIBRACIÓN DE CANCHA — toca cada punto sobre las líneas pintadas",
  "pitch.panoNote": "La calibración se hace sobre la panorámica, así que la vista cambió a ella.",
  "pitch.tapHint": "Toca esto en el video:",
  "pitch.allDone": "Todos los puntos están resueltos — revisa el ajuste y guarda.",
  "pitch.progress": "{n} puestos · {s} omitidos · {t} en total",
  "pitch.skip": "Omitir (no se ve)",
  "pitch.undo": "Deshacer",
  "pitch.clear": "Borrar todo",
  "pitch.dragHint": "Arrastra un marcador para ajustarlo. Omite los puntos que no veas; necesitas al menos 7 para probar un ajuste y mientras más separados, mejor.",
  "pitch.far": "banda lejana (arriba en la imagen)",
  "pitch.near": "banda cercana (abajo)",
  "pitch.format": "Formato de cancha",
  "pitch.thisGame": "Este partido es {fmt}: el modelo de cancha arranca en ese formato.",
  "pitch.nineNote": "El 9v9 usa la cancha 9v9 de US Soccer: área 14 × 36 yd, área chica 5 × 16 yd, punto penal a 10 yd, círculo central de 8 yd. Las canchas 9v9 miden 70–80 × 45–55 yd, así que escribe el largo y el ancho reales cuando el club los conozca. Si un área está pintada con otra medida, salta sus esquinas y usa el contorno, la línea de mitad y el punto central.",
  "pitch.length": "Largo (m)",
  "pitch.width": "Ancho (m)",
  "pitch.measured": "Medida con cinta",
  "pitch.measuredHint": "Llena ambas medidas para indicar que se midieron.",
  "pitch.groupLeft": "Extremo izquierdo",
  "pitch.groupRight": "Extremo derecho",
  "pitch.groupExtras": "Esquinas, puntos y línea media",
  "pitch.rowTip": "Hacer que sea el siguiente por tocar",
  "pitch.removePoint": "Quitar este punto",
  "pitch.placed": "puesto",
  "pitch.skippedTag": "omitido",
  "pitch.checkFit": "Revisar ajuste",
  "pitch.propose": "Prellenar con Claude",
  "pitch.proposing": "Claude está mirando este cuadro… (unos 30 segundos)",
  "pitch.proposeTip": "Primero pausa en un momento en que las líneas estén libres de jugadores. Claude ubica los puntos que ve; tú los revisas.",
  "pitch.proposed": "Claude ubicó {n} puntos. Revisa las líneas que dibuja sobre el video, arrastra cualquier punto que esté mal, marca los que le faltaron y luego Guarda.",
  "pitch.proposedNone": "Claude no pudo distinguir ningún punto en este momento. Prueba un momento en que las líneas estén libres de jugadores.",
  "pitch.proposedSuggested": "Claude ubicó {n} puntos y sugiere {s} más (violeta punteado). Para cada sugerencia: haz clic si está justo sobre la esquina pintada, arrástrala hasta allí si no, o marca el lugar correcto. Las sugerencias no cuentan hasta que lo hagas. Luego Guarda.",
  "pitch.suggestedHint": "Sugerido (marcador punteado), aún no ubicado. Si está justo sobre la pintura, confírmalo; si no, arrástralo o marca el lugar correcto.",
  "pitch.confirmSuggested": "Está en su lugar — confirmar",
  "pitch.suggestedTag": "sugerido",
  "pitch.suggestedMarkerTip": "sugerido: haz clic si está justo sobre la pintura, arrástralo si no",
  "calib.checkFind": "Buscar el error",
  "calib.checking": "Buscando el error… (hasta un minuto)",
  "calib.checkTip": "Prueba intercambiar puntos fáciles de confundir y dejar fuera un punto a la vez, solo con tus propios puntos. Nada se mueve hasta que hagas clic.",
  "calib.checkNone": "Ningún error por sí solo lo explica. Acerca cada punto y revisa que esté justo sobre su esquina.",
  "calib.checkFound": "Lo encontré — corregir esto baja el error de {before} m a {after} m:",
  "calib.swapLine": "{a} y {b} parecen intercambiados",
  "calib.checkFoundOff": "Estos puntos no coinciden con los demás — sin ellos el error es {after} m en vez de {before} m. Acerca cada uno y ponlo justo sobre su esquina:",
  "calib.offLine": "{a} (unos {m} m desviado)",
  "calib.applySwap": "Intercambiarlos",
  "calib.applySwaps": "Intercambiarlos todos",
  "calib.showPoint": "Mostrarlo",
  "pitch.traceTitle": "Trazar líneas",
  "pitch.legendTodo": "traza estas",
  "pitch.legendDone": "trazada",
  "pitch.legendNow": "trazando",
  "pitch.traceWhy": "Elige una línea y haz clic 5–10 veces en cualquier parte de su pintura, repartidos. Un punto sobre una línea no se puede etiquetar mal, y la línea de banda cercana solo tiene tres puntos con nombre — trazarla hace el ajuste mucho más estable.",
  "pitch.traceTip": "Clic para trazar esta línea; clic de nuevo para terminar",
  "pitch.traceClear": "Quitar los puntos de esta línea",
  "pitch.traceDotTip": "clic para quitar este punto",
  "pitch.tracingHint": "Trazando",
  "pitch.tracingHow": "Haz clic en cualquier parte de la pintura de esta línea, 5–10 veces, repartidos — no sobre jugadores. Clic en un punto para quitarlo. Esc o Listo al terminar.",
  "pitch.traceDone": "Listo",
  "pitch.trace.near_touchline": "Línea de banda cercana",
  "pitch.trace.halfway": "Línea de mitad",
  "pitch.trace.far_touchline": "Línea de banda lejana",
  "pitch.trace.center_circle": "Círculo central",
  "pitch.trace.left_goal_line": "Línea de meta izquierda",
  "pitch.trace.right_goal_line": "Línea de meta derecha",
  "pitch.trace.left_box_front": "Área izquierda · línea frontal",
  "pitch.trace.right_box_front": "Área derecha · línea frontal",
  "calib.zoomIn": "Acercar",
  "calib.zoomOut": "Alejar",
  "calib.fit": "Completa",
  "calib.loupe": "Lupa",
  "calib.stillLoading": "Cargando el cuadro nítido…",
  "calib.stillFailed": "No se pudo cargar el cuadro nítido.",
  "calib.retry": "Reintentar",
  "calib.stillOn": "Cuadro nítido · resolución completa",
  "calib.stillTip": "Estás ubicando puntos sobre el cuadro original de la cámara, no sobre la copia liviana del video.",
  "calib.stillOff": "Copia del video (borrosa) · pausa para ver el cuadro nítido",
  "calib.help": "Rueda para acercar · arrastra con clic derecho o Espacio+arrastrar para moverte · haz clic en un punto para seleccionarlo, las flechas lo mueven 1 px (Shift: 10) · F acerca a él · Supr borra el punto seleccionado · ⌘/Ctrl+Z deshacer · 0 imagen completa",
  "pitch.checking": "Revisando…",
  "pitch.save": "Guardar",
  "pitch.saving": "Guardando…",
  "pitch.saveAnyway": "Guardar igual…",
  "pitch.saveAnywayConfirm": "Sí, guardar este ajuste flojo",
  "pitch.remove": "Quitar calibración",
  "pitch.removeConfirm": "Confirmar: quitar calibración",
  "pitch.removing": "Quitando…",
  "pitch.fitResult": "Error típico {rms} m · peor {max} m",
  "pitch.fitLines": "¿Las líneas dibujadas coinciden con las pintadas? Si no, arrastra los puntos y revisa de nuevo.",
  "pitch.fitStale": "Los puntos cambiaron desde la última revisión — las líneas punteadas están desactualizadas.",
  "pitch.saved": "Guardado. Este partido ya usa su propia calibración de cancha.",
  "pitch.removed": "Calibración quitada.",
  "pitch.needPoints": "Pon al menos 7 puntos primero.",
  "pitch.videoNotReady": "El video aún no está listo — espera la imagen e intenta de nuevo.",
  "pitch.failed": "La solicitud falló",
  "pitch.refusedHint": "No se guardó. Puedes corregir los puntos o guardarlo igual si estás seguro.",
  "pitch.refusedRunning": "Aún no se guardó. Este ajuste sirve para distancia y velocidad (2 m o mejor), todavía no para los mapas de cancha más finos (1 m). Usa Guardar igual para conservarlo.",
  "pitch.keysBlocked": "Los atajos de etiquetado están apagados mientras calibras.",
  "pitch.lm.left_box_goal_top": "Izquierda: área grande · esquina en la línea de meta (lejana)",
  "pitch.lm.left_box_goal_bottom": "Izquierda: área grande · esquina en la línea de meta (cercana)",
  "pitch.lm.left_penalty_box_top": "Izquierda: área grande · esquina frontal (lejana)",
  "pitch.lm.left_penalty_box_bottom": "Izquierda: área grande · esquina frontal (cercana)",
  "pitch.lm.left_goal_area_goal_top": "Izquierda: área chica · esquina en la línea de meta (lejana)",
  "pitch.lm.left_goal_area_goal_bottom": "Izquierda: área chica · esquina en la línea de meta (cercana)",
  "pitch.lm.left_goal_area_top": "Izquierda: área chica · esquina frontal (lejana)",
  "pitch.lm.left_goal_area_bottom": "Izquierda: área chica · esquina frontal (cercana)",
  "pitch.lm.right_box_goal_top": "Derecha: área grande · esquina en la línea de meta (lejana)",
  "pitch.lm.right_box_goal_bottom": "Derecha: área grande · esquina en la línea de meta (cercana)",
  "pitch.lm.right_penalty_box_top": "Derecha: área grande · esquina frontal (lejana)",
  "pitch.lm.right_penalty_box_bottom": "Derecha: área grande · esquina frontal (cercana)",
  "pitch.lm.right_goal_area_goal_top": "Derecha: área chica · esquina en la línea de meta (lejana)",
  "pitch.lm.right_goal_area_goal_bottom": "Derecha: área chica · esquina en la línea de meta (cercana)",
  "pitch.lm.right_goal_area_top": "Derecha: área chica · esquina frontal (lejana)",
  "pitch.lm.right_goal_area_bottom": "Derecha: área chica · esquina frontal (cercana)",
  "pitch.lm.tl_corner": "Banderín de córner · lejano izquierdo",
  "pitch.lm.bl_corner": "Banderín de córner · cercano izquierdo",
  "pitch.lm.tr_corner": "Banderín de córner · lejano derecho",
  "pitch.lm.br_corner": "Banderín de córner · cercano derecho",
  "pitch.lm.left_penalty_spot": "Punto de penal · izquierdo",
  "pitch.lm.right_penalty_spot": "Punto de penal · derecho",
  "pitch.lm.halfway_top": "Línea media · banda lejana",
  "pitch.lm.halfway_bottom": "Línea media · banda cercana",
  "pitch.lm.center_spot": "Punto central",
};

export const translations: Record<"en" | "es", Dict> = { en, es };
